// Code generated by frontend/scripts/generate-element-docs.mjs. DO NOT EDIT.
//
// Element documentation compiled from the @mountaya SDK sources, resolved through the
// "@mountaya/source" export condition — src, never dist, so a gate is never green on a
// stale build. Types are rendered from each declaration's own type node rather than
// from the checker, so no absolute path from the generating machine reaches this file.
// Regenerate with:
//
//   node frontend/scripts/generate-element-docs.mjs
//
// The documented set is authored in applications/docs/descriptions/elements/, where
// every component-shaped export is either given a page or excluded with a reason. The
// generator refuses to run while any of them is in neither list.

import type { ElementDocs, ElementPages } from "@mountayaapp/markdoc";

export const ELEMENT_DOCS: ElementDocs = {
  ActivityIcon: {
    description:
      "Its colours are the sheet's `--mtya-activity-badge-bg` and `--mtya-activity-glyph-color`, so\nthe studio's dark scheme and a host's `tokens` reach every badge; only the two sizes are\nwritten on the element. `size` defaults to `1.75rem`.\n\nThe artwork comes from the MARKER seam rather than from an asset this package ships, which is\nwhat lets it exist at all: the silhouettes are a vocabulary a consumer supplies for their own\nactivities, resolved through `activityMarkerIcon`, and it answers null for a slug the\ninstalled set has no artwork for. A badge with nothing to draw renders NOTHING rather than a\nbare tinted square: an empty chip in a row reads as a broken image, where an absent one reads\nas a row whose activity simply has no mark.\n\nIt is decorative unless `label` is given (the row's name already carries the itinerary's\nidentity), so the unlabelled badge is hidden from assistive technology. A LABELLED one is a\ngraphic that means something, so it takes `role=\"img\"` with the name rather than only a\ntooltip: a tooltip is pointer-only, and a badge whose whole content is a picture would\notherwise be announced as nothing at all to a reader who never hovers.",

    importPath: "@mountaya/studio-react",
    name: "ActivityIcon",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "glyphSize",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "label",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        defaultValue: '"1.75rem"',
        description: "",
        name: "size",
        required: false,
        type: "string",
      },
      {
        control: {
          kind: "enum",
          options: [
            "backcountry-skiing",
            "cross-country-skiing",
            "gravel-bike",
            "hiking-and-trail",
            "mountain-bike",
            "professional-trail",
            "road-cycling",
            "running",
            "ski-touring",
            "snowshoe-walking",
          ],
        },
        description: "",
        name: "slug",
        required: true,
        type: "RestActivitySlug",
      },
    ],
    propsTypeName: "ActivityIconProps",
    summary:
      "ActivityIcon is the activity badge a row, a header or a dialog subject leads with: the activity's silhouette in a soft tinted chip.",
  },
  AgentThreadsProvider: {
    description:
      "It reads its seams from the studio rather than from props: the tool context and the whole\nbus (a run reads the manifest the bus advertises and dispatches with the model as its\norigin, which no per-call dispatch seam expresses), the tab strip registration off the host\nregistry, the ids seam (the tool context's when it names one, the host's otherwise), the\ntransport the nearest client builds and the localized words. A provider mounted inside the\nstudio has the same access to them a leaf does, and props for these would be several things\na consumer could wire wrongly with nothing to say so.\n\n`configure` runs after every render with no dependency list, because the engine reads every\nmember when it uses it and a render that changed nothing costs an assignment. The lifecycle\nis one subscription held while the engine runs: mounting or enabling resumes it, unmounting or\ndisabling parks it, and StrictMode's remount parks and resumes it again with one live session\nper thread.",

    importPath: "@mountaya/studio-react",
    name: "AgentThreadsProvider",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description:
          "The tree that reads the threads: every surface that shows or drives a conversation.",
        name: "children",
        required: true,
        type: "ReactNode",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "true",
        description:
          "Whether the co-pilot runs here. A provider that is not enabled builds no engine, opens no transport and registers no tab strip: its readers see a conversation set that stays empty on the Search tab, and its commands do nothing, so the co-pilot's panel never opens. The tree beneath keeps its place across a change of it.",
        name: "enabled",
        required: false,
        type: "boolean",
      },
      {
        description:
          "invalidateAiConfig marks the organization's AI configuration stale after a fault that turns on it: a run refused as not-configured, or one refused for access.\n\nA callback rather than a query key read here, because the key belongs to whatever cache\nthe host binds and this provider has no business naming one: the conversation faults it\nfeeds already take the invalidation as a function. Absent, the fault still surfaces its\nmessage and the config is re-read on the next natural refetch, which is the correct defined\nabsence for a host with no cache of its own.",
        name: "invalidateAiConfig",
        required: false,
        type: "(organizationId: string) => void",
      },
      {
        description:
          "invalidateMemberships marks the reader's memberships stale after a run is refused or ended for want of access: its start or its stream answered 402, 403 or 404, a message that found its run gone, or a run ended because its member may not run it there any more (no write role in its organization, or an agreement that does not include Map Studio).\n\nA callback for the reason {@link invalidateAiConfig} is one: the\nmemberships are whatever the host's cache holds. The co-pilot's availability is read off\nthem, so the notice that follows the refresh says why the co-pilot is withheld. Absent, the\nrefusal still surfaces its message and the memberships are re-read on their next refetch.",
        name: "invalidateMemberships",
        required: false,
        type: "() => void",
      },
      {
        description:
          "onSignInRefused asks the host whether the reader is still signed in after the API refused the browser's own session under a run: its start, its stream or a message into it answered 401, as it is once the reader signs out or their session is revoked. Called once per refusal.\n\nA callback for the reason {@link invalidateAiConfig} is one: how a\nperson signs in is the host's. It is handed to the engine only where the client above rides\nthe browser's own session: under a publishable key a 401 refuses the key or the session token\nbeside it, which is no sign-in a host can ask about. Absent, the refusal still surfaces its\nmessage.",
        name: "onSignInRefused",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      {
        description:
          "The host's own remedy for a failed call, appended to the playbook's recovery the model reads with it; see {@link RecoveryAddendum}. Omitted, the model reads the playbook's recovery alone.",
        name: "recoveryAddendum",
        required: false,
        type: "RecoveryAddendum",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether a run names the server tools its model sees, from the tools the conversation's surfaces route to. Leave it off under a publishable key, whose runs call no server tool and are refused a list naming any; turn it on under the browser's own session to narrow the server tools a run offers its model.",
        name: "routesServerTools",
        required: false,
        type: "boolean",
      },
      {
        description:
          "How the co-pilot's open conversations survive a reload; see {@link AgentThreadsUrlSeam}.",
        name: "threadsUrl",
        required: false,
        type: "AgentThreadsUrlSeam",
      },
    ],
    propsTypeName: "AgentThreadsProviderProps",
    summary:
      "AgentThreadsProvider binds the studio's concurrent chats to React. The machine is {@link AgentThreadsEngine} in `@mountaya/studio/agent`, which holds the sessions, the confirmations, the routing, the tab strip and the URL mirror; its docblock says what each does. This binding does only what React must: hold the engine, hand it the host's seams on every render, and keep it running while the provider is mounted and enabled. A provider whose `enabled` is false binds no engine (see {@link enabled}), and the tree beneath it keeps its place as `enabled` changes.",
  },
  AllowlistField: {
    description:
      "The mode is local state seeded from the derived mode and latched: it re-derives\nonly on an external reseed (an org switch, a discarded edit, or a config reset),\nmeaning an incoming value that changed since the last render and does not match what the\nfield last emitted. A controlled parent echoing the emission straight back leaves\nthe latched mode alone, so a mid-edit Specific selection that momentarily empties\ndoes not snap the segment back to None or All. Two refs (the previous value prop\nand the field's last emission) tell an external reseed apart from the field's own\necho.",

    importPath: "@mountaya/studio-react",
    name: "AllowlistField",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "label",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "description",
        required: false,
        type: "string",
      },
      { description: "", name: "value", required: true, type: "string[]" },
      {
        description: "",
        name: "onChange",
        required: false,
        seam: "handler",
        type: "(next: string[]) => void",
      },
      { description: "", name: "data", required: true, type: "ComboboxData" },
      {
        control: { kind: "enum", options: ["cap", "display"] },
        description: "",
        name: "variant",
        required: true,
        type: "AllowlistVariant",
      },
      {
        description: "",
        name: "modeLabels",
        required: true,
        type: "{ all: string; none?: string | undefined; specific: string }",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "placeholder",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "nothingFoundMessage",
        required: false,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "readOnly",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Why the list was refused, shown under the picker, which it marks invalid and describes. A refusal answered at an entry of the list (an entry the catalogue no longer holds) belongs here, beside the entry the reader removes, rather than in a toast. Left out, the field shows none.",
        name: "error",
        required: false,
        type: "ReactNode",
      },
    ],
    propsTypeName: "AllowlistFieldProps",
    summary:
      "AllowlistField is the allowlist control every surface that caps a catalogue binds to (the embed wizard's terrain and overlay lists, and an organization's own security rules): an explicit All / None / Specific SegmentedControl gating a MultiSelect. The segment fixes the intent (allow all, allow none, or allow a specific set), and the MultiSelect is enabled only in the Specific mode, so a publisher never types the `[\"*\"]` wildcard by hand. The `none` segment shows only for a `cap` variant, where an empty list is an enforced deny-all; a `display` variant treats empty as show-all and omits it. The value is a `string[]`. When a description is present, the control row sits an `xs` gap below it so the help text never crowds the controls (flush under the label otherwise), matching UnitPickerField. The segment overrides only the theme's full-width default to a natural-width control so it sits inline to the picker's left rather than stretching across its own row; its size is the theme default (matching the adjacent picker) like every other control. The row never wraps (the natural-width segment plus the `flex:1` picker, which absorbs the remaining width and shrinks to fit), so even in the narrow wizard rail the segment and the picker stay on one line rather than the picker dropping to a second row.",
  },
  AppModal: {
    description:
      "Routing every dialog through one shell is what makes a create form, a share dialog and a\nconfirm all settle the same way: the chrome lives here and a caller supplies only the body\nand the action row.\n\n**Built on Mantine's COMPOSED parts rather than the `<Modal title={…}>` shorthand**, because\nthe shorthand renders whatever it is handed inside the `<h2>` that `aria-labelledby` points\nat. A header carrying an icon and a description would then make both part of the dialog's\nannounced name (\"Duplicate itinerary Alpine traverse 12 km 840 m\") and nest flow content\ninside a heading. Composed, the `<h2>` holds the title alone, the icon is its decorative\nsibling, and the description becomes the `aria-describedby` target instead.\n\n**Fullscreen dialogs are deliberately not served here.** A panorama filling the viewport and a\nwizard's header/main/footer column are flex shells that this stack-based body would fight.",

    importPath: "@mountaya/studio-react",
    name: "AppModal",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "children", required: true, type: "ReactNode" },
      {
        control: { kind: "string" },
        description:
          'The close button\'s accessible name: the localized "Close", on every dialog, so a reader navigating by name meets one target wherever they are. Never a different dismissal\'s word: a confirm whose X announces "Cancel" beside a footer Cancel is two indistinguishable buttons.\n\nRequired rather than resolved here, which is the same contract every other surface in this\npackage keeps: copy arrives from the host, through `useMountayaStrings` or through a prop.',
        name: "closeLabel",
        required: true,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether a click on the overlay closes the dialog. A shown-once dialog sets false.",
        name: "closeOnClickOutside",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        description: "Whether Escape closes the dialog. A shown-once dialog sets false.",
        name: "closeOnEscape",
        required: false,
        type: "boolean",
      },
      {
        description:
          "The line under the title: either a one-sentence description of the dialog, or the identity of the single named resource it acts on. Required: a dialog that states only a verb leaves the reader to infer what it will act on, and one that drops the line between phases moves everything under it.\n\nPass `null` to render without one, which a confirm does: its body IS that sentence, and\nrepeating it in the header would say the same thing twice. Required-with-an-explicit-null\nrather than optional, so leaving it out is a compile error instead of a silent omission.",
        name: "description",
        required: true,
        type: "ReactNode | null",
      },
      {
        description: "The action row pinned below the body, usually a {@link ModalFooter }.",
        name: "footer",
        required: false,
        type: "ReactNode",
      },
      {
        description: "The glyph for the leading icon accent, framed in a light ThemeIcon.",
        name: "icon",
        required: false,
        type: "ReactNode",
      },
      {
        control: { kind: "string" },
        description:
          "The accent the header glyph is tinted with: a design-system colour name or a CSS colour.\n\nA `string` rather than a tone, and rather than Mantine's own `MantineColor`. This shell is\ngeneric: it carries a product's own identity colour as readily as a caution, and a closed\nset of three would make the first unexpressible, and Mantine's colour type would make the\nsecond a design system's name.\n\n{@link ConfirmModal } is the surface that does take a closed set (see its `tone`), because\na confirmation has three meanings and no more.",
        name: "iconColor",
        required: false,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description:
          "Keeps the body mounted while closed (Mantine's `keepMounted`), for a dialog whose body holds state that must survive a reopen.",
        name: "keepMounted",
        required: false,
        type: "boolean",
      },
      { description: "", name: "onClose", required: true, seam: "handler", type: "() => void" },
      {
        description:
          "Renders the body AND the footer as one `<form>` with this submit handler, so a footer whose confirm omits `onConfirm` submits it. Without this the footer is a sibling of the body and a form-backed dialog would have to smuggle its action row into `children` to keep the submit button inside the form.",
        name: "onSubmit",
        required: false,
        seam: "handler",
        type: "FormEventHandler<HTMLFormElement>",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "opened",
        required: true,
        type: "boolean",
      },
      {
        defaultValue: '"md"',
        description:
          'The dialog\'s width: "md" is the form width, and a confirm or simple dialog passes "sm".',
        name: "size",
        required: false,
        type: "StudioSize | (string & {}) | number",
      },
      {
        control: { kind: "string" },
        description:
          "The dialog heading, and its accessible name. A STRING, not a node: it renders inside the `<h2>` the dialog's `aria-labelledby` points at, where a node would both nest flow content in a heading and read out as part of the announced name. Anything richer belongs on `description`, which is a sibling of the heading for exactly that reason.",
        name: "title",
        required: true,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Whether the title is drawn for assistive technology alone: it names the dialog all the same, and no eye meets it. For a dialog whose body opens on a heading of its own that says the same, so the name is not shown twice. The header then holds the close button alone, with no divider under it.",
        name: "titleHidden",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "true",
        description:
          "Whether the header shows its close button. A shown-once reveal phase hides it.",
        name: "withCloseButton",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "number" },
        description: "",
        name: "zIndex",
        required: false,
        type: "number",
      },
    ],
    propsTypeName: "AppModalProps",
    summary:
      "AppModal is the dialog shell every standard modal composes: a centred Mantine Modal whose header pairs an optional icon accent with the title and a dimmed description beneath it, whose body is a consistent stack, and whose footer is pinned below it.",
  },
  CompassControl: {
    description:
      "The rose **counter-rotates** by the live bearing, which is the part that is easy to get\nbackwards: a bearing of 90° turns the map clockwise, so a rose that is to keep pointing\nat true north must turn 90° anticlockwise. Rotating it the same way as the map produces a\ncompass that confidently points the wrong way, which nobody notices in review because it\nmoves plausibly.",

    importPath: "@mountaya/elements-react",
    name: "CompassControl",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Overrides the reset. Unset, the control resets the map's bearing and pitch itself.\n\nSupplying one also makes the control live with no map in context: the default is what\nneeded one, and a handler that replaces it does not. The rose then reads the identity\nbearing, because there is no camera to track.",
        name: "onResetNorth",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "CompassControlStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
    ],
    propsTypeName: "CompassControlProps",
    summary: "CompassControl resets the bearing to north, and doubles as an orientation readout.",
  },
  CompassRose: {
    description:
      "Drawn rather than glyphed, and that is why it is not one of the provider's glyphs: the north\nneedle has to be a known shape at a known angle for the bearing rotation to mean\nanything, and an icon set's compass would point wherever its designer chose.\n\nStatic. The bearing rotation is applied by the wrapping element, so this never re-renders\non a camera move, which matters, because a camera move fires continuously through a pan.\nThat is also why `style` here is safe to hand a caller: the transform that turns the rose\nis on the span outside it, so nothing a caller writes can collide with the bearing.\n\nThe two neutral fills come from CSS rather than from `fill` attributes because they are\nscheme-dependent, and a scheme-dependent colour is not expressible as an SVG presentation\nattribute.",

    importPath: "@mountaya/elements-react",
    name: "CompassRose",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        control: { kind: "number" },
        description:
          "The rose's width and height in CSS pixels: the rose is square, so one dimension sizes it.",
        name: "size",
        required: true,
        type: "number",
      },
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
    ],
    propsTypeName: "CompassRoseProps",
    summary:
      'CompassRose is the compass disc every orientation affordance draws: a disc that paints the control surface so it blends into the button it sits in, an orange north needle, and two scheme-aware grey glyphs (the "N" and the south needle) that lighten on a dark surface so the rose reads on both.',
  },
  ConfirmModal: {
    description:
      'Three confirm treatments are sanctioned, and a caller picks one by NAME: `tone` is the bus\'s\nown `ConfirmationTone`, so a tool\'s declared tone reaches this dialog unchanged and a host\nraising it directly reads from the same three. Two Mantine colour props would instead put a\ndesign system in the published surface and let a host invent a fourth treatment by passing a\ncolour nobody sanctioned.\n\nTitles are sentence case ("Delete itinerary", not "Delete Itinerary"), matching the rest of\nthe dialog chrome. The acknowledge tick resets whenever the dialog reopens, so one\nacknowledgement never carries into the next prompt; a queued caller that reuses one instance\nshould also remount it through a `key`, so the dialog\'s own mount-time state starts fresh.',

    importPath: "@mountaya/studio-react",
    name: "ConfirmModal",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description:
          'The label of an "I understand…" checkbox below the body that gates the confirm button, for the highest-stakes deletes, where a stray click would destroy a whole resource. Confirm stays disabled until it is ticked, and the tick resets each time the dialog opens.',
        name: "acknowledgeLabel",
        required: false,
        type: "string",
      },
      {
        description:
          "The consequence the reader confirms: a string renders as a paragraph, a node as it is.",
        name: "body",
        required: true,
        type: "ReactNode",
      },
      {
        control: { kind: "string" },
        description:
          'The footer\'s dismissal, which keeps things as they are ("Cancel", "Keep editing").',
        name: "cancelLabel",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description:
          'The header close button\'s accessible name: the localized "Close". It is distinct from `cancelLabel` even though both dismiss: two buttons in one dialog that announce themselves identically are indistinguishable to anyone navigating by name.',
        name: "closeLabel",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: 'The footer\'s confirm, named by its verb ("Delete itinerary").',
        name: "confirmLabel",
        required: true,
        type: "string",
      },
      {
        description: "Overrides the leading header glyph.",
        name: "icon",
        required: false,
        type: "ReactNode",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Whether the confirmed action is out: Confirm shows its spinner, and every way out (Cancel, the close button, Escape and the overlay) is held until it settles.",
        name: "loading",
        required: false,
        type: "boolean",
      },
      {
        description: "Called by every dismissal: Cancel, the close button, Escape and the overlay.",
        name: "onCancel",
        required: true,
        seam: "handler",
        type: "() => void",
      },
      {
        description: "Called by Confirm.",
        name: "onConfirm",
        required: true,
        seam: "handler",
        type: "() => void",
      },
      {
        control: { kind: "boolean" },
        description: "Whether the dialog shows. The caller owns it.",
        name: "opened",
        required: true,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description:
          'The dialog\'s heading and accessible name, in sentence case ("Delete itinerary").',
        name: "title",
        required: true,
        type: "string",
      },
      {
        control: { kind: "enum", options: ["affirmative", "cautionary", "destructive"] },
        defaultValue: '"destructive"',
        description:
          "How loud the prompt is. `destructive` is the default because it is the safe direction to be wrong in: this dialog exists for consequential verbs, so the loudest treatment is the default and a softer one is an explicit opt-in.\n\nThe BUS's own vocabulary, and that is the point of taking it rather than two colour props.\nA tool declares a tone on its confirm policy, `useConfirmGate` hands it straight through, and\nnothing between the two has to know what red is.",
        name: "tone",
        required: false,
        type: "ConfirmationTone",
      },
      {
        control: { kind: "number" },
        description:
          "The dialog's stacking order, for a confirm raised over a surface that sets its own, such as the full-screen scrim of an agent-driving overlay, so the confirm stays clickable on top.",
        name: "zIndex",
        required: false,
        type: "number",
      },
    ],
    propsTypeName: "ConfirmModalProps",
    summary:
      "ConfirmModal is the one confirm dialog the studio raises: the {@link AppModal} shell (centered + dimmed + the shared header band) whose footer is a `[Cancel] [Confirm]` pair. It standardizes the destructive look (a warning glyph in the header's icon accent, red by default, so the consequence reads at a glance) and the acknowledge gate, so a host's own mutation dialogs and the tool bus's confirm gate settle the same way. The header always carries an icon: a string body renders as plain text and a node body renders as-is, but both sit beside the leading glyph. The caller owns the open state, drives `loading` from its mutation's pending state, and settles by calling onConfirm/onCancel; the footer's Cancel button, the header close button, the escape key, and the overlay all route to onCancel, so dismissing is always the safe choice. While `loading`, every one of them is held with the footer's Cancel, so the confirmed action is never dismissed from under itself. It sizes to AppModal's \"sm\", the simple-dialog width.",
  },
  ControlButton: {
    description:
      "A plain `<button>` and a class, deliberately. The alternative, taking a component\nlibrary's icon button, would put that library in the bundle of every application that\nrenders a zoom control, and impose its theme tokens on a map that has to sit inside\nsomeone else's design system.\n\nSize, radius, colours and shadow all come from `--mtya-*` custom properties, so an\napplication restyles the whole stack by setting those variables rather than by overriding\nselectors. Mountaya's own studio does exactly that, mapping its design tokens onto them,\nwhich is how one implementation serves both.",

    importPath: "@mountaya/elements-react",
    name: "ControlButton",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description:
          "The accessible name. Required: a control stack of unlabelled squares is unusable.",
        name: "label",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description:
          "The ids of what describes the button beyond its label. `<ControlTooltip>` writes its open bubble's id here.",
        name: "aria-describedby",
        required: false,
        type: "string",
      },
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        control: { kind: "string" },
        description: "A glyph role from `UI_GLYPHS`. Omit when passing `children` instead.",
        name: "glyph",
        required: false,
        type: "string",
      },
      { description: "", name: "onClick", required: false, seam: "handler", type: "() => void" },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        description:
          "Renders the button as a toggle that is on, through `aria-pressed`.\n\nPainted as the same calm grey as hover rather than as a filled accent, as `expanded` and\n`busy` are. Omitted, the button is no toggle and states no pressed state at all.",
        name: "pressed",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the surface this button discloses is shown, through `aria-expanded`: a panel toggle states it, and names the surface with `controls`.\n\nPainted as `pressed` is while it is `true`.",
        name: "expanded",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description: "The id of the surface `expanded` speaks of, written as `aria-controls`.",
        name: "controls",
        required: false,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the action the button started is still under way, through `aria-busy`: the geolocate button states it while a fix is out.\n\nPainted as `pressed` is while it is `true`.",
        name: "busy",
        required: false,
        type: "boolean",
      },
      {
        description: "The `<button>` element, for a caller that hands focus back to it.",
        name: "ref",
        required: false,
        type: "Ref<HTMLButtonElement>",
      },
      {
        control: { kind: "string" },
        description: "Marks the button in the DOM, so the stack's dismiss-on-press can exempt it.",
        name: "kind",
        required: false,
        type: "string",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
      {
        description:
          "Replaces the glyph, for a control drawing something of its own (the compass rose).",
        name: "children",
        required: false,
        type: "ReactNode",
      },
    ],
    propsTypeName: "ControlButtonProps",
    summary: "ControlButton is the square chip every map control is built from.",
  },
  ControlGroup: {
    description:
      "The shadow belongs to the group, not to each button: per-button shadows stack at the\nseams and read as a gap between two controls that are meant to be one.",

    importPath: "@mountaya/elements-react",
    name: "ControlGroup",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        control: { kind: "enum", options: ["vertical", "horizontal"] },
        defaultValue: '"vertical"',
        description:
          "`vertical` stacks the buttons into one flush chip, which is the stack's own idiom.",
        name: "orientation",
        required: false,
        type: '"vertical" | "horizontal"',
      },
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Whether every control in the group is disabled, which flattens the raised surface.\n\nA property of the GROUP, not an instruction to its children: each control keeps its own\n`disabled`, because a group regularly holds one live control beside a gated one. Set\nthis only when all of them are off, or the chip stops looking raised while something\ninside it is still pressable.",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      { description: "", name: "children", required: true, type: "ReactNode" },
    ],
    propsTypeName: "ControlGroupProps",
    summary: "ControlGroup joins adjacent buttons into a single raised surface.",
  },
  ControlTooltip: {
    description:
      "Hand-built rather than taken from a UI library, and that is the point of the package\nboundary rather than an omission: a tooltip is thirty lines, and taking a component\nlibrary for it would put that library in every consumer's bundle and force its theme onto\ntheir map.\n\nIt opens on hover **and on focus**, and closes on Escape. Hover-only tooltips are the most\ncommon a11y regression in map chrome: the description is the only place the control's\npurpose is written, so a keyboard user who could never reach it would be navigating a row\nof unlabelled squares.",

    importPath: "@mountaya/elements-react",
    name: "ControlTooltip",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "title",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "description",
        required: true,
        type: "string",
      },
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Suppresses the bubble entirely.\n\nUsed while the control's own panel or modal is open, so a tooltip never covers the\nsurface its own button just revealed.",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "enum", options: ["left", "right", "top", "bottom"] },
        description:
          "Which side of the control the bubble opens on, for a caller that knows where its control sits.",
        name: "side",
        required: false,
        type: '"left" | "right" | "top" | "bottom"',
      },
      {
        description:
          "The control the bubble describes: one element that takes `aria-describedby`.\n\nWhile the bubble is open the control is handed its id there, after any description the\ncontrol already names, so a keyboard reader hears the second line with the control's name.",
        name: "children",
        required: true,
        type: 'ReactElement<{ "aria-describedby"?: string | undefined }>',
      },
    ],
    propsTypeName: "ControlTooltipProps",
    summary:
      "ControlTooltip is the two-line label beside every map control: the control's name, then a dimmed sentence saying what it does.",
  },
  CopilotNoAccessCard: {
    description:
      "The co-pilot panel shows it for a reader the co-pilot seam reads as not writable, and shows the\nseam's `withheldNotice` instead wherever the viewer does not hold the `copilot` entitlement. A\nrole that does not write withholds that entitlement too, so a host whose organization's\nagreement covers the co-pilot hands this card as `withheldNotice` for such a reader: what the\nhost would say about an agreement would be false for them, and this card says what is true.\n\nIt reads the studio's copy, so it renders inside `MountayaStudioHost`, as the notice does.",

    importPath: "@mountaya/studio-react",
    name: "CopilotNoAccessCard",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "CopilotNoAccessCard is the body the co-pilot shows to a reader whose role in the resource's organization does not write there: it says the co-pilot edits the map, and so is for an owner, an admin or a contributor. It carries no action, since granting a role is an owner's or an admin's task the reader cannot do themselves.",
  },
  CopilotPanel: {
    description:
      "It opens when a co-pilot tab is active over an open itinerary, a saved zone or a zone being\ncreated, outside a zone's setup step, and shuts when the reader returns to Search or leaves the\neditor. The editor's \"Ask the co-pilot\" entry makes a co-pilot tab active, and the header's\nclose control returns to Search. `useStudioCopilotInset` reports the width it covers.\n\nIts body is the first of these that applies: the host's notice for a viewer the co-pilot is\nwithheld from, then the editor's gates in order (a personal resource, no resource open, a\nviewer or a non-member of the resource's organization, an organization with no AI provider\nconfigured), and otherwise the conversation, a scrolling transcript under a pinned composer.\nThe gates are the scope and access checks the run makes, so the panel never shows an input the\nrun would refuse. Sending opens the conversation's run, or sends a follow-up once one is open,\nand the send control becomes a Stop control while the turn streams.\n\nA host that arranges its own studio opens the panel through its own control, with\n`studio.tabs.open` and `{ kind: \"copilot\" }`, and states `behavior.copilot` on\n`MountayaStudioHost`: with no co-pilot seam, the panel shows no conversation.",

    importPath: "@mountaya/studio-react/copilot",
    name: "CopilotPanel",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "CopilotPanel is the studio's co-pilot: a full-height column docked over the left edge of its nearest positioned ancestor, which slides in when the co-pilot opens and out when it shuts. It takes no props and reads the studio a `<MountayaStudioHost>` provides around it.",
  },
  DialogSubject: {
    description:
      'It answers "what am I acting on", which the title cannot: a title states the verb,\nand a static sentence under it only restates that verb. It matters most where it is\nleast expected to: a form whose fields have been rewritten until nothing else on\nthe dialog says where it came from, or a confirm a reader cannot complete and so\nmost needs to understand.\n\nIt takes already-formatted strings rather than an id, and never fetches. Every\nsurface that shows a subject already holds its record for its own reasons, and WHICH\ncopy it holds is a decision only that surface can make: the export and directions\ndialogs act on the publication and must name it, while the embed and video wizards\nconfigure the draft and must name that. A component that fetched by id would pick\none and make half the headers name a copy their body will not hand over.',

    importPath: "@mountaya/studio-react",
    name: "DialogSubject",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        defaultValue: '""',
        description: "",
        name: "ascent",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        defaultValue: '""',
        description: "",
        name: "distance",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "name",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "qualifier",
        required: false,
        type: "string",
      },
      {
        control: {
          kind: "enum",
          options: [
            "backcountry-skiing",
            "cross-country-skiing",
            "gravel-bike",
            "hiking-and-trail",
            "mountain-bike",
            "professional-trail",
            "road-cycling",
            "running",
            "ski-touring",
            "snowshoe-walking",
          ],
        },
        description: "",
        name: "slug",
        required: false,
        type: "RestActivitySlug | null",
      },
    ],
    propsTypeName: "DialogSubjectProps",
    summary:
      "DialogSubject is the identity line a dialog states under its title when it acts on one concrete named resource: the activity glyph, the name, then the distance · ↗ascent through the shared RowMetadata, the same treatment the itinerary list row and the editor's own header carry, so the resource reads here as the same object it was picked from.",
  },
  DirectionsControl: {
    description: "",

    importPath: "@mountaya/elements-react",
    name: "DirectionsControl",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        control: { kind: "boolean" },
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        description:
          "What a press does. **Required**, unlike the `on*` on every other control here.\n\nThe rest of this package's controls default to driving the map, and their handler is an\noverride. These two have nothing to drive: exporting a route and routing between points\nboth end in a surface (a dialog, a panel, a download) that belongs to the application\nand cannot be guessed. A control that rendered without one would be a button that looks\nlive and does nothing, which is worse than not offering it.",
        name: "onClick",
        required: true,
        seam: "handler",
        type: "() => void",
      },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "ActionControlStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
    ],
    propsTypeName: "ActionControlProps",
    summary:
      "DirectionsControl asks the application to open directions for what the map is showing.",
  },
  DownloadControl: {
    description:
      "Deliberately map-independent: it stays live when the map has not finished loading, because\nwhat it exports is an itinerary the application already holds, not something read off the\ncanvas. Compose it through `<MapControls>`'s `children` so it joins the same stack, spacing\nand focus order as the rest.",

    importPath: "@mountaya/elements-react",
    name: "DownloadControl",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        control: { kind: "boolean" },
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        description:
          "What a press does. **Required**, unlike the `on*` on every other control here.\n\nThe rest of this package's controls default to driving the map, and their handler is an\noverride. These two have nothing to drive: exporting a route and routing between points\nboth end in a surface (a dialog, a panel, a download) that belongs to the application\nand cannot be guessed. A control that rendered without one would be a button that looks\nlive and does nothing, which is worse than not offering it.",
        name: "onClick",
        required: true,
        seam: "handler",
        type: "() => void",
      },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "ActionControlStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
    ],
    propsTypeName: "ActionControlProps",
    summary: "DownloadControl asks the application to export what the map is showing.",
  },
  DraftProvenanceBanner: {
    description: "",

    importPath: "@mountaya/studio-react",
    name: "DraftProvenanceBanner",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "enum", options: ["import", "suggest"] },
        description:
          "The staging flow that seeded the unsaved draft: a suggested route or an imported file. It is the flow the panel is in, as the studio's `DraftEditorFlow` names it.",
        name: "draftFlow",
        required: true,
        type: "DraftEditorFlow",
      },
      {
        description:
          "Overrides the words this surface says, for this surface only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest of\nthe section alone, and carried down to the rows and pickers this surface mounts. Scoped to\nthe sections it renders: a key it never says is a type error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "DraftProvenanceBannerStrings",
      },
    ],
    propsTypeName: "DraftProvenanceBannerProps",
    summary:
      "DraftProvenanceBanner is the informational callout shown above the staging form while an import or suggestion draft is being reviewed. It tells the user the itinerary is an unsaved draft and that they must create it before they can add segments and waypoints. It only communicates, since adding is already structurally impossible during staging (the AddSegment/AddWaypoint buttons live in the persisted lists, which do not render until the itinerary exists). The flow selects the glyph and the copy: the suggestion variant reads as a generated route, the import variant as an imported file. It stays mounted through the geometry edit (only the footer swaps), so its placement above the form keeps the guidance visible the whole time. Built on the light, informational Alert so it nudges without alarming.",
  },
  DraftSegmentRow: {
    description:
      "The snap/exact toggle rides the `trailing` slot (a compact, icon-only choice between the\nrouted candidate and the track as recorded) and shows only when the segment carries BOTH and\nis not snap-locked. Each option's tooltip names the distance IT would produce, so a reader\nsees what the swap costs before making it rather than watching the row's own number move\nafterwards.",

    importPath: "@mountaya/studio-react",
    name: "DraftSegmentRow",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "boolean" },
        description: "",
        name: "isOnly",
        required: true,
        type: "boolean",
      },
      { description: "", name: "liveSegment", required: false, type: "ItinerarySegment | null" },
      { description: "", name: "previousEnd", required: true, type: "[number, number] | null" },
      { description: "", name: "segment", required: true, type: "ItineraryDraftSegment" },
      {
        description:
          "Overrides the words this surface says, for this surface only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest of\nthe section alone, and carried down to the rows and pickers this surface mounts. Scoped to\nthe sections it renders: a key it never says is a type error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "DraftSegmentRowStrings",
      },
      {
        control: { kind: "number" },
        description: "",
        name: "activityDefaultSpeed",
        required: true,
        type: "number | null",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "activitySlug",
        required: true,
        type: "string | null",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "isFirst",
        required: true,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "isLast",
        required: true,
        type: "boolean",
      },
    ],
    propsTypeName: "DraftSegmentRowProps",
    summary:
      "DraftSegmentRow is the import preview's row: the same published editor row, with the draft's own metrics and one control the stored chain has no use for.",
  },
  EditorListSection: {
    description: "",

    importPath: "@mountaya/studio-react",
    name: "EditorListSection",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "action", required: false, type: "ReactNode" },
      { description: "", name: "children", required: true, type: "ReactNode" },
      {
        control: { kind: "number" },
        description: "",
        name: "count",
        required: true,
        type: "number",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "label",
        required: true,
        type: "string",
      },
      {
        control: { kind: "number" },
        description: "",
        name: "limit",
        required: false,
        type: "number",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "limitTooltip",
        required: false,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "required",
        required: false,
        type: "boolean",
      },
    ],
    propsTypeName: "EditorListSectionProps",
    summary:
      "EditorListSection is the shared chrome around an editor's list of rows (persisted segments/waypoints and the import draft's). It renders a header that pairs the section label on the left with the row count on the right (bare and hidden at zero by default, or a persistent \"X/Y\" cap when a `limit` is passed), then a bordered container whose border and radius match the form inputs above it; each direct child renders as one padded row, with a hairline divider between rows. It is purely presentational (the count and the rows are passed in), so the same shell serves every list. An empty list still renders the labeled header and an empty box, so the section reads consistently before the first row lands.",
  },
  EditorMapView: {
    description:
      "Its client builds an edit engine for each edit, and that engine stands on `terra-draw`, its\nMapLibre adapter and `@maplibre/maplibre-gl-directions`, which this component loads. A map\nthat is shown and never edited mounts {@link ViewerMapView }, whose client refuses every edit\nand loads none of them. The root `<MapView>` is a map with no client at all, for a surface that\ndrives the map itself.",

    importPath: "@mountaya/elements-react/editor",
    name: "EditorMapView",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description:
          "Names the map container as a labelled `region` landmark for assistive technology.\n\nThis package ships no i18n runtime, so the label is the caller's, in the reader's language.\nOmitted (a secondary mini-map inside a surface already labelled), the container carries no\nlandmark role.",
        name: "ariaLabel",
        required: false,
        type: "string",
      },
      {
        description:
          "The class of the container the map attaches its canvas to. Follows every render.\n\nA container given a class is sized by that class: the fill described under `style` is left\nout, since an inline declaration outranks every rule a class carries. A string that names\nno class, empty or blank, is read as none, and the fill stays.",
        name: "className",
        required: false,
        seam: "style",
        type: "string",
      },
      {
        description:
          "The SDK client the map fetches its own specifications through.\n\nThe credential, the retry policy and the Tile API base are the application's to compose, so\nit hands over the instance it already holds, as it hands over `transformRequest`.\n\nA seed, read once as the map is built: a client whose session token is a function carries\neach rotation to every request the map makes, and a different client needs a new `key`.",
        name: "client",
        required: true,
        type: 'MountayaMapOptions["client"]',
      },
      {
        description:
          "The base layer the map opens on, for an application that already holds the layers manifest.\n\nA seed, read once as the map is built. Afterwards the base layer changes through\n`MapClient.setBaseTerrain`, never through this prop.",
        name: "initialBaseTerrain",
        required: false,
        type: "BaseTerrainConfig",
      },
      {
        description:
          "The camera the map opens on: a center as `[longitude, latitude]` and a zoom.\n\nA seed, read once as the map is built. Afterwards the camera moves through the client. A\n`#view` hash in the address wins over it while `withUrlHash` is on, since that hash is a\nview somebody shared.",
        name: "initialViewport",
        required: false,
        type: "MapViewport",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the reader pans, zooms and rotates the map directly.\n\n`false` hands the camera to the surface that owns the map (a mini-map beside a street-level\nview, for instance): it then moves only through the `MapClient` it reports. A seed, read\nonce as the map is built.",
        name: "interactive",
        required: false,
        type: "boolean",
      },
      {
        description:
          "The glyph set this map's markers draw from.\n\nA seed, read once as the map is built. An application with two glyph families in play (a\nlicensed set for its own surfaces and a plain one for an embedded map) names the one it wants\nhere.",
        name: "markerIcons",
        required: false,
        type: "IconProvider",
      },
      {
        control: { kind: "number" },
        description:
          "How many tiles MapLibre keeps in memory, as a whole number of tiles.\n\nA surface that loads tiles ahead and then replays the same views, as a video recording does,\npasses a large value so those tiles are still held when the replay reaches them. A seed, read\nonce as the map is built: MapLibre takes it at construction alone.",
        name: "maxTileCacheSize",
        required: false,
        type: "number",
      },
      {
        description:
          "Called when the API refuses one of the map's own requests with a 401, a 402 or a 403: a tile, a sprite, a glyph or a style.\n\nThe refusal carries the status and the address refused, so an application words a sign-in\napart from a missing agreement. Attached as the map is built, like `onError`, and before any\nclient is handed out: a base style the API refuses is refused before the style loads, which\nmay keep `onClientReady` from ever being called, and `MapClient.onTilesAccessRequired` can\nonly be subscribed on a client that was handed out. A refusal is never also reported to\n`onError`.\n\nRead at each report, so a new handler takes effect without rebuilding the map.",
        name: "onAccessRequired",
        required: false,
        seam: "handler",
        type: '(refusal: MountayaMapEvents["accessrequired"]) => void',
      },
      {
        description:
          "Called as the surface unmounts, just before the client it handed out through `onClientReady` is disposed, so the application releases its reference to it.\n\nPaired with `onClientReady`: a surface torn down before its style loaded handed out no\nclient, so it is torn down without this being called. React StrictMode builds and tears\ndown such a surface on every development mount.\n\nRead as the surface unmounts, so a new handler takes effect without rebuilding the map.",
        name: "onClientDestroy",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      {
        description:
          "Called once with the `MapClient` this map is driven through, when its style can take sources and layers.\n\nNever called for a surface that unmounts before then, and never for a map whose base style\nwas refused or failed: `onAccessRequired` and `onError` say why such a map stays blank. The\nclient is disposed as the surface unmounts, after `onClientDestroy`, so a reference kept past\nthat point drives nothing.\n\nRead when the map reports ready, so a handler written inline rebuilds nothing and the one of\nthe latest render is the one called.",
        name: "onClientReady",
        required: true,
        seam: "handler",
        type: "(client: MapClient) => void",
      },
      {
        description:
          "Called with each failure the map reports that no call of yours could reject: an error MapLibre raised, such as a style that does not parse or a tile the server failed on.\n\nAttached as the map is built, before it makes its first request, so a failure that lands\nbefore `onClientReady` is heard, and so is one that keeps it from ever being called. It\nreports and replaces nothing. A refusal of access goes to `onAccessRequired` instead, and a\nmap that cannot be built at all (no WebGL context) throws to the nearest error boundary,\nwhich is React's own contract for a failure while mounting.\n\nRead at each report, so a new handler takes effect without rebuilding the map.",
        name: "onError",
        required: false,
        seam: "handler",
        type: "(error: Error) => void",
      },
      {
        description:
          "Called with the `MountayaMap` the client wraps, beside `onClientReady` and from the same readiness, so the two land together.\n\nThe elements read the map (`MountayaElementsProvider` takes it and every control drives it)\nwhere an application's own surfaces read the `MapClient`, so a surface that mounts both takes\nboth from here.\n\nRead when the map reports ready, as `onClientReady` is.",
        name: "onMapReady",
        required: false,
        seam: "handler",
        type: "(map: MountayaMap) => void",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the rendered frame stays readable after the browser has shown it.\n\n`true` for a surface whose canvas is read back: a video recording, frame by frame, and a\nsession recorder that samples the page. It costs a copy of the frame on every render, so a\nsurface nothing reads leaves it off. A seed, read once as the map is built: MapLibre takes it\nat construction alone.",
        name: "preserveDrawingBuffer",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Rewrites each request the map makes for a tile, a sprite, a glyph or a style, so it carries the application's own credential.\n\nWhether the map takes one is decided at the mount: supplied then, it replaces the transform\nthe map derives from `client`, and omitted then, the derived one stands for the map's life.\nThe function itself is read at each request, so a transform rebuilt when a credential rotates\nreaches the next tile, and a render that omits the prop keeps the last one supplied.",
        name: "transformRequest",
        required: false,
        type: "TileRequestTransform",
      },
      {
        description:
          'CSS for the container element.\n\n`style` is the DOM\'s word here, not MapLibre\'s: the map style goes on the terrain seeds\nabove. Supplying it replaces the fill and is never merged with it, so a caller sizing the map\ntakes over the sizing entirely, and one who only wants a border writes\n`{ height: "100%", width: "100%", border: "1px solid" }`.',
        name: "style",
        required: false,
        seam: "style",
        type: "ElementStyle",
      },
      {
        description:
          "Builds the recording subsystem this map records and previews through.\n\nAbsent, the map carries no video encoder and the client answers that recording is\nunsupported, which is what a map showing a route should cost. A surface that exports one\nsupplies the seam, reading what it needs off the map it is handed: `VideoRunEngine` from\n`@mountaya/elements/video` takes the raw map and the shared event engine, both of which the\nmap publishes.\n\nA seed, read once as the map is built: the recorder attaches a render listener to the map it\nwas built over, so a factory that changed identity later could not replace it.",
        name: "video",
        required: false,
        type: "(map: MountayaMap) => VideoSeam",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the map draws its own credit line: MapLibre's attribution control, holding the Mountaya credit beside what each tile source declares.\n\n`false` only for a host that draws the same credits itself, which then answers for the tile\nlicences: a map that shows tiles and credits nobody breaks them. A seed, read once as the\nmap is built.",
        name: "withAttribution",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the map keeps its camera in the page's address, as `#view=<zoom>/<lat>/<lng>/<bearing>/<pitch>`, so a view is shared by its link and restored on load.\n\nIt writes `location.hash` at each camera move and replaces the history entry, so a page\nturns it on where it owns the address: one map per page, the one whose view a link shares.\nA seed, read once as the map is built.",
        name: "withUrlHash",
        required: false,
        type: "boolean",
      },
    ],
    propsTypeName: "EditorMapViewProps",
    summary:
      "EditorMapView owns one map's lifecycle and hands out the `MapClient` an editor drives it through, edits included. It holds no itinerary or store knowledge: a host bridges its own state to the client.",
  },
  EditorSaveFooter: {
    description:
      'What it adds to `<EditActions>` is everything about THIS panel rather than about\nsaving: the sticky chrome the secondary sidenav pins its primary action in, the localized\nverbs, and the one flow whose Save is not a save at all. A suggest-draft segment edit\nmerges the segment commit and the itinerary create into a single "Create itinerary"\npress, so it stands in for the footer entirely.\n\nThe frame is drawn only when a session is armed, which is why `useEditActions` is read here\nas well as inside the component: the footer carries a divider and a background, so an empty\none is a line across the panel with nothing above it. Both reads see the same store, so they\ncannot disagree about whether anything is open.',

    importPath: "@mountaya/studio-react",
    name: "EditorSaveFooter",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description:
          "The live map client, the segment family's draft source: a segment's handles live on the canvas, not in the store. The waypoint family ignores it.",
        name: "client",
        required: true,
        type: "MapClient | null",
      },
      {
        control: { kind: "string" },
        description:
          "The itinerary every save is sent for, or null over a staged draft, whose saves the studio writes into the draft.",
        name: "itineraryId",
        required: true,
        type: "string | null",
      },
      {
        description:
          "Overrides the words this surface says, for this surface only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest of\nthe section alone, and carried down to the rows and pickers this surface mounts. Scoped to\nthe sections it renders: a key it never says is a type error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "EditorSaveFooterStrings",
      },
    ],
    propsTypeName: "EditorSaveFooterProps",
    summary: "EditorSaveFooter seats the published Save at the foot of the sidenav panel.",
  },
  EmbedFieldControl: {
    description:
      "It is rendered again when a key of the configuration it reads changes, or when its field,\nits options, its callback or the safety rules do, and for no patch of another field. The\nembed form mounts one control per field and every patch hands each of them a new\nconfiguration object, so a control that followed the object would be drawn again for every\npatch.",

    importPath: "@mountaya/studio-react/embed",
    name: "EmbedFieldControl",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "config", required: true, type: "EmbedConfig" },
      { description: "", name: "field", required: true, type: "EmbedConfigField" },
      { description: "", name: "layerOptions", required: true, type: "EmbedLayerOptions" },
      {
        description: "",
        name: "onPatch",
        required: true,
        seam: "handler",
        type: "(patch: EmbedConfigPatch) => void",
      },
      {
        description: "",
        name: "safetyRules",
        required: true,
        type: "EmbedSafetyRules | undefined",
      },
    ],
    propsTypeName: "EmbedFieldControlProps",
    summary:
      "EmbedFieldControl draws one field of the embed configuration as the control its kind calls for, controlled by `config` and reporting a change through `onPatch`.",
  },
  EmbedWizard: {
    description:
      "Embeddability is `usePublishableScope`'s answer for the owning organization, the one rule\nevery output of an organization is gated on, so the action menus and the wizard agree by\nconstruction. A resource owned by no organization, by a personal scope or by one the host\ncannot resolve shows the not-embeddable state rather than a broken form, which is also why a\nPERSONAL collection is never embeddable: its scope can mint no publishable key to scope one to. A\ntarget whose read failed is none of those: the wizard says it could not be loaded\n(`embed.targetLoadFailed`) and refuses nothing.\n\nIt is offered in Mountaya's web application alone. In an Embedded Studio, whose client carries\na publishable key, it reads nothing and says that embeds are configured in that application:\nits live preview and its camera capture read over the application's own session, which an\nintegrator's page does not hold.\n\nWhile the resource loads it shows a loader and asks the host nothing. Once the resource has\nloaded and its owner can publish, {@link EmbedWizardOwner} asks the host about that\norganization and hands the resolved context to the body, which owns the local config and\nrenders the rail and the stage. The wizard mounts in a fullscreen Modal whose body is a\nfull-height column; the modal close dispatches studio.embed.close through the bus.",

    importPath: "@mountaya/studio-react/embed",
    name: "EmbedWizard",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description:
          "The hook that answers the publishable keys an embed of the resolved organization may carry.\n\nThe wizard calls it as a hook, on every render, so it keeps one identity for the wizard's\nlife: a hook declared at module scope, as {@link EmbedWizard}'s example hands one. Handing\nanother hook, or none, to an open wizard changes the hooks it calls.",
        name: "useKeys",
        required: false,
        type: "StudioEmbedKeysSeam",
      },
      {
        description:
          "The hook that answers what the host can say about the organization the resource belongs to. It keeps one identity for the wizard's life, as {@link useKeys} does.",
        name: "useOrganization",
        required: false,
        type: "StudioEmbedOrganizationSeam",
      },
      {
        description:
          "The itinerary or the collection the snippet is authored for, as `{ kind, id }`: the target `studio.embed.open` takes and the embed slot holds.",
        name: "target",
        required: true,
        type: "ShareLinkTarget",
      },
      {
        description:
          "Overrides the words this surface says, for this surface only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest of\nthe section alone, and carried down to the rows and pickers this surface mounts. Scoped to\nthe sections it renders: a key it never says is a type error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "EmbedWizardStrings",
      },
    ],
    propsTypeName: "EmbedWizardProps",
    summary:
      "EmbedWizard is the embed-in-website wizard entry: it loads the target (an itinerary or a collection), reads the organization that owns it, and gates on embeddability before rendering the configuration surface.",
  },
  EmptyState: {
    description:
      'The third state a list has, and the reason it is published beside {@link ListSkeleton } and\n{@link ErrorAlert }: a list is loading, or it failed, or it has nothing in it, and a consumer\nassembling one from published rows needs all three to read as one system. The icon arrives\npre-sized so a caller picks the glyph and its scale, and the action slot lets them offer a\nrecovery (create, retry) without this block knowing what it does. Every string is the\ncaller\'s: this package ships no i18n runtime. `size` defaults to "md".\n\nReach for it for the generic "no results / nothing yet" surface. It is NOT the shape for a screen\nthat invites or gates (a hero introducing a first action, a notice that an action is not part\nof the viewer\'s agreement, a branded interstitial), each of which owns its own composition\nrather than dressing up this neutral block.',

    importPath: "@mountaya/studio-react",
    name: "EmptyState",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "action", required: false, type: "ReactNode" },
      {
        control: { kind: "string" },
        description: "",
        name: "description",
        required: false,
        type: "string",
      },
      { description: "", name: "icon", required: true, type: "ReactNode" },
      {
        control: { kind: "enum", options: ["compact", "inline", "md"] },
        defaultValue: '"md"',
        description: "",
        name: "size",
        required: false,
        type: "EmptyStateSize",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "title",
        required: true,
        type: "string",
      },
    ],
    propsTypeName: "EmptyStateProps",
    summary:
      'EmptyState is the "nothing here" block: a backgroundless gray icon over a title, an optional dimmed description, and an optional action.',
  },
  ErrorAlert: {
    description:
      "One component rather than the treatment re-derived per surface, so a failure reads the same\nway wherever it happens. It is not a notification: a notification is something that happened\nelsewhere, and this is the answer to what the reader just did, shown where they did it.",

    importPath: "@mountaya/studio-react",
    name: "ErrorAlert",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "children", required: true, type: "ReactNode" },
      {
        control: { kind: "string" },
        description: "",
        name: "title",
        required: false,
        type: "string",
      },
    ],
    propsTypeName: "ErrorAlertProps",
    summary:
      "ErrorAlert is the in-place failure surface a form or a dialog shows when an action did not take: a red alert carrying an information glyph and the caller's message.",
  },
  FormErrorAlert: {
    description:
      "The sibling of {@link ErrorAlert } and deliberately not the same component: that one takes a\ntitle and arbitrary content for a failure a surface explains, where this one takes ONE\nresolved line and sits in a fixed place. A form that could pass a title would be a form\ndeciding how loudly its own failure reads, which is the drift the pair exists to prevent.\n\nPresentational: it holds no state and resolves no copy. The form owns the message and clears\nit on the next attempt.",

    importPath: "@mountaya/studio-react",
    name: "FormErrorAlert",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "message",
        required: true,
        type: "string | null",
      },
    ],
    propsTypeName: "FormErrorAlertProps",
    summary: "FormErrorAlert is the inline error a form shows directly above its actions.",
  },
  FullscreenHeader: {
    description: "",

    importPath: "@mountaya/studio-react",
    name: "FullscreenHeader",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "children", required: false, type: "ReactNode" },
      {
        control: { kind: "string" },
        description: "",
        name: "closeLabel",
        required: true,
        type: "string",
      },
      { description: "", name: "onClose", required: true, seam: "handler", type: "() => void" },
      { description: "", name: "subject", required: false, type: "ReactNode" },
      {
        control: { kind: "string" },
        description: "",
        name: "title",
        required: true,
        type: "string",
      },
    ],
    propsTypeName: "FullscreenHeaderProps",
    summary:
      "FullscreenHeader is the editor-style top bar shared by the full-screen studio surfaces (the export-as-video and embed-in-website wizards and the waypoint details workbench): one row carrying the surface title with the subject (the itinerary name) beside it after a dot, an optional centered slot (a step rail), and the close affordance. The close carries an aria-label only (no tooltip, since a bare close glyph reads on its own), so every surface presents an identical header. The header owns no state; the caller passes the title, subject, and slot.",
  },
  FullscreenModal: {
    description:
      "Inside a studio it covers the studio and nothing beside it, so a studio embedded in one\nregion of a page opens its full-screen surfaces in that region. Outside one it covers the\nwindow.\n\nThe name is an `aria-label` rather than an `aria-labelledby` pointing at\nFullscreenHeader's `<h2>`, precisely because the shell frames phases that render no\nheader: a wizard's loading and error states are children too, and an idref into a\nheading that is not mounted yet leaves the dialog with no accessible name at all.\nThe label and that heading read the same locale key, so they cannot say two things.",

    importPath: "@mountaya/studio-react",
    name: "FullscreenModal",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "ariaLabel",
        required: true,
        type: "string",
      },
      { description: "", name: "children", required: true, type: "ReactNode" },
      { description: "", name: "onClose", required: true, seam: "handler", type: "() => void" },
    ],
    propsTypeName: "FullscreenModalProps",
    summary:
      "FullscreenModal is the editor-style chrome shared by the full-screen studio surfaces (the export-as-video and embed-in-website wizards and the waypoint details workbench): a padding-less, square Mantine Modal whose body is a full-height flex column, so a pinned header and the scrolling body sit without sticky positioning. It holds no surface state (the content to render is passed as children), so the same shell frames a wizard's loader, its defensive state, and its loaded body, as well as the waypoint workbench, alike.",
  },
  GeolocateControl: {
    description:
      "MapLibre's own GeolocateControl is not used, here or anywhere in this package. Beyond the\nvisual mismatch, its behaviour is opinionated in ways a host application often needs to\noverride (it mounts a marker, it can enter a watch mode, and its error handling is\nsilent), and none of that is reachable from outside it.\n\nThe button stays live after a refusal rather than disabling itself: a disabled button\noffers no way back, whereas a live one can carry the tooltip that says where the setting\nis, and an application can hand `onLocate` a dialog of its own.\n\nThat tooltip sentence follows the permission and nothing else. It is shown while the map's\ngeolocation seam answers that location stands refused, read without prompting at the mount and\nagain once each fix has settled. A fix that timed out or found no signal fails with location\nstill allowed, and keeps the ordinary sentence. So does a seam whose permission read throws or\nrejects: it is read as a seam that cannot say, and the fix settles as it would have. The\nbutton is busy until the fix settles and `onError` is told then, whatever the read after\nit does: a read that never settles leaves the sentence as it stood and holds nothing else.\nThe sentence follows the latest read of the map the control stands over: an answer that\narrives after a later read's is dropped, and a fix that lands once the provider's map is\nanother one reads nothing through the map it was asked of. A control whose map is taken away\ngoes back to the ordinary sentence.",

    importPath: "@mountaya/elements-react",
    name: "GeolocateControl",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Overrides the request. Unset, the control asks the browser and eases the map itself.\n\nSupplying one also makes the control live with no map in context: what needed a map was\nthe easing, and an application that owns the request owns where the fix lands.",
        name: "onLocate",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      {
        description:
          "Called when the browser refuses or the fix fails, for an application's own recovery UI.\n\nAlways an `Error`, as every element's `onError` is: the `MountayaElementsError` the map\nrejects with, whose message states the reason. A geolocation seam of the host's own that\nrejects with a value that is no `Error` is reported as a `MountayaElementsRejectionError`\nholding that value as its `cause`. Never called for a fix the application makes itself\nthrough `onLocate`, nor after the control has unmounted.",
        name: "onError",
        required: false,
        seam: "handler",
        type: "(error: Error) => void",
      },
      {
        control: { kind: "boolean" },
        description:
          "Reports a fix as in flight, for an application that owns the request through `onLocate`.\n\nUnset, the control tracks its own request. The pair matters: an `onLocate` that\nsuppresses the default also suppresses the state the default sets, so without this the\nbutton would sit idle and unpainted through every fix the application makes.",
        name: "locating",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "GeolocateControlStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Suppresses the tooltip.\n\nFor an application whose `onLocate` opens a surface of its own (a permission\npre-prompt, say), and for the same reason `PanelControl` suppresses its own: a bubble\nover the surface a press just revealed reads as a rendering fault.",
        name: "tooltipDisabled",
        required: false,
        type: "boolean",
      },
    ],
    propsTypeName: "GeolocateControlProps",
    summary: "GeolocateControl centres the map on the device location.",
  },
  Glyph: {
    description:
      'Two things here are less obvious than they look.\n\nThe **paint mode** is read from the set on every draw rather than assumed. Nucleo\nglyphs are filled shapes with no fill of their own; Lucide glyphs are open strokes with\n`fill="none"`. Painting one the other way renders nothing at all (a blank button, with\nno error), so a component that hardcoded either would break the moment a set is swapped,\nwhich is the whole point of the seam.\n\nThe markup is **parsed into real elements**, never injected. `parseGlyph` allowlists a short\nlist of shape tags and geometry attributes and drops everything else, so an application\nthat builds a provider from a source it does not fully control cannot turn an icon into\nan execution.',
    domAttributes: "svg",
    importPath: "@mountaya/elements-react",
    name: "Glyph",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "A role from `UI_GLYPHS`: `zoom-in`, `layers`, `geolocate`.",
        name: "name",
        required: true,
        type: "string",
      },
      {
        description:
          "Restated rather than inherited, and `style` below with it.\n\nBoth arrive through the `<svg>` props this extends, so neither line changes the type. They\nare written out because these two are the placement contract every published element owes a\nhost (`apiShape.test.tsx` holds every one of them to it), and a reader should not have to\nresolve an `Omit` to find out whether a glyph can be positioned.",
        name: "className",
        required: false,
        seam: "style",
        type: "string",
      },
      {
        control: { kind: "string" },
        description:
          'An accessible name, for the rare glyph that IS the control rather than decoration.\n\nAbsent by default, and the default is the common case: a glyph beside a label repeats it,\nso announcing both makes a screen reader say everything twice. Passing one flips the\nelement from `aria-hidden` to `role="img"`: a glyph cannot be half-announced, and leaving\n`aria-hidden` on an element that carries the only name renders it unreachable rather than\nmerely unlabelled.',
        name: "label",
        required: false,
        type: "string",
      },
      {
        control: { kind: "number" },
        defaultValue: "16",
        description: "",
        name: "size",
        required: false,
        type: "number",
      },
      {
        description:
          "Widened past `CSSProperties` deliberately: `ElementStyle` admits the `--mtya-*` custom properties that ARE the theming seam, and the DOM typing does not.",
        name: "style",
        required: false,
        seam: "style",
        type: "ElementStyle",
      },
      {
        description:
          "The set to draw from, when it is not the control set.\n\nFor a surface that binds a set of its own and draws every glyph from it: a studio's wrapper\nover its interface set, a waypoint layer over a set of places. The glyph reads the set's\npaint mode, stroke width and view box with its shapes, so one `name` drawn from two sets is\ntwo different drawings.",
        name: "icons",
        required: false,
        type: "IconProvider",
      },
      {
        defaultValue: "null",
        description:
          "What to draw when the provider has no shape under this name.\n\n`null` by default, and the default is right for a control: a zoom button whose glyph is\nmissing should not grow a word where an icon belongs. It is wrong for anything drawing a\nDATUM (a place's services, say), because a host that has not registered the `amenity-*`\nfamily then gets an empty cell over data the itinerary genuinely carries, and nothing\nanywhere says so. Those call sites pass the name as text.",
        name: "fallback",
        required: false,
        type: "ReactNode",
      },
    ],
    propsTypeName: "GlyphProps",
    summary: "Glyph draws one icon from the provider's control set, or from the set `icons` names.",
  },
  ImportItineraryContent: {
    description:
      "One session seed runs here: the activity is defaulted to the first in the catalog\nonce it resolves, matching the create + suggest flows, which also un-gates the\ndropzone. The itinerary name seeds (a friendly default and the source-name\nadoption) run in the staging form, which owns the draft the name field reflects.",

    importPath: "@mountaya/studio-react/import",
    name: "ImportItineraryContent",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "ImportItineraryContent is the studio sidenav body for the import INPUT step: the provider/file picker that produces a preview. Top to bottom it shares the studio form rhythm (a SidenavContentFormBody at the canonical gap): the shared content header (a back affordance that discards the import through the bus, behind the same confirm the create back button uses); the activity chooser (kept first, since the dropzone is gated on an activity); the primer, which says what the matching will do under that activity; and the file dropzone, always shown with no source chooser and the format auto-detected from the dropped file's extension. Once a preview resolves into a draft, the unified create/edit panel takes over its staging step (the name, organization, visibility, collections, granularity, the editable rows, and the Import action all live there), so this surface stays input-only. Every mutation flows through the bus, the one mutation surface the agent shares.",
  },
  ImportPreviewLegend: {
    description:
      'It says solid and dashed rather than "route" and "recording" because which is\nwhich is per segment: a segment left on its recording paints the ROUTED line\ndashed, and one snapped to trails paints the recording dashed. Naming them by\nrole would be wrong on half a mixed draft.\n\nIt sits in the panel above the segment list rather than floating over the map. The\nrows it explains are here and the toggle that flips which stroke is which is here,\nso a panel that reads top to bottom should not send the reader to the other half of\nthe screen for its own key, and a key read AFTER the rows is a key read too late.\nIt wears the same dimmed FieldLabel every other section on this panel wears, so it\nreads as one of them rather than as loose text between two controls.\n\nA leaf: no state, two lines. The caller owns whether there is anything to tell\napart: a draft whose segments all carry a single candidate paints no dashed twin.',

    importPath: "@mountaya/studio-react/import",
    name: "ImportPreviewLegend",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "ImportPreviewLegend names the two strokes an import preview paints, because the map draws every routable segment twice (solid for the candidate that would be created, dashed for the one that would not), and an unlabelled second line reads as a mistake rather than as an offer.",
  },
  ImportRoutingGuideLink: {
    description:
      "It exists as its own component rather than as markup copied twice because the two\ncall sites must open the SAME dialog with the SAME glyph and the SAME run-lock\nbehaviour: a second copy is a second thing to keep in step.\n\nThe guide is read-only, but it is still a dialog over the workspace: raising one\nmid-run puts a modal between the user and the run they are watching, so the link\ngoes inert with every other studio control while the co-pilot drives.",

    importPath: "@mountaya/studio-react/import",
    name: "ImportRoutingGuideLink",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "ImportRoutingGuideLink is the one door to the routing guide, rendered at both points in the import flow where the question comes up: beside the dropzone, where a reader has not yet seen what matching a recording to the trail network does, and beside the segment list, where the per-segment toggles act on it.",
  },
  ImportRoutingGuideProvider: {
    description:
      "A flag local to the import fork makes that fork the only door, so\nthe explanation of the feature the import is built around is reachable only\nAFTER the decision has been framed, and only from one surface.\nIt introduces itself instead: the first time a browser opens the import surface the\ndialog opens with it, and after that the link on the dropzone step and the one\nbeside the segment list reopen it on demand.\n\nOnce per BROWSER, not once per session. A reader importing their fifth GPX is not\nasking to be taught; a dialog that reappears every time becomes a thing to dismiss\nrather than read, which costs exactly the attention it was added to win.\n\nIt stays out of the tool bus deliberately, exactly as the activity guide does.\nThis is panel state (which reference material is on screen), not a mutation of\nthe itinerary, and the tool bus is the mutation surface.",

    importPath: "@mountaya/studio-react/import",
    name: "ImportRoutingGuideProvider",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "children", required: true, type: "ReactNode" },
      {
        description:
          "The pictures the guide's two comparisons are drawn from, each as a URL the host serves. The package ships none, so a host that supplies none gets both arguments in words.",
        name: "pictures",
        required: false,
        type: "ImportRoutingGuidePictures",
      },
    ],
    propsTypeName: "ImportRoutingGuideProviderProps",
    summary:
      "ImportRoutingGuideProvider owns the routing guide's open flag and renders the one dialog for the whole studio.",
  },
  ItineraryActionsMenu: {
    description: "",

    importPath: "@mountaya/studio-react",
    name: "ItineraryActionsMenu",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description:
          "The id of the user who created the itinerary, its own `created_by.id`. With `organizationId` it decides whether the reader may change the itinerary: a personal itinerary is its author's alone. Omitted, no author is known, and under a roster the host states a personal itinerary offers no item that changes it.",
        name: "createdById",
        required: false,
        type: "string",
      },
      {
        description:
          "The item that opens the editor, which only a host can build: it is a LINK to their own route, and it has to behave like one (a modifier-click opens a tab, a right-click offers the address). A `<Menu.Item component={Link}>` from their router goes here.\n\nOmitted, the item is not rendered, which is also what a host already INSIDE the editor\nwants: there is nowhere left to navigate. It is left out for a reader who may not edit the\nitinerary, as Rename is.",
        name: "editItem",
        required: false,
        type: "ReactNode",
      },
      {
        control: { kind: "string" },
        description: "The itinerary every item acts on.",
        name: "itineraryId",
        required: true,
        type: "string",
      },
      {
        description:
          "Told the menu's open state at each change of it: `true` as the reader opens the menu and `false` as it closes, whichever way it closes. A parent holds its own surface visible while the menu is open through it (the sidenav row pins its `data-menu-open` highlight this way).\n\nA co-pilot run that starts while the menu is open closes it, and that close is reported as\n`false` like any other, so a host that mirrors the state holds nothing open through the run\nand the menu does not open by itself as the run ends.",
        name: "onToggle",
        required: false,
        seam: "handler",
        type: "(opened: boolean) => void",
      },
      {
        description:
          "The host's rename entry point. Present, the menu shows a Rename item that calls it, to a reader who may edit the itinerary; omitted (a host with no inline rename, such as the elevation panel), the item is not rendered.",
        name: "onRename",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      {
        control: { kind: "string" },
        description:
          "The id of the organization that owns the itinerary, its own `organization_id`, which gates every output of the menu. Every host passes the itinerary's own, so the gate runs against the real owner rather than a workspace fallback, a foreign itinerary opened by a direct link included. Omitted, with no owner known, every output is withheld.",
        name: "organizationId",
        required: false,
        type: "string",
      },
      {
        control: {
          kind: "enum",
          options: [
            "top",
            "right",
            "bottom",
            "left",
            "bottom-end",
            "bottom-start",
            "left-end",
            "left-start",
            "right-end",
            "right-start",
            "top-end",
            "top-start",
          ],
        },
        defaultValue: '"bottom-end"',
        description:
          'Where the dropdown opens. A host anchored to the bottom of the viewport (the elevation panel) passes a `"top-*"` position, and the menu then opens upward and stays up rather than flipping back down. The item order is the same either way: Delete stays last.',
        name: "position",
        required: false,
        type: "FloatingPosition",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Whether the itinerary has a publication, which puts the Unpublish item in the menu for a reader who holds the write grant. The host answers it because the host holds the record (the sidenav row has the listing's `status`, the detail panel the open itinerary's). Omitted, by a surface that cannot answer, the item is not rendered rather than offered against an unknown state. It sits in its own band above Delete: the two are the ways an itinerary stops being available, and the divider between them says only one of the two is permanent.",
        name: "published",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "number" },
        description:
          "How many segments the itinerary holds. With none, every output that reads the geometry is disabled with its reason.",
        name: "segmentCount",
        required: true,
        type: "number",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "true",
        description:
          "Whether the trigger shows. The host turns it on while its row is hovered or focused, and an open menu always shows its trigger.",
        name: "triggerVisible",
        required: false,
        type: "boolean",
      },
    ],
    propsTypeName: "ItineraryActionsMenuProps",
    summary:
      "ItineraryActionsMenu is the three-dots action menu for a single itinerary, shared by the sidenav row and the elevation panel. Its trigger reveals on the host's hover/focus (via `triggerVisible`) but stays mounted and focusable so Tab always reaches it, and an open menu pins it visible so it does not vanish from under the popover. Every action the menu owns routes through the bus, the one mutation surface: Export and Export-as-video open their dialogs for the whole itinerary, Manage collections opens the collections dialog, and Delete dispatches the delete. Unpublish stands in a band of its own directly above Delete, the menu's two endings, and it is the reversible one of the pair. It appears only when the host says the itinerary has a publication, since a draft has nothing to withdraw. With no segment there is no geometry to export, so both export actions are disabled and explain why on hover rather than opening an empty dialog. Export-as-video is gated on the `video-export` entitlement: a viewer without it never sees the item, the same hidden-entry gate the tool enforces, read reactively so a mid-session entitlement change reveals or hides it live. Rename is the host's: it shows only when the host passes `onRename`. Edit is the host's as well, and for a stronger reason: it is a LINK to their own route (so modifier-click and right-click behave like a normal link), so the host hands the whole item in through `editItem`, and a host already inside the editor simply omits it. Every item that changes the itinerary (Rename, Edit, Unpublish and Delete) is left out for a reader who may not make the change, so a member whose role reads only is offered the outputs and nothing that writes.",
  },
  ItineraryForm: {
    description:
      "While an edit, a split pick or another create holds the editor, Create is held with the\none-edit rule as its reason: the create would be refused with `edit-in-progress`.\n\nWhile editing, the activity is locked (it drives routing and recommendations), the\nname renames in place on commit, and the order reads activity (locked), name, all\nbefore the segment/waypoint lists the detail panel renders beneath this form.\nCollection membership lives in the dedicated Manage collections modal, not this\nform. Visibility is authored later in the embed wizard, not here, so a\nfresh itinerary stays private until then. Every mutation flows through the bus,\nthe one mutation surface the agent shares.\n\nWhen an import/suggest draft is staging (`draftFlow` set, no itinerary), the form\ndelegates to StagingForm: the same metadata fields read from the active draft\nsession and written through the per-flow setters, with Create committing the\ndraft. Each of the three modes is its own body, so the form's state starts again when the\nmode changes. The edit form starts again for another itinerary too, by the itinerary's id\nand with no `key` from the host: a name typed for one itinerary, and a rename one was\nrefused, never show under another. For the itinerary it holds, its name field follows the\nname it is handed: a rename made anywhere else shows in it.",

    importPath: "@mountaya/studio-react",
    name: "ItineraryForm",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "enum", options: ["import", "suggest"] },
        description:
          "The flow whose staged draft the form edits while `itinerary` is null: its metadata is read off the draft's session and written through the flow's setters, and Create commits the draft. Omitted or null, with no itinerary, the form is the manual create, whose submit creates an itinerary shell and arms its first segment.",
        name: "draftFlow",
        required: false,
        type: "DraftEditorFlow | null",
      },
      {
        description:
          "The open itinerary, which the form edits: the activity is locked and the name renames in place. Collection membership lives in the Manage collections dialog. Null for a create or a staged draft.",
        name: "itinerary",
        required: true,
        type: "ItineraryWithDetails | null",
      },
      {
        description:
          "Overrides the words this surface says, for this surface only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest of\nthe section alone, and carried down to the rows and pickers this surface mounts. Scoped to\nthe sections it renders: a key it never says is a type error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "ItineraryFormStrings",
      },
    ],
    propsTypeName: "ItineraryFormProps",
    summary:
      "ItineraryForm is the shared create/edit itinerary surface in the sidenav: the activity chooser, the name field, and the collections the itinerary belongs to. While creating, the activity is editable, the name is pre-filled with a generated friendly name, and the order reads activity, name, organization, collections, then the Create action; submitting dispatches studio.itinerary.create (private by default) and opens the new itinerary by routing to ?itinerary_id=, which re-renders this surface in edit mode. The chosen collections attach best-effort after the shell lands; a lost attach raises a non-blocking warning while the create still succeeds, and an HTTP failure surfaces the shared inline error above the footer.",
  },
  ItineraryHeaderIdentity: {
    description:
      "All three are the same KIND of statement (facts about the thing being edited that\nthe reader reads and does not set), so they belong on one line rather than two. Two\nlines would cost a row of the narrow panel the segment list wants, and would make the\nactivity read as a field of its own with the measurements as its caption, which is\nbackwards: the activity is the least changeable thing in the header.\n\nThe activity is a pure readout by design: it drives routing and recommendations, so\nit is fixed at creation. It carries no padlock glyph: a padlock earns its place\nbeside a control someone might otherwise try to use, and there is no control here to\nmistake it for. The reason stays on the line's tooltip for anyone who looks.\n\nIt DOES carry the activity silhouette, at the reduced badge size the zone source\nbadge uses rather than the list rows' full one. The glyph is what the reader\nrecognises before they read anything: it is the same mark the list row they arrived\nfrom wore, so the panel confirms which itinerary opened at a glance instead of asking\nthem to parse a word. It costs no measurement either: the badge is `flex: none` and\nthe name is the line's flexible part, so the badge's width comes out of an activity\nname that already ellipsises, never out of the distance or the ascent.\n\nPublication is deliberately NOT here. It is a statement about a second copy of the\nitinerary that other people see, not about the thing being edited, and beside the\nactivity it would make the header a list of unlike facts. It has its own band\ndirectly below (see {@link ItinerarySubHeader }), which is also where its badge lives,\nso the state is named once.\n\nThe measurements track the in-flight segment edit through useLiveAwareSegments, so\nthe totals move as a segment is reshaped rather than only on commit, and recompute\nthe moment a segment is added or removed. An itinerary with no routable geometry yet\nshows the activity alone: the activity holds the line open in every state, so\nnothing collapses under the reader and no placeholder is needed.",

    importPath: "@mountaya/studio-react",
    name: "ItineraryHeaderIdentity",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "ItineraryHeaderIdentity is everything the editor's header says about the itinerary BESIDES its name, on ONE dimmed line: what it is routed for, how far it goes, and how much it climbs.",
  },
  ItineraryHeaderMeta: {
    description: "",

    importPath: "@mountaya/studio-react",
    name: "ItineraryHeaderMeta",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "ItineraryHeaderMeta renders an itinerary's live total distance + ascent under a header title, in the same RowMetadata treatment the itinerary list row uses (the distance, an interpunct, then a TrendingUp-captioned ascent), so the header reads with the list at a glance. It is the meta line for a header that shows no activity (a share surface, say); the editor folds the same measurements into its one identity line beside the activity (see {@link ItineraryHeaderIdentity }), where the activity holds the line open and this block's placeholder is not needed. It reads the segments from the itinerary view (the store-backed source the segment list and elevation panel render from), then overlays the in-flight segment edit through useLiveAwareSegments, so the totals track a segment being reshaped live, not only on commit, and still recompute the moment a segment is added or removed. profileCharacteristics aggregates the per-segment distance + ascent across the chain; the unit formatters return an empty string for an absent metric, which RowMetadata drops. Until a segment carries a distance (the itinerary has none, or its first segment is still being drawn and has not resolved into any geometry) there is nothing to summarize, so rather than collapse the line (and pull the header's bottom divider above the co-pilot header's, which it must stay flush with) it holds the slot with a dimmed \"No segment\" placeholder, so the two headers align whether or not the itinerary has any routable segment yet.",
  },
  ItineraryHeaderTitle: {
    description:
      "The heading itself is editable, so the one place a reader already looks for the name is\nalso where it changes, and the panel holds no second, labelled name field below it.\n\nIt opens on double-click (the same slow-double-click-a-filename gesture the\nitinerary list rows and the segment rows use) and from the actions menu's Rename\nitem, which is the discoverable path and the keyboard one. Enter commits, Escape\ncancels, and a blur that is not the commit's own focus loss cancels too. That wiring\nis the published `useInlineRename`'s, including the check that holds the field open on\na name the shared schema rejects rather than closing on something the backend would.",

    importPath: "@mountaya/studio-react",
    name: "ItineraryHeaderTitle",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "name",
        required: true,
        type: "string",
      },
      { description: "", name: "rename", required: true, type: "InlineRename" },
      {
        description:
          "Overrides the words this surface says, for this surface only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest of\nthe section alone, and carried down to the rows and pickers this surface mounts. Scoped to\nthe sections it renders: a key it never says is a type error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "ItineraryHeaderTitleStrings",
      },
    ],
    propsTypeName: "ItineraryHeaderTitleProps",
    summary:
      "ItineraryHeaderTitle is the editor header's name: a heading that turns into its own rename field in place.",
  },
  ItineraryProfile: {
    description:
      "Keyed by the itinerary asked for, so a new `itineraryId` or `surface` is a new profile and\nstarts as one: its uncontrolled window is `defaultRange` again, or the whole itinerary, where\na window kept in meters of the previous itinerary would draw the next one through a stretch\nit may not even reach. A chain a caller states through `segments` is one profile for as long\nas it is stated, so it keeps its window while it is edited in place and when an id arrives on\nit. See {@link surfaceKey}.",

    importPath: "@mountaya/elements-react/elements",
    name: "ItineraryProfile",
    package: "@mountaya/elements-react",
    parts: [
      {
        name: "Amenities",
        props: [
          {
            description:
              "The same placed marks the pin layer draws; the ones with no services are skipped.",
            name: "waypoints",
            required: true,
            type: "readonly PlacedWaypointMark[]",
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ItineraryProfileAmenitiesStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
          {
            control: { kind: "number" },
            defaultValue: "DEFAULT_PROFILE_AMENITY_MAX",
            description:
              "How many badges one stack shows before it counts the rest.\n\nA fraction is rounded down. A value below zero, or one that is no number, shows no badge\nand counts every service.",
            name: "max",
            required: false,
            type: "number",
          },
        ],
        propsTypeName: "ItineraryProfileAmenitiesProps",
        summary: "ItineraryProfileAmenities stacks a place's services above its pin.",
      },
      {
        name: "Cutoffs",
        props: [
          {
            description: "The placed cut-offs, already filtered to the visible window.",
            name: "cutoffs",
            required: true,
            type: "readonly PlacedCutoff[]",
          },
          {
            description:
              "Writes a cut-off's instant, an ISO 8601 string, as the label above its line.\n\nFormatting an instant needs a locale and a time zone, and both are the application's: an\nelement that picked `toLocaleTimeString()` would print a Paris race in the reader's own zone\nand be quietly wrong for every reader not standing on the course.",
            name: "formatCutoffAt",
            required: true,
            type: "(iso: string) => string",
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ItineraryProfileCutoffsStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItineraryProfileCutoffsProps",
        summary:
          "ItineraryProfileCutoffs draws a dashed vertical at each published cut-off, with its time above the chart.",
      },
      {
        name: "Bands",
        props: [
          { description: "", name: "bands", required: true, type: "readonly ProfileBand[]" },
          {
            control: { kind: "number" },
            description: "",
            name: "totalDistance",
            required: true,
            type: "number",
          },
          {
            description:
              "The stretch of itinerary the strip is drawn over, in meters from its start: the window of the chart it sits under, so a band stays under the stretch of curve it describes at any zoom. A band the window does not reach is not drawn, and one that crosses an end of it is cut there.",
            name: "range",
            required: false,
            type: "ProfileRange",
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ItineraryProfileBandsStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItineraryProfileBandsProps",
        summary: "ItineraryProfileBands paints a caller's spans beneath the curve.",
      },
      {
        name: "Chart",
        props: [
          { description: "", name: "points", required: true, type: "readonly ElevationPoint[]" },
          {
            control: { kind: "number" },
            description: "",
            name: "totalDistance",
            required: true,
            type: "number",
          },
          { description: "", name: "domain", required: true, type: "ElevationDomain" },
          {
            control: { kind: "number" },
            description: "",
            name: "activeIndex",
            required: true,
            type: "number | null",
          },
          {
            control: { kind: "boolean" },
            defaultValue: "false",
            description:
              "Renders the chart non-interactive: no scrub, no key, still drawn and still read.",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          {
            control: { kind: "string" },
            description: "The chart's accessible name.",
            name: "label",
            required: true,
            type: "string",
          },
          {
            control: { kind: "string" },
            description:
              'What the cursor currently reads, spoken rather than shown. `""` while no cursor stands.',
            name: "valueText",
            required: true,
            type: "string",
          },
          {
            description:
              "Stretches drawn in a colour of their own. Empty leaves the curve on its CSS fill.",
            name: "curveColors",
            required: true,
            type: "readonly CurveColor[]",
          },
          {
            description:
              "The stretch of itinerary drawn. The whole itinerary when nothing has zoomed.",
            name: "range",
            required: true,
            type: "ProfileRange",
          },
          {
            description: "",
            name: "onScrub",
            required: true,
            seam: "handler",
            type: "(index: number | null) => void",
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItineraryProfileChartProps",
        summary:
          'ItineraryProfileChart draws the curve, and answers "where along the itinerary is the pointer".',
      },
      {
        name: "DropLines",
        props: [
          { description: "", name: "dividers", required: true, type: "readonly PlacedDivider[]" },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItineraryProfileDropLinesProps",
        summary:
          "ItineraryProfileDropLines draws a dashed line from each dividing waypoint down to the curve.",
      },
      {
        name: "Navigator",
        props: [
          { description: "", name: "series", required: true, type: "ElevationSeries" },
          { description: "", name: "domain", required: true, type: "ElevationDomain" },
          { description: "", name: "range", required: true, type: "ProfileRange" },
          { description: "", name: "curveColors", required: true, type: "readonly CurveColor[]" },
          {
            control: { kind: "boolean" },
            defaultValue: "false",
            description: "Renders the strip non-interactive: no grid, both handles disabled.",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          { description: "", name: "units", required: true, type: "UnitFormatOptions" },
          {
            description: "",
            name: "onRangeChange",
            required: true,
            seam: "handler",
            type: "(next: ProfileRange) => void",
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ItineraryProfileNavigatorStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItineraryProfileNavigatorProps",
        summary:
          "ItineraryProfileNavigator is the whole itinerary in miniature, with the drawn window marked on it.",
      },
      {
        name: "Sections",
        props: [
          { description: "", name: "rows", required: true, type: "readonly ItinerarySegmentRow[]" },
          {
            control: { kind: "enumArray", options: ["distance", "ascent", "descent", "grade"] },
            description: "",
            name: "stats",
            required: true,
            type: "readonly ProfileSectionStatKey[]",
          },
          { description: "", name: "range", required: true, type: "ProfileRange" },
          {
            control: { kind: "number" },
            description: "",
            name: "totalDistance",
            required: true,
            type: "number",
          },
          { description: "", name: "units", required: true, type: "UnitFormatOptions" },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ItineraryProfileSectionsStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItineraryProfileSectionsProps",
        summary:
          "ItineraryProfileSections states each leg between the itinerary's dividing waypoints.",
      },
      {
        name: "Stats",
        props: [
          {
            control: {
              kind: "enumArray",
              options: [
                "distance",
                "duration",
                "ascent",
                "descent",
                "elevation",
                "grade",
                "difficulty",
              ],
            },
            description: "",
            name: "stats",
            required: true,
            type: "readonly ProfileStatKey[]",
          },
          {
            description: "",
            name: "characteristics",
            required: true,
            type: "ResolvedCharacteristics",
          },
          { description: "", name: "units", required: true, type: "UnitFormatOptions" },
          {
            description:
              "What the cursor is over, if anything.\n\n`distance` and `elevation` state the hovered point while a scrub is in progress and the\nitinerary's figures otherwise, so the strip reads as one row that follows the cursor rather\nthan as a second row that appears beside it and shifts the layout.\n\nHeld `null` when the hover card is on: the card owns *what is under my finger*, the strip\nowns *what the itinerary is*, and one number in two places is one place too many.",
            name: "cursor",
            required: true,
            type: "{ readonly distance: number; readonly elevation: number } | null",
          },
          {
            control: { kind: "number" },
            description:
              "The grade under the cursor, in degrees. The itinerary's maximum when nothing is hovered.",
            name: "cursorGrade",
            required: true,
            type: "number | null",
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ItineraryProfileStatsStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItineraryProfileStatsProps",
        summary:
          "ItineraryProfileStats states the itinerary's figures, and follows the cursor while one is scrubbing.",
      },
      {
        name: "Tooltip",
        props: [
          { description: "", name: "point", required: true, type: "ElevationPoint" },
          { description: "", name: "series", required: true, type: "ElevationSeries" },
          { description: "", name: "cumulative", required: true, type: "CumulativeElevation" },
          { description: "", name: "domain", required: true, type: "ElevationDomain" },
          { description: "", name: "range", required: true, type: "ProfileRange" },
          {
            control: { kind: "number" },
            description: "",
            name: "grade",
            required: true,
            type: "number | null",
          },
          { description: "", name: "classification", required: true, type: "ProfileClass | null" },
          {
            control: { kind: "enumArray", options: ["distance", "elevation", "ascent", "descent"] },
            description: "",
            name: "stats",
            required: true,
            type: "readonly ProfileTooltipStatKey[]",
          },
          {
            control: { kind: "enum", options: ["top", "cursor"] },
            description: "",
            name: "placement",
            required: true,
            type: '"top" | "cursor"',
          },
          { description: "", name: "units", required: true, type: "UnitFormatOptions" },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ItineraryProfileTooltipStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItineraryProfileTooltipProps",
        summary: "ItineraryProfileTooltip states what the cursor is standing on.",
      },
      {
        name: "Waypoints",
        props: [
          {
            description: "",
            name: "waypoints",
            required: true,
            type: "readonly PlacedWaypointMark[]",
          },
          {
            control: { kind: "boolean" },
            defaultValue: "false",
            description: "Renders the pins inert: drawn, and pressed by nobody.",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          {
            description: "",
            name: "onWaypointClick",
            required: false,
            seam: "handler",
            type: "(id: string) => void",
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ItineraryProfileWaypointsStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItineraryProfileWaypointsProps",
        summary: "ItineraryProfileWaypoints draws the places sitting on the curve.",
      },
      {
        name: "XAxis",
        props: [
          {
            description: "",
            name: "options",
            required: true,
            type: "ItineraryProfileXAxisOptions",
          },
          { description: "", name: "range", required: true, type: "ProfileRange" },
          {
            control: { kind: "number" },
            description: "",
            name: "totalDistance",
            required: true,
            type: "number",
          },
          { description: "", name: "dividers", required: true, type: "readonly PlacedDivider[]" },
          { description: "", name: "units", required: true, type: "UnitFormatOptions" },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ItineraryProfileXAxisStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItineraryProfileXAxisProps",
        summary:
          "ItineraryProfileXAxis states where along the itinerary the chart is, and names the places on it.",
      },
    ],
    props: [
      {
        control: { kind: "string" },
        description:
          "An itinerary to draw, resolved through the client on the provider.\n\nOne prop instead of a fetch, a state variable and three mappings: the response carries the\ngeometry, the server's own figures and the itinerary's waypoints in a single round trip, so all\nthree arrive together. Two elements given the same id share one request.\n\n**Nothing here fetches.** The element hands the provider's client to `fetchItinerary`, which\ncalls a method on it: one credential, one retry policy, one error taxonomy.\n\nAn id with no client on the provider renders the empty state and reports through `onError`,\nrather than throwing during render.",
        name: "itineraryId",
        required: false,
        type: "string",
      },
      {
        control: { kind: "enum", options: ["published"] },
        description: "Reads the itinerary's published snapshot rather than its draft.",
        name: "surface",
        required: false,
        type: '"published"',
      },
      {
        description: "Units, slope notation and the locale to format in.",
        name: "units",
        required: false,
        type: "UnitFormatOptions",
      },
      {
        control: { kind: "number" },
        description:
          "The plot's height in CSS pixels.\n\nA number rather than a CSS length, because the value also has to be a length the marks layer\ncan share: accepting `40vh` would mean measuring the element to place a pin, which is the one\nthing this element never does.",
        name: "height",
        required: false,
        type: "number",
      },
      {
        control: {
          kind: "enumArray",
          options: [
            "distance",
            "duration",
            "ascent",
            "descent",
            "elevation",
            "grade",
            "difficulty",
          ],
        },
        description: "Which figures the strip carries, in the order given. `[]` drops the strip.",
        name: "stats",
        required: false,
        type: "readonly ProfileStatKey[]",
      },
      {
        description:
          'Draws the curve itself in more than one colour.\n\nThree shapes, one pipeline. A list of coloured stretches paints them as given: an itinerary\nassembled from parts reads as those parts when the line carries their colours. `{ mode: "slope"\n}` computes the stretches from the itinerary\'s own steepness. `{ mode: "classify" }` takes\nstretches a caller has named, and the name is what the hover card reads.\n\nDistinct from `ItineraryProfile.Bands`, which paints a strip *beneath* the chart. This is the\ncurve, and the area fill under it, which carries the same gradient.',
        name: "curveColors",
        required: false,
        type: "ProfileColoring",
      },
      {
        description:
          "The itinerary's authored legs, which the section chips state.\n\n**Places mark the itinerary; segments divide it.** The itinerary's waypoints put pins on the\ncurve and names under the axis; its segments decide where one leg ends and the next begins, and\nwhat each chip says about it. They are different lists doing different jobs, and one resolved\nitinerary supplies both.\n\nThe chips read the SAME array `<ItinerarySegmentTable>` renders its rows from, which is why the\nderivation lives in `@mountaya/elements/profile` rather than in either element.\n\nResolved from the itinerary, and stated here only by a caller already holding the chain (an\neditor whose geometry changes as it is drawn, or a chain assembled from directions). **A stated\nchain is also the curve**: the line is walked from these legs, in this order, so the chips can\nnever describe a line they did not come from. Beside `itineraryId`, it is the one way data\nreaches the element, and `<ItinerarySegmentTable>` takes it too.\n\nA new array of the same legs, in the same order, is the same chain: the element walks it again\nonly when a leg object changes.",
        name: "segments",
        required: false,
        type: "readonly ItinerarySegmentInput[]",
      },
      {
        description:
          "Marks of your own, drawn on the curve.\n\nThe itinerary's own waypoints cover the common case (a pin at a place) and stay thin on\npurpose. An application whose marks carry more than that (a drop line to the baseline, a hover\nstate, a hit target wider than the glyph, a tooltip) renders them here instead, and positions\neach one with `useProfilePlot`, which answers with the same scale the curve is drawn against.\n\nChildren are placed in the same layer as the built-in pins and the cursor dot. That layer is\npointer-transparent; opt individual children back in.",
        name: "children",
        required: false,
        type: "ReactNode",
      },
      {
        description:
          "Takes over what a hover does.\n\nLeft unset, and with a map in context, the profile drives the map itself: it pins a marker at\nthe hovered coordinate and moves it as the cursor sweeps. Set, that is suppressed entirely and\nthe element becomes presentational: the handler receives the hovered point, or `null` on leave,\nand decides. The assembled studio routes the cursor through its tool bus this way while\nrendering this exact component.\n\nA cursor that goes with no pointer leaving reports `null` as well, once: the profile is\ndisabled under it, draws another itinerary (a new `itineraryId`, a new `surface`, or another\nchain stated under the same one), or is unmounted.",
        name: "onHover",
        required: false,
        seam: "handler",
        type: "(point: ElevationPoint | null) => void",
      },
      {
        description:
          "Called with a waypoint's id when its pin is pressed. Unset leaves the pins inert.",
        name: "onWaypointClick",
        required: false,
        seam: "handler",
        type: "(id: string) => void",
      },
      {
        control: { kind: "boolean" },
        description:
          "The services each place offers, stacked above its pin.\n\nOn by default, unlike every other opt-in part of this element, and the data is the switch: the\nlayer renders nothing at all for an itinerary whose waypoints declare no amenities. Pass\n`false` to suppress it on an itinerary that does, or an object to cap the stack.",
        name: "amenities",
        required: false,
        type: "boolean | ItineraryProfileAmenitiesOptions",
      },
      {
        description:
          "Writes a published cut-off's instant, an ISO 8601 string, as the time above a dashed vertical at its place.\n\nThe cut-offs are drawn exactly when it is given: an instant needs a locale and a time zone,\nwhich are the application's. A hut-to-hut itinerary publishes none and draws nothing either\nway. `<ItineraryWaypointTable>` and `<WaypointCard>` take the same function.\n\nA new function is a new way of printing, and redraws the cut-offs: hold it stable.",
        name: "formatCutoffAt",
        required: false,
        type: "(iso: string) => string",
      },
      {
        control: { kind: "boolean" },
        description:
          "Which scales the chart draws.\n\n`true` is the elevation scale alone. The distance scale takes a row of its own under the chart,\nso it is asked for by name: `axis={{ x: true, y: true }}`.\n\nThe elevation scale is off by default, because the stat strip already states the itinerary's\nlowest and highest point and two places for one number is one too many. An application that\ndrops the strip turns it on, and gives up the width the labels need.",
        name: "axis",
        required: false,
        type: "boolean | ProfileAxes",
      },
      {
        control: { kind: "boolean" },
        description:
          "A chip per section, stating what each leg between two dividing waypoints measures.\n\nReads the same array an `<ItinerarySegmentTable>` renders its rows from, so a leg stated under\nthe chart and the same leg stated in a table beside it cannot disagree. Renders nothing when no\nwaypoint divides the itinerary.",
        name: "sections",
        required: false,
        type: "boolean | ItineraryProfileSectionsOptions",
      },
      {
        control: { kind: "boolean" },
        description:
          "A card at the cursor, stating what it is standing on.\n\nTurning it on makes the stat strip stop following the cursor and state the itinerary's own\nfigures instead: the card owns *what is under my finger*, the strip owns *what the itinerary\nis*, and one number in two places is one place too many.",
        name: "tooltip",
        required: false,
        type: "boolean | ItineraryProfileTooltipOptions",
      },
      {
        control: { kind: "boolean" },
        description:
          "A strip below the chart showing the whole itinerary, with the drawn window marked on it.\n\nDragging either handle zooms the curve. The window is `range`; this is the control for it, and\na caller who wants their own control leaves this off and drives `range` directly.",
        name: "navigator",
        required: false,
        type: "boolean | ItineraryProfileNavigatorOptions",
      },
      {
        description:
          "The stretch of itinerary the chart is drawn over, in meters.\n\nOmit for an uncontrolled window the element moves itself. `null` is a controlled whole\nitinerary: the value a controlled caller writes to zoom out without knowing the itinerary's\nlength.",
        name: "range",
        required: false,
        type: "ProfileRange | null",
      },
      {
        description:
          "Where an uncontrolled window starts.\n\nRead once for each itinerary: a new `itineraryId` or `surface` is a new profile, and its\nwindow starts over from here. A chain stated through `segments` is one profile for as long\nas it is stated, whatever id comes with it, so its window stands until the element is\ngiven another `key`.",
        name: "defaultRange",
        required: false,
        type: "ProfileRange",
      },
      {
        description:
          "The window, reported whole after every change a reader makes to it. Additive: it does not suppress the drag.\n\nA new itinerary starts over from `defaultRange` and reports nothing, as an uncontrolled\nvalue under a new key does. A caller that mirrors the window controls `range`.",
        name: "onRangeChange",
        required: false,
        seam: "handler",
        type: "(next: ProfileRange) => void",
      },
      {
        description:
          "Reports a failure resolving `itineraryId`. Additive; the element renders its empty state.",
        name: "onError",
        required: false,
        seam: "handler",
        type: "(error: Error) => void",
      },
      {
        control: { kind: "enum", options: ["route", "sea-level", "window"] },
        description:
          'What the elevation scale is measured from.\n\n`"route"` anchors on the route\'s own lowest point, because the shape of the climb is what a\nprofile is read for: a ridge traverse between 2,400 m and 2,600 m is a ridge, not a flat line\nfour fifths of the way up an empty frame.\n\n`"sea-level"` anchors at zero instead. The curve then sits at its true height in the frame, so\ntwo itineraries drawn side by side are comparable, at the cost of flattening every itinerary\nthat does not start near the sea.\n\n`"window"` anchors on whatever the drawn window contains, so zooming into a section rescales to\nthat section\'s own climb rather than keeping it a fifth of the frame tall.',
        name: "anchor",
        required: false,
        type: '"route" | "sea-level" | "window"',
      },
      {
        control: { kind: "string" },
        description:
          "The leg to light on the curve, by id.\n\nTakes over the map-to-chart half. With a map in context the chart already listens for the leg\nunder the map's own cursor and tints its stretch; state an id here and the element stops\nlistening and tints yours instead, the same controlled/uncontrolled pair `range` draws. Matches\na leg's own id or, on a collapsed group, any of its members'.\n\n`null` states \"nothing\", which is not the same as leaving it out: one is a caller saying the\ncursor is off the route, the other is a caller not answering the question at all.",
        name: "highlighted",
        required: false,
        type: "string | null",
      },
      {
        control: { kind: "string" },
        description: "Colour of the lit stretch on the MAP.",
        name: "emphasisColor",
        required: false,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description:
          "Renders the profile non-interactive.\n\nNo cursor, no scrub, no map marker, no lit leg, no drag. The curve, the strip and the bands\nstill draw and still read: a disabled profile is a picture, not a blank. A scrub a child asks\nfor through `useProfilePlot().scrubTo` is not taken either, and `onHover` hears `null` for the\ncursor a disable clears.",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description:
          "The id the MAP's cursor marker is pinned under.\n\nA map marker, not a waypoint: this is the pin MapLibre draws where the cursor is, and it is the\none place in this element where \"marker\" means the renderer's own object.",
        name: "markerId",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "Colour of the map's cursor marker.",
        name: "markerColor",
        required: false,
        type: "string",
      },
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "ItineraryProfileStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
    ],
    propsTypeName: "ItineraryProfileProps",
    summary: "The element's root: one surface per itinerary.",
  },
  ItineraryProfilePanel: {
    description:
      "It reads the itinerary from the nearest {@link ItineraryViewProvider }, or from the studio's\nactive itinerary where none is mounted, and derives the series the chart plots, scoped to the\nselection and with a live edit swapped in. What the profile drives goes through the tool bus:\na hover moves the map's follow-marker (`studio.itinerary.profileMarker.set`, throttled and\nfire-and-forget), the segment chooser selects a segment and fits the map to it\n(`studio.itinerary.segment.select`, `studio.map.fitBoundsToSegment`), and a waypoint mark's\nclick opens the place and frames it (`studio.map.fitBoundsOrFly`, `studio.map.resetNorth`).\nThe tooltip's terrain readout comes from the live map's DEM sampler.\n\nThe follow-marker moves in a preview (import) view as in a persisted one: the import draft\nreaches the panel through the same view. Selection is a persisted-editing concern, so a preview\ndrives none. The panel renders nothing with no itinerary in view, and the chart renders nothing\nfor a series that carries no elevation (2D geometry). Each section prop is forwarded to the\nchart as given, so a caller (the embed) can publish a subset of sections; a mount that passes\nnone renders the full profile.",

    importPath: "@mountaya/studio-react",
    name: "ItineraryProfilePanel",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Shrinks the profile to its characteristics row while a floating panel is open. A height-aware host (the in-app studio and the embed) drives it from the resolved profile visibility.",
        name: "collapsed",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Whether the panel is drawn in an embed. The in-app studio renders the actions menu beside the segment chooser; an embed renders none, since its download lives in a map control. It also moves the analysis gate from the viewer's `profile-insights` entitlement to the publisher's `showAnalysis`.",
        name: "embedded",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description:
          "One colour every band is painted in, in place of each segment's own, so the chart matches an embed's single-colour map. Omitted, each band keeps its segment's colour.",
        name: "segmentColor",
        required: false,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "true",
        description: "Whether the analysis section (the surface and way-type bars) renders.",
        name: "showAnalysis",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "true",
        description: "Whether the characteristics row renders.",
        name: "showCharacteristics",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "true",
        description: "Whether the elevation chart renders.",
        name: "showElevation",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Offers the reader their own travel pace, re-timing the profile's figures for their session alone. The surfaces that publish an itinerary to somebody else (the embed and the share view) pass it; the authenticated studio has a rate field per segment.",
        name: "showPaceControl",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "enum", options: ["published"] },
        description:
          'The snapshot the pace re-time is asked for. A preview that authenticates as the owner passes `"published"`, so the reader\'s pace re-times the route a visitor is served rather than the draft. Omitted, the credential decides, which is the published copy for a visitor.',
        name: "surface",
        required: false,
        type: "ItinerarySurface",
      },
    ],
    propsTypeName: "ItineraryProfilePanelProps",
    summary:
      "ItineraryProfilePanel is the docked elevation profile of the itinerary in view: the chart, its characteristics and its analysis, which the `profile-insights` entitlement gates.",
  },
  ItinerarySegmentList: {
    description:
      "Named for what it resolves rather than for what it draws: the published `<SegmentList>` it mounts\ntakes a chain, and this is the component that decides WHICH chain and, from the granted\ncapabilities, which of the two lists reads it. It reads the itinerary from the nearest\n{@link ItineraryViewProvider }, or from the studio's store where none is mounted, and renders\nnothing with none open, the same empty case the editor bails on.\n\nThe read-only signal is the granted capabilities and the itinerary's scope together\n(`useOpenItineraryEditable`): a surface that withholds \"editor\" (the public share view), and an\nitinerary whose organization's agreement does not include Map Studio, get the read-only list\ninstead, the same rows with every mutating affordance absent. The bus would refuse each write\nanyway, so the UI never offers one it cannot dispatch.",

    importPath: "@mountaya/studio-react",
    name: "ItinerarySegmentList",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary: "ItinerarySegmentList is the studio's segment editor for the OPEN itinerary.",
  },
  ItinerarySegmentTable: {
    description:
      "**A row is a segment**: one of the itinerary's own authored legs, carrying the name someone gave\nit and the figures the server computed for it. Its stretch of the shared sparkline is measured\nfrom its own geometry, so the legs tile the curve exactly and no boundary is guessed.\n\nThe same data `<ItineraryProfile>` draws as a curve, read as a roadbook. Both elements derive\ntheir rows through one function, so a leg stated here and the same leg stated under the chart\ncannot disagree, which is the entire reason the derivation lives in `@mountaya/elements/profile`\nrather than in either element.\n\nA collapsed group is one row, as it is in the assembled studio's segment list: the run reads as\nthe leg a reader thinks of it as, and its figures are its members' summed.\n\n**Custom columns are the point.** Cut-off times, services, aid-station notes, a sponsor's column:\nthey go in the same ordered list the built-in ones do, so a caller reorders, drops or interleaves\nrather than being handed ours followed by theirs.\n\n**It does not sort, and that is a decision.** An itinerary table is ordered by distance along the\nitinerary. Reordering it makes two columns lie: the distance column's delta is measured against\nthe row above, and the section profile lights a contiguous stretch. A table sorted by a cut-off\ntime would state a delta against a row that is not adjacent to it on the ground.\n\nIts parts are published on the element (`ItinerarySegmentTable.Row`, `.Sparkline`, `.Toolbar`)\nfor a caller composing their own layout out of them.",

    importPath: "@mountaya/elements-react/elements",
    name: "ItinerarySegmentTable",
    package: "@mountaya/elements-react",
    parts: [
      {
        name: "Row",
        props: [
          { description: "", name: "row", required: true, type: "ItinerarySegmentRow" },
          {
            control: {
              kind: "enumArray",
              options: [
                "segment",
                "profile",
                "distance",
                "elevation",
                "altitude",
                "duration",
                "difficulty",
              ],
            },
            description: "",
            name: "columns",
            required: true,
            type: "readonly ItinerarySegmentTableColumnSpec[]",
          },
          { description: "", name: "units", required: true, type: "UnitFormatOptions" },
          {
            control: { kind: "string" },
            description:
              "The whole itinerary's curve, built once by the table and shared by every row.",
            name: "polyline",
            required: true,
            type: "string",
          },
          {
            control: { kind: "string" },
            description: "",
            name: "area",
            required: true,
            type: "string",
          },
          {
            control: { kind: "number" },
            description: "",
            name: "totalDistance",
            required: true,
            type: "number",
          },
          {
            control: { kind: "boolean" },
            description: "",
            name: "hovered",
            required: true,
            type: "boolean",
          },
          {
            control: { kind: "boolean" },
            description: "Renders the row non-interactive: no hover, no press.",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          {
            description: "Called with the row as the pointer enters it or focus arrives in it.",
            name: "onHover",
            required: false,
            seam: "handler",
            type: "(row: ItinerarySegmentRow | null) => void",
          },
          {
            description: "",
            name: "onSegmentClick",
            required: false,
            seam: "handler",
            type: "(id: string) => void",
          },
          {
            control: { kind: "boolean" },
            description:
              "Whether this row is the highlighted one, scrolled into view as it becomes so.\n\nInside the nearest ancestor that scrolls (a panel, a sidebar, a table given a height) and\nby the least that shows the row. The page is never scrolled, so a row in no scrolling\ncontainer stays where it is.",
            name: "revealed",
            required: false,
            type: "boolean",
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ItinerarySegmentTableRowStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItinerarySegmentTableRowProps",
        summary: "",
      },
      {
        name: "Sparkline",
        props: [
          {
            control: { kind: "string" },
            description:
              "The whole itinerary's curve, as an SVG `points` string. Built once by the table.",
            name: "polyline",
            required: true,
            type: "string",
          },
          {
            control: { kind: "string" },
            description:
              "The whole itinerary's filled region, as an SVG path. Built once by the table.",
            name: "area",
            required: true,
            type: "string",
          },
          {
            control: { kind: "number" },
            description: "",
            name: "totalDistance",
            required: true,
            type: "number",
          },
          {
            description: "The stretch this row lights, in meters.",
            name: "range",
            required: true,
            type: "ProfileRange",
          },
          {
            control: { kind: "string" },
            description: "",
            name: "color",
            required: false,
            type: "string",
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItinerarySparklineProps",
        summary: "",
      },
      {
        name: "Toolbar",
        props: [
          { description: "", name: "children", required: false, type: "ReactNode" },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItinerarySegmentTableToolbarProps",
        summary:
          "ItinerarySegmentTableToolbar is a styled strip above the table, and nothing else.",
      },
    ],
    props: [
      {
        control: { kind: "string" },
        description:
          "An itinerary to tabulate, resolved through the client on the provider.\n\nOne prop instead of a fetch and three mappings: the geometry, the server's own figures and\nthe itinerary's own chain arrive in a single round trip, with each group collapsed to one\nleg. A profile given the same id shares that request.",
        name: "itineraryId",
        required: false,
        type: "string",
      },
      {
        control: { kind: "enum", options: ["published"] },
        description: "Reads the itinerary's published snapshot rather than its draft.",
        name: "surface",
        required: false,
        type: '"published"',
      },
      {
        description:
          "The legs to tabulate, for a caller already holding them.\n\n**A row is a segment.** Each leg states its own name, its own colour and the figures the server\ncomputed for it, and its stretch of the sparkline is measured from its own geometry, so nothing\nhere is projected, estimated or ordered by proximity. `itinerarySegments` on\n`@mountaya/elements/data` maps an API itinerary onto this shape; a caller with no Mountaya\nitinerary states the fields by hand.\n\nGiven alongside `itineraryId`, this wins: a chain a caller is already holding is never worth\ndiscarding in favour of a request.\n\nA new array of the same legs, in the same order, is the same chain: the rows are built again\nonly when a leg object changes.",
        name: "segments",
        required: false,
        type: "readonly ItinerarySegmentInput[]",
      },
      {
        control: {
          kind: "enumArray",
          options: [
            "segment",
            "profile",
            "distance",
            "elevation",
            "altitude",
            "duration",
            "difficulty",
          ],
        },
        description:
          "Which columns the table carries, in the order given. `[]` drops the table.\n\nA new list of the same columns is the same list. A custom column whose `cell` is a new function\nrenders every row again: hold that column stable.",
        name: "columns",
        required: false,
        type: "readonly ItinerarySegmentTableColumnSpec[]",
      },
      {
        description: "Units, slope notation and the locale to format in.",
        name: "units",
        required: false,
        type: "UnitFormatOptions",
      },
      {
        description:
          "Rendered above the table, in a strip of its own. No strip is drawn for a slot React draws nothing for (`undefined`, `null`, `false`).",
        name: "toolbar",
        required: false,
        type: "ReactNode",
      },
      {
        description:
          "Takes over what a hover does.\n\nLeft unset, and with a map in context, the table drives the map itself: it lights the hovered\nleg on the route and pins a marker at its end, and it listens for what the map's own cursor is\nover so the two follow each other. Set, all of that is suppressed and the element becomes\npresentational: the handler receives the hovered row, or `null` on leave.\n\nA table disabled over a pointed row reports `null` as well, once.",
        name: "onHover",
        required: false,
        seam: "handler",
        type: "(row: ItinerarySegmentRow | null) => void",
      },
      {
        description:
          "Called with a leg's id when its name is pressed. Unset leaves the name as text.\n\nThe id the caller gave, which for a collapsed group is the group's synthetic id rather than any\none member's: the row stands for the run, so pressing it means the run.",
        name: "onSegmentClick",
        required: false,
        seam: "handler",
        type: "(id: string) => void",
      },
      {
        control: { kind: "string" },
        description:
          "The row to light, in addition to whatever the pointer is over.\n\nTakes over the map-to-row half. With a map in context the table already listens for the leg\nunder the map's own cursor and lights that row; state an id here and the element stops\nlistening and lights yours instead, the same controlled/uncontrolled pair `range` draws on the\nchart. Matches a row's own id or, on a collapsed group, any of its members'.",
        name: "highlighted",
        required: false,
        type: "string | null",
      },
      {
        control: { kind: "string" },
        description: "Colour of the lit stretch on the map.",
        name: "emphasisColor",
        required: false,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "Renders the table non-interactive. No hover echo, no map marker, no press.",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Reports a failure resolving `itineraryId`. Additive; the element renders its empty state.",
        name: "onError",
        required: false,
        seam: "handler",
        type: "(error: Error) => void",
      },
      {
        control: { kind: "string" },
        defaultValue: "ITINERARY_SEGMENT_TABLE_MARKER_ID",
        description: "The id the MAP's cursor marker is pinned under.",
        name: "markerId",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "Colour of the map's cursor marker.",
        name: "markerColor",
        required: false,
        type: "string",
      },
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "ItinerarySegmentTableStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
    ],
    propsTypeName: "ItinerarySegmentTableProps",
    summary: "ItinerarySegmentTable states every leg of an itinerary, in the order it runs.",
  },
  ItinerarySubHeader: {
    description:
      'It is its own band because publication is a different KIND of statement from\neverything else in the header. The header names the itinerary (what it is called,\nwhat it is routed for, how far it goes), all of which the user is editing.\nPublication is about a second copy of the itinerary that other people see, and a\nmark for it beside the activity would make the header a list of unlike facts. Given\na rule of its own, the header stays identity and this band stays one decision.\n\nIt is ONE row: the state on the left, the act on the right. Three rows (a filled\nbadge, a button, and a sentence beneath) would say the same thing three times\n("MODIFIÉ", "Publier les modifications", "Modifié depuis votre dernière publication")\nin the loudest ink on the panel. The state is a coloured dot and a word, the age of\nwhat is live follows it in the dimmed metadata idiom the rows use, and the sentence\nsits in the line\'s tooltip, where an explanation belongs once the state itself is\nlegible. The button keeps only the verb: the line beside it already says what changed.\n\nWith nothing left to publish the button stops being a verb: a lime, checked "Up to date"\npill, the studio\'s locked-status treatment for a state that is settled. A DISABLED\nbutton is the wrong shape for it: gray and dimmed reads as a primary action the panel\nhas broken, when the truth is that the work is done. That state outranks every blocker\nbelow it, because a peer holding a segment cannot make a settled itinerary un-settled.\n\nThe pill answers nothing. Withdrawal is not offered on this band at all: the itinerary\naction menu sits in the header one row above and carries the Unpublish item, and a\ndropdown here holding that single act would put the same verb on screen twice, one row\napart.\n\nIt is ALWAYS mounted for an open itinerary, and the action is always rendered, standing\ndown with its reason on hover rather than unmounting. A band that disappeared whenever\nthere was nothing to publish would make the segment list jump up the panel the moment a\npublish lands and jump back down on the next edit. A row that moves under the user is\nworse than a row that spends its height saying "everything is live", which is a thing\nworth knowing anyway.\n\nTwo readers are offered no act. One who may not edit the itinerary, a member whose role reads\nonly among them: publishing writes it. And one who does not hold the `publish` entitlement,\nwhose organization\'s agreement does not include Map Publishing: what they would publish is\nserved to nobody. The band says what the public sees and offers no act, at the same height.\n\nThe row cannot clip at any panel width or in any language: the action is fixed-width\nand never shrinks, and the status text gives up its width first and ellipsises. The\ndot and the state word are what must survive, and both are before the truncation\npoint.\n\nThe action stands down, with the reason on hover, whenever something is in flight\nthat publishing would freeze half-finished: a collaborator holding any of the\nitinerary\'s entities, the co-pilot driving, or the local user\'s own open edit.\nThose are the same preconditions the tool re-checks and the server enforces;\nshowing them here just saves a doomed round-trip.',

    importPath: "@mountaya/studio-react",
    name: "ItinerarySubHeader",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      'ItinerarySubHeader is the band under the editor\'s header that answers "what does the public see?" and offers the one act that changes that answer.',
  },
  ItineraryWaypointList: {
    description:
      "The read-only signal is the granted capabilities and the itinerary's scope together\n(`useOpenItineraryEditable`): a surface that withholds \"editor\" (the public share view), and an\nitinerary whose organization's agreement does not include Map Studio, get the same rows with\nevery mutating affordance absent. The bus would refuse each write anyway, so the UI never offers\none it cannot dispatch.",

    importPath: "@mountaya/studio-react",
    name: "ItineraryWaypointList",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "ItineraryWaypointList is the studio's waypoint editor for the OPEN itinerary. It reads the itinerary from the nearest {@link ItineraryViewProvider }, or from the studio's store where none is mounted, and renders nothing with none open, the same empty case the editor bails on.",
  },
  ItineraryWaypointTable: {
    description:
      "The companion to `ItinerarySegmentTable`, and the two answer different questions rather than one\nquestion twice. A segment is what the itinerary's author DREW: it has a name, a colour, a\nduration the server computed and a mode. A waypoint is what they MARKED, and it has a set of\nservices, a cut-off and whatever else they needed to publish about the place. A race course is\nread as places; a hut-to-hut tour is read as legs. Neither table is a degenerate case of the\nother, which is why one table cannot serve both.\n\n**One row per pass, keyed `id#pass`.** A loop that returns to Chamonix produces two rows by\nconstruction, because `resolveWaypoints` already resolves a place once per close approach. A\nproximity radius would put a loop's start and finish at the same meter; this avoids it without a\nradius to tune.\n\n**The figures come off the same cumulative scan the segment table reads.** The last row's running\nascent equals `profileTotals(series).ascent` exactly, so the two tables over one itinerary state\nthe same totals rather than two estimates that nearly agree.\n\nIts parts are published on the element (`ItineraryWaypointTable.Row`, `.Sparkline`, `.Toolbar`)\nfor a caller composing their own layout out of them.",

    importPath: "@mountaya/elements-react/elements",
    name: "ItineraryWaypointTable",
    package: "@mountaya/elements-react",
    parts: [
      {
        name: "Row",
        props: [
          { description: "", name: "row", required: true, type: "ItineraryWaypointRow" },
          {
            control: {
              kind: "enumArray",
              options: [
                "waypoint",
                "profile",
                "distance",
                "elevation",
                "altitude",
                "amenities",
                "cutoff",
                "properties",
              ],
            },
            description: "",
            name: "columns",
            required: true,
            type: "readonly ItineraryWaypointTableColumnSpec[]",
          },
          { description: "", name: "units", required: true, type: "UnitFormatOptions" },
          {
            control: { kind: "string" },
            description:
              "The whole itinerary's curve, shared by every row rather than rebuilt per row.",
            name: "polyline",
            required: true,
            type: "string",
          },
          {
            control: { kind: "string" },
            description: "",
            name: "area",
            required: true,
            type: "string",
          },
          {
            control: { kind: "number" },
            description:
              "The itinerary's full length, which the sparkline scales its lit stretch against.",
            name: "totalDistance",
            required: true,
            type: "number",
          },
          {
            control: { kind: "boolean" },
            description: "",
            name: "hovered",
            required: true,
            type: "boolean",
          },
          {
            control: { kind: "boolean" },
            description: "Renders the row non-interactive: no hover.",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          {
            description: "",
            name: "onHover",
            required: false,
            seam: "handler",
            type: "(row: ItineraryWaypointRow | null) => void",
          },
          {
            description:
              "Writes a cut-off's instant, an ISO 8601 string. Without it the `cutoff` cell is empty.",
            name: "formatCutoffAt",
            required: false,
            type: "(iso: string) => string",
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ItineraryWaypointTableRowStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItineraryWaypointTableRowProps",
        summary: "",
      },
      {
        name: "Sparkline",
        props: [
          {
            control: { kind: "string" },
            description:
              "The whole itinerary's curve, as an SVG `points` string. Built once by the table.",
            name: "polyline",
            required: true,
            type: "string",
          },
          {
            control: { kind: "string" },
            description:
              "The whole itinerary's filled region, as an SVG path. Built once by the table.",
            name: "area",
            required: true,
            type: "string",
          },
          {
            control: { kind: "number" },
            description: "",
            name: "totalDistance",
            required: true,
            type: "number",
          },
          {
            description: "The stretch this row lights, in meters.",
            name: "range",
            required: true,
            type: "ProfileRange",
          },
          {
            control: { kind: "string" },
            description: "",
            name: "color",
            required: false,
            type: "string",
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItinerarySparklineProps",
        summary: "",
      },
      {
        name: "Toolbar",
        props: [
          {
            defaultValue: "[]",
            description:
              "The amenities this itinerary actually offers somewhere, ordered by the name a reader sees in the provider's locale.\n\nOnly the ones present. A legend of every amenity, greyed where absent, is a different design\ndecision and belongs to the caller: this row is a FILTER, and a chip that can only ever empty\nthe table is a control that lies about what it does.",
            name: "available",
            required: false,
            type: "readonly string[]",
          },
          {
            defaultValue: "[]",
            description: "The amenities currently filtering the table. Empty means no filter.",
            name: "selected",
            required: false,
            type: "readonly string[]",
          },
          {
            description:
              "Called with the next selection when a chip is pressed. Unset leaves the chips out.",
            name: "onSelect",
            required: false,
            seam: "handler",
            type: "(amenities: readonly string[]) => void",
          },
          {
            control: { kind: "boolean" },
            defaultValue: "false",
            description: "",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          { description: "", name: "children", required: false, type: "ReactNode" },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ItineraryWaypointTableToolbarStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ItineraryWaypointTableToolbarProps",
        summary:
          "ItineraryWaypointTableToolbar is the strip above the table, with the amenity filter in it.",
      },
    ],
    props: [
      {
        control: { kind: "string" },
        description:
          "The itinerary to resolve through the client on the provider.\n\nThe same contract every resource-taking element in this package keeps: pass an id and the\nelement fetches, pass `waypoints` and it draws what you gave it. Passed both, the caller's list\nwins, and the id still resolves the geometry where `geojson` is not given.",
        name: "itineraryId",
        required: false,
        type: "string",
      },
      {
        control: { kind: "enum", options: ["published"] },
        description: "Reads the published snapshot rather than the draft.",
        name: "surface",
        required: false,
        type: '"published"',
      },
      {
        description:
          "The places, stated directly.\n\nA `Waypoint` rather than the wire's own record: the element's shape, which `itineraryWaypoints`\non `@mountaya/elements/data` maps a stored itinerary onto. A new array of the same places is\nthe same list.",
        name: "waypoints",
        required: false,
        type: "readonly Waypoint[]",
      },
      {
        description: "The geometry the places are projected onto.",
        name: "geojson",
        required: false,
        type: "ProfileInput | null",
      },
      {
        control: {
          kind: "enumArray",
          options: [
            "waypoint",
            "profile",
            "distance",
            "elevation",
            "altitude",
            "amenities",
            "cutoff",
            "properties",
          ],
        },
        description:
          "Which columns appear, in the order they appear.\n\nA new list of the same columns is the same list. A custom column whose `cell` is a new function\nrenders every row again: hold that column stable.",
        name: "columns",
        required: false,
        type: "readonly ItineraryWaypointTableColumnSpec[]",
      },
      {
        description: "Units, slope notation and the locale to format in.",
        name: "units",
        required: false,
        type: "UnitFormatOptions",
      },
      {
        description:
          "Rendered in the toolbar strip, beside the amenity filter. With no filter chips to draw, no strip is drawn for a slot React draws nothing for (`undefined`, `null`, `false`).",
        name: "toolbar",
        required: false,
        type: "ReactNode",
      },
      {
        defaultValue: "[]",
        description:
          "The amenities filtering the table. Empty, or unset, shows every place.\n\nControlled by the caller, with `onFilterChange` reporting a chip press. It is the shape the\nsegment table's `highlighted` takes, for the same reason: an element that owned this state\nwould fight an application that also wants to drive it from a URL.",
        name: "filter",
        required: false,
        type: "readonly string[]",
      },
      {
        description:
          "Called with the next filter when a chip is pressed. Unset leaves the chips out entirely.",
        name: "onFilterChange",
        required: false,
        seam: "handler",
        type: "(amenities: readonly string[]) => void",
      },
      {
        description:
          "Writes a cut-off's instant, an ISO 8601 string, in the `cutoff` column.\n\nWithout it the column is empty: an instant needs a locale and a time zone, which are the\napplication's. `<ItineraryProfile>` and `<WaypointCard>` take the same function. A new\nfunction renders every row again: hold it stable.",
        name: "formatCutoffAt",
        required: false,
        type: "(iso: string) => string",
      },
      {
        description:
          "Called as the pointer moves across rows, with `null` on leave and, once, when the table is disabled over a pointed row.\n\nSet, the table stops driving the map: it pins no cursor marker and the caller decides. The\none line that makes this element presentational rather than a map controller.",
        name: "onHover",
        required: false,
        seam: "handler",
        type: "(row: ItineraryWaypointRow | null) => void",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        description: "",
        name: "onError",
        required: false,
        seam: "handler",
        type: "(error: Error) => void",
      },
      {
        control: { kind: "string" },
        defaultValue: "ITINERARY_WAYPOINT_TABLE_MARKER_ID",
        description: "The map marker id the hovered place is pinned under.",
        name: "markerId",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "markerColor",
        required: false,
        type: "string",
      },
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "ItineraryWaypointTableStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
    ],
    propsTypeName: "ItineraryWaypointTableProps",
    summary:
      "ItineraryWaypointTable states an itinerary as its PLACES, one row per place per pass.",
  },
  LanguageSelect: {
    description:
      "It owns no state and no dispatch. A caller passes the current language and an `onChange` that\nroutes the choice wherever it belongs (through a tool bus, into a form), so a person clicking\nand an agent setting the same preference travel the same path.\n\nWhat the component is actually for is the geometry, which is easy to get wrong and invisible\nuntil the two halves are open at once: Mantine centres a left section and lays out a dropdown\noption on its own gap, so a mark in the closed input and the same mark in the open list sit at\ndifferent distances from their text. Both are pinned to one spacing token here.",

    importPath: "@mountaya/studio-react",
    name: "LanguageSelect",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "aria-label",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "description",
        required: false,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "label",
        required: false,
        type: "string",
      },
      {
        description: "",
        name: "onChange",
        required: true,
        seam: "handler",
        type: "(language: MapLanguage) => void",
      },
      {
        description: "",
        name: "options",
        required: true,
        type: "readonly { label: string; value: MapLanguage }[]",
      },
      {
        description: "",
        name: "renderMark",
        required: false,
        type: "(language: MapLanguage) => ReactNode",
      },
      {
        control: { kind: "enum", options: ["de", "en", "es", "fr", "it", "ja", "zh"] },
        description: "",
        name: "value",
        required: true,
        type: "MapLanguage",
      },
    ],
    propsTypeName: "LanguageSelectProps",
    summary:
      "LanguageSelect is the language picker: a Select carrying an optional mark flush-left in its input and beside every option, with the selected row carrying the native combobox check.",
  },
  LayersPanel: {
    description:
      "Three sections, and the split between the last two is the manifest's, not a preference: base maps\nreplace one another, classic overlays replace one another, and enrichments stack. Rendering all\nthree as one flat list of toggles (the obvious first design) makes a user turn on two overlays\nthat repaint the same ground and conclude the map is broken.\n\nEvery word is in the provider's language: the headings from its copy, and each terrain, category\nand overlay by the name the catalogue carries for that language, falling back to English and then\nto the slug. The language of the map's own labels is the map's and is not read here.",

    importPath: "@mountaya/elements-react",
    name: "LayersPanel",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "LayersPanelStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
      {
        description:
          "Reports an entry the viewer's agreement does not include, so it renders locked.\n\nWhat an agreement includes is the API's to enforce: the Tile API answers 403 whatever a\npicker shows. This exists so an application that already knows what the agreement includes\ncan say so before the request, rather than letting a press fail silently.",
        name: "isLocked",
        required: false,
        type: "(slug: string) => boolean",
      },
      {
        description: "Called when a locked entry is pressed, for an application's own notice.",
        name: "onLockedClick",
        required: false,
        seam: "handler",
        type: "(slug: string) => void",
      },
    ],
    propsTypeName: "LayersPanelProps",
    summary: "LayersPanel is the base-map and overlay picker.",
  },
  ListSkeleton: {
    description:
      "Reach for it when the pending content is a LIST whose row shape the placeholder can mirror.\nWhere the shape is unknown or is not a list, a centred block loader reserving a height is the\nbetter answer: a stripe list promises rows, and a surface that then renders a form has told\nthe reader something untrue for the length of a request.",

    importPath: "@mountaya/studio-react",
    name: "ListSkeleton",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "number" },
        defaultValue: "44",
        description:
          "Each stripe's height, in pixels: a list row's resting height, so the skeleton holds the space the rows will.",
        name: "height",
        required: false,
        type: "number",
      },
      {
        control: { kind: "number" },
        defaultValue: "3",
        description: "How many stripes to draw.",
        name: "rows",
        required: false,
        type: "number",
      },
    ],
    propsTypeName: "ListSkeletonProps",
    summary:
      "ListSkeleton renders a vertical stack of skeleton stripes shown while a list loads. It standardizes the loading placeholder so every list-backed surface telegraphs pending state the same way; callers that need a different row count or height pass `rows` / `height`.",
  },
  LockedActionsButton: {
    description: "",

    importPath: "@mountaya/studio-react",
    name: "LockedActionsButton",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "The disabled trigger's accessible name.",
        name: "ariaLabel",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description:
          "The user id of the peer holding the entity's lock, whose name the tooltip states.",
        name: "lockHolderId",
        required: true,
        type: "string",
      },
    ],
    propsTypeName: "LockedActionsButtonProps",
    summary:
      "LockedActionsButton is the peer-lock specialization of {@link DisabledActionsButton}: it resolves the \"{holder} is editing this…\" label through the host's peer-name seam and renders the shared disabled kebab with it, so a peer-locked row's actions trigger names WHO holds the lock. The presentation (the greyed kebab, the focusable span shim, the focus-revealing tooltip) lives in the shared component; this only supplies the holder-named label, the same one the row body's tooltip resolves.",
  },
  MapControls: {
    description: "",

    importPath: "@mountaya/elements-react",
    name: "MapControls",
    package: "@mountaya/elements-react",
    parts: [
      {
        name: "Compass",
        props: [
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            control: { kind: "boolean" },
            defaultValue: "false",
            description: "",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          {
            description:
              "Overrides the reset. Unset, the control resets the map's bearing and pitch itself.\n\nSupplying one also makes the control live with no map in context: the default is what\nneeded one, and a handler that replaces it does not. The rose then reads the identity\nbearing, because there is no camera to track.",
            name: "onResetNorth",
            required: false,
            seam: "handler",
            type: "() => void",
          },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "CompassControlStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "CompassControlProps",
        summary:
          "CompassControl resets the bearing to north, and doubles as an orientation readout.",
      },
      {
        name: "Directions",
        props: [
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            control: { kind: "boolean" },
            description: "",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          {
            description:
              "What a press does. **Required**, unlike the `on*` on every other control here.\n\nThe rest of this package's controls default to driving the map, and their handler is an\noverride. These two have nothing to drive: exporting a route and routing between points\nboth end in a surface (a dialog, a panel, a download) that belongs to the application\nand cannot be guessed. A control that rendered without one would be a button that looks\nlive and does nothing, which is worse than not offering it.",
            name: "onClick",
            required: true,
            seam: "handler",
            type: "() => void",
          },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ActionControlStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ActionControlProps",
        summary:
          "DirectionsControl asks the application to open directions for what the map is showing.",
      },
      {
        name: "Download",
        props: [
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            control: { kind: "boolean" },
            description: "",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          {
            description:
              "What a press does. **Required**, unlike the `on*` on every other control here.\n\nThe rest of this package's controls default to driving the map, and their handler is an\noverride. These two have nothing to drive: exporting a route and routing between points\nboth end in a surface (a dialog, a panel, a download) that belongs to the application\nand cannot be guessed. A control that rendered without one would be a button that looks\nlive and does nothing, which is worse than not offering it.",
            name: "onClick",
            required: true,
            seam: "handler",
            type: "() => void",
          },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ActionControlStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ActionControlProps",
        summary: "DownloadControl asks the application to export what the map is showing.",
      },
      {
        name: "Geolocate",
        props: [
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            control: { kind: "boolean" },
            defaultValue: "false",
            description: "",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          {
            description:
              "Overrides the request. Unset, the control asks the browser and eases the map itself.\n\nSupplying one also makes the control live with no map in context: what needed a map was\nthe easing, and an application that owns the request owns where the fix lands.",
            name: "onLocate",
            required: false,
            seam: "handler",
            type: "() => void",
          },
          {
            description:
              "Called when the browser refuses or the fix fails, for an application's own recovery UI.\n\nAlways an `Error`, as every element's `onError` is: the `MountayaElementsError` the map\nrejects with, whose message states the reason. A geolocation seam of the host's own that\nrejects with a value that is no `Error` is reported as a `MountayaElementsRejectionError`\nholding that value as its `cause`. Never called for a fix the application makes itself\nthrough `onLocate`, nor after the control has unmounted.",
            name: "onError",
            required: false,
            seam: "handler",
            type: "(error: Error) => void",
          },
          {
            control: { kind: "boolean" },
            description:
              "Reports a fix as in flight, for an application that owns the request through `onLocate`.\n\nUnset, the control tracks its own request. The pair matters: an `onLocate` that\nsuppresses the default also suppresses the state the default sets, so without this the\nbutton would sit idle and unpainted through every fix the application makes.",
            name: "locating",
            required: false,
            type: "boolean",
          },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "GeolocateControlStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
          {
            control: { kind: "boolean" },
            defaultValue: "false",
            description:
              "Suppresses the tooltip.\n\nFor an application whose `onLocate` opens a surface of its own (a permission\npre-prompt, say), and for the same reason `PanelControl` suppresses its own: a bubble\nover the surface a press just revealed reads as a rendering fault.",
            name: "tooltipDisabled",
            required: false,
            type: "boolean",
          },
        ],
        propsTypeName: "GeolocateControlProps",
        summary: "GeolocateControl centres the map on the device location.",
      },
      {
        name: "Panel",
        props: [
          {
            control: { kind: "string" },
            description: "",
            name: "label",
            required: true,
            type: "string",
          },
          {
            control: { kind: "string" },
            description: "",
            name: "tooltipTitle",
            required: true,
            type: "string",
          },
          {
            control: { kind: "string" },
            description: "",
            name: "tooltipDescription",
            required: true,
            type: "string",
          },
          {
            control: { kind: "string" },
            description: "",
            name: "glyph",
            required: true,
            type: "string",
          },
          {
            control: { kind: "string" },
            description:
              "Names the panel in the DOM, on the element that wraps the toggle and the floating panel.\n\nThe stack's dismiss-on-press reads it there: a press inside the element of the open panel\nis never a dismissal, and `MapControlsProps.openPanel` names the open panel by this value.\nA panel the stack ships, or a word of the application's own for a panel it composes.",
            name: "kind",
            required: true,
            type: "string",
          },
          {
            control: { kind: "boolean" },
            description: "",
            name: "open",
            required: true,
            type: "boolean",
          },
          {
            description:
              "Called with the state the press is asking for, not with the state it left.\n\nThe control is controlled (`open` is the caller's), so a bare notification would leave\nevery caller re-deriving `!open` from the value it just rendered, and the two disagree\nfor exactly as long as a press and a re-render are apart.",
            name: "onToggle",
            required: true,
            seam: "handler",
            type: "(open: boolean) => void",
          },
          {
            control: { kind: "boolean" },
            defaultValue: "false",
            description: "",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "The control's own outermost element, for a caller that has to point at it.\n\nOne caller and one reason: a floating panel positioned somewhere ELSE on the page has to\nexempt its own toggle from its outside-click handler, or a press on the toggle closes the\npanel and the button's own handler immediately reopens it. Reading the button out of the\ndocument by its `kind` looks equivalent and is not: it finds the FIRST match on the page,\nso two maps on one page exempt each other's button, and it finds nothing at all across a\nshadow root.\n\nA panel rendered through `children` needs none of this: it is inside this element already.",
            name: "ref",
            required: false,
            type: "Ref<HTMLDivElement>",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
          {
            description:
              "The panel itself, rendered beside the button while open.\n\nOmit it when the application positions its own panel: in a drawer, a sidebar, or a\nlayer anchored somewhere other than the button. The control is then the toggle alone,\nand everything else about it holds: the held paint, the suppressed tooltip, and the\n`kind` the stack's dismiss-on-press exempts.\n\nOne of React's empty values (`null`, `false`, what a failed condition hands) reads as\nomitted, so a conditional panel draws no empty floating box.",
            name: "children",
            required: false,
            type: "ReactNode",
          },
        ],
        propsTypeName: "PanelControlProps",
        summary: "PanelControl is a button that owns a floating panel.",
      },
      {
        name: "Search",
        props: [
          {
            control: { kind: "boolean" },
            defaultValue: "false",
            description: "",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          {
            control: {
              kind: "enumArray",
              options: ["collections", "itineraries", "places", "pois", "ways", "zones"],
            },
            description: "The branches to ask for.",
            name: "targets",
            required: false,
            type: "readonly SearchTargetName[]",
          },
          {
            control: { kind: "enum", options: ["DE", "EN", "ES", "FR", "IT", "JA", "ZH"] },
            description: "The language the Data API answers in.",
            name: "language",
            required: false,
            type: "DataLanguage",
          },
          {
            control: { kind: "number" },
            description:
              "Per-target result cap: a whole number from 1 to 50, the range the API takes.",
            name: "limit",
            required: false,
            type: "number",
          },
          {
            description:
              "The units every figure in the list is stated in: an itinerary's length and ascent, and the distance or elevation in the sentence under a place, a way or a point of interest.\n\nA change of its `length` or its `language` asks a fresh round, since those sentences are\nworded as their branch lands.",
            name: "units",
            required: false,
            type: "UnitFormatOptions",
          },
          {
            description:
              "How a row's title and second line are worded for the reader, as `SearchEngine` takes it.\n\n`resolveLabel` answers the word a wire value reads as: a category, a way's tags. `units`,\nwhen stated here, replaces the control's own `units` for the sentences this wording words,\nand an application states one or the other.",
            name: "wording",
            required: false,
            type: "SearchWording",
          },
          {
            control: { kind: "string" },
            description:
              "The query, when the application owns it. Unset, the control owns its own, whether or not `onQueryChange` is supplied.",
            name: "query",
            required: false,
            type: "string",
          },
          {
            description:
              "Called with the query each keystroke and each clear asks for. With `query` supplied it is how the application is asked to change the value. With none it observes: the control has already written its own.",
            name: "onQueryChange",
            required: false,
            seam: "handler",
            type: "(query: string) => void",
          },
          {
            control: { kind: "boolean" },
            description:
              "Whether the search is restricted to the visible area, when the application owns it. Unset, the control owns its own, whether or not `onViewportScopeChange` is supplied.",
            name: "viewportScope",
            required: false,
            type: "boolean",
          },
          {
            description:
              "Called with the scope a press on the checkbox asks for. With `viewportScope` supplied it is how the application is asked to change the value. With none it observes.",
            name: "onViewportScopeChange",
            required: false,
            seam: "handler",
            type: "(scoped: boolean) => void",
          },
          {
            description:
              "Overrides what picking a result does.\n\nUnset, the control moves the camera onto the result and drops a pin. Set, it does neither and\nthe handler decides: the assembled studio routes a pick through its own mutation surface this\nway while rendering this exact list.",
            name: "onSelect",
            required: false,
            seam: "handler",
            type: "(row: SearchRow) => void",
          },
          {
            description:
              "Overrides how a round reaches the API. Unset, it routes the map's own `search`.",
            name: "onSearch",
            required: false,
            seam: "handler",
            type: "SearchFetch",
          },
          {
            description:
              "Called once for each branch whose request failed, with the failure.\n\nAlways an `Error`, as every element's `onError` is: an `onSearch` that rejects with any other\nvalue is reported as a `MountayaElementsRejectionError` holding that value as its `cause`. It\nreports and replaces nothing: the list still shows the branch's error line and its retry.",
            name: "onError",
            required: false,
            seam: "handler",
            type: "(error: Error) => void",
          },
          {
            description:
              "The pin dropped on a picked result. Its id and position are the control's.",
            name: "resultMarker",
            required: false,
            type: 'Omit<MarkerOptions, "id" | "lngLat">',
          },
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "SearchControlStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "SearchControlProps",
        summary:
          "SearchControl is a search box over Mountaya's unified search, with the results under it.",
      },
      {
        name: "StreetView",
        props: [
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            control: { kind: "boolean" },
            defaultValue: "false",
            description: "",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "StreetViewControlStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
          {
            control: { kind: "number" },
            defaultValue: "13",
            description: "The zoom below which the handle is inert.",
            name: "minZoom",
            required: false,
            type: "number",
          },
          {
            control: { kind: "string" },
            description:
              "The Maps JS API key the coverage check is made with.\n\nNever ours: Street View is Google's product billed to whoever asks for it, so the key\nbelongs to the application. Without one, and without an `onDrop` of your own, the\ncontrol renders nothing at all, because a handle whose every drop must fail is worse\nthan an absent one.",
            name: "googleMapsApiKey",
            required: false,
            type: "string",
          },
          {
            description:
              "Bounds the droppable area to an itinerary, and draws that corridor for the length of a drag.",
            name: "bounds",
            required: false,
            type: "StreetViewBounds",
          },
          {
            description:
              "Overrides what a grab does. Unset, the map shows Street View coverage and the corridor.\n\nPaired with `onDragEnd`, which is called on EVERY path that ends a drag: a release, a\npointer cancel, a capture the browser took back, a zoom falling below the threshold with the\nhandle still held, and the control leaving the page mid-drag. An override that shows\nsomething has to be told to take it down again by every one of them.\n\nThis is also the pair that makes the handle live with **no map in context**, where every\nother control here is made live by whichever single handler replaces its default. The\nasymmetry is the drop: unprojecting a screen point into a coordinate is the map's own\nmeasurement, so a mapless handle can be dragged and can tell an application about the\ndrag, but the drop itself resolves to nothing. Presentational Street View is therefore\nthe drag overrides, and `onDrop`/`onOpen` never fire without a canvas to aim at.",
            name: "onDragStart",
            required: false,
            seam: "handler",
            type: "() => void",
          },
          {
            description:
              "Overrides what the end of a drag does, on the terms {@link onDragStart} states.",
            name: "onDragEnd",
            required: false,
            seam: "handler",
            type: "() => void",
          },
          {
            description:
              "Called when a drop is refused, for an application raising its own message. Only the latest drop is answered: a check a later drop replaced reports nothing, and neither does one that answers after the control was disabled or unmounted.",
            name: "onUnavailable",
            required: false,
            seam: "handler",
            type: "(reason: StreetViewMiss) => void",
          },
          {
            description:
              "Called when a drop's coverage check failed rather than found no imagery: the optional peer `@googlemaps/js-api-loader` is not installed, Google's own library failed to load, or Google failed the request (a key it does not accept, a quota spent, the network). The drop is refused as `unavailable` all the same, since nothing can be opened.\n\nA reader cannot tell this from a place with no imagery, so it is the developer's signal.\nWithout it, a missing peer is written once to the console of a development build, and a\nfailed load or a failed request is reported nowhere.",
            name: "onError",
            required: false,
            seam: "handler",
            type: "(error: Error) => void",
          },
          {
            description:
              "Replaces the glyph in the handle and in the ghost.\n\nFor an application with artwork of its own, such as a pegman image its users already\nrecognise.",
            name: "children",
            required: false,
            type: "ReactNode",
          },
          {
            description: "",
            name: "onDrop",
            required: false,
            seam: "handler",
            type: "(lngLat: [number, number]) => boolean",
          },
          { description: "", name: "onOpen", required: false, seam: "handler", type: "undefined" },
        ],
        propsTypeName: "StreetViewControlProps",
        summary: "StreetViewControl is the handle a user drags onto the map to look around.",
      },
      {
        name: "Zoom",
        props: [
          { description: "", name: "className", required: false, seam: "style", type: "string" },
          {
            control: { kind: "boolean" },
            defaultValue: "false",
            description: "",
            name: "disabled",
            required: false,
            type: "boolean",
          },
          {
            description:
              "Overrides what a press does.\n\nLeft unset, the control drives the map directly, which is what a consumer wants. Set,\nthe control becomes presentational and the handler decides, which is how Mountaya's\nown studio routes every action through its tool bus while sharing this exact component.\n\nSupplying one also makes that half of the pair live with no map in context, because a\nhandler that suppresses the default has nothing left to wait for. The two halves are\ngated separately: a stack that overrides only zoom-in must not carry a live zoom-out\nwhose default it never wired.",
            name: "onZoomIn",
            required: false,
            seam: "handler",
            type: "() => void",
          },
          {
            description: "",
            name: "onZoomOut",
            required: false,
            seam: "handler",
            type: "() => void",
          },
          {
            description:
              "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
            name: "strings",
            required: false,
            type: "ZoomControlStrings",
          },
          { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
        ],
        propsTypeName: "ZoomControlProps",
        summary: "ZoomControl is the +/- pair, as one flush chip.",
      },
    ],
    props: [
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Which controls to render. Each defaults to off, so a stack is opt-in per control.",
        name: "zoom",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "compass",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "geolocate",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "layers",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "preferences",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "pois",
        required: false,
        type: "boolean",
      },
      {
        control: {
          kind: "enum",
          options: ["top-right", "top-left", "bottom-left", "bottom-right"],
        },
        defaultValue: '"top-right"',
        description: "",
        name: "position",
        required: false,
        type: '"top-right" | "top-left" | "bottom-left" | "bottom-right"',
      },
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "MapControlsStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Renders the whole stack inert.\n\nRead once here and threaded to every control, which is what keeps a control added to\nthis stack from shipping live: it takes the same flag its siblings do. Mountaya's own\nstudio passes its agent-run lock through this, so the entire chrome goes inert together\nwhile an agent drives.",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      { description: "", name: "layersPanel", required: false, type: "LayersPanelProps" },
      { description: "", name: "poiPanel", required: false, type: "PoiPanelProps" },
      {
        description:
          "The preferences panel's own props, on the same terms its two siblings take theirs.\n\nHoisting them here as a `preferencesValue` and an `onPreferencesChange` is what would put a\n`UnitFormatOptions` under the same word as the `preferences` BOOLEAN above, the flag\ndeciding whether the control renders at all. Two different things wearing one name, which\nno amount of documentation fixes. One panel, one prop, and the controlled pair\nstays inside the type that owns it.\n\nOmitted, the stack holds the preference set itself, so every row of the panel responds to\nits press: it starts from `defaultUnits` in the language the provider selected, and a\nlanguage row still drives the map. The set reaches nothing outside the panel, so an\napplication whose profile, table or own surfaces read units passes the pair and holds the\nvalue.",
        name: "preferencesPanel",
        required: false,
        type: "PreferencesPanelProps",
      },
      {
        description:
          "The open panel, when the application owns that state: one of the built-in panels, the `kind` of a `PanelControl` composed in `children`, or `null` for none.\n\nUnset, the stack owns it, which is what a consumer rendering the built-in panels\nwants. Set, the stack only reads it: `onTogglePanel` is how the built-in toggles ask\nfor a change, and `onClosePanel` is how a press elsewhere in the stack does. An\napplication composing its own `PanelControl`s in `children` supplies this alone, so the\nstack still knows a panel is open and can dismiss it.\n\nA press inside the open panel, or on its toggle, is never a dismissal. Neither is a press on\nanother panel's toggle when that toggle writes this state: every toggle of a controlled\nstack, and the built-in ones otherwise.",
        name: "openPanel",
        required: false,
        type: "PanelName | (string & {}) | null",
      },
      {
        description:
          "Called with the built-in panel whose toggle was pressed, in a controlled stack. A composed `PanelControl` reports through its own `onToggle`.",
        name: "onTogglePanel",
        required: false,
        seam: "handler",
        type: "(panel: PanelName) => void",
      },
      {
        description:
          "Called when a press elsewhere in a controlled stack asks for the open panel to close.",
        name: "onClosePanel",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      {
        description:
          "Controls of the application's own, rendered between the panel cluster and the geolocate/compass pair.\n\nComposition is the full-strength path, not a bolt-on: an application that needs its own\nhandler per control, its own gating, or panels it positions itself leaves every flag off\nand builds the stack here out of `ZoomControl`, `CompassControl`, `GeolocateControl` and\n`PanelControl`. It keeps the positioning, the token block and the dismiss-on-press,\nwhich is everything this component is for.",
        name: "children",
        required: false,
        type: "ReactNode",
      },
    ],
    propsTypeName: "MapControlsProps",
    summary:
      "The stack's body, which {@link MapControls} publishes with its controls attached as parts.",
  },
  MapView: {
    description:
      "It holds no itinerary, zone or collection knowledge, and the camera is not a prop: the map is\nan imperative object, and a camera mirrored into props ends in a loop, a user pan updating\nstate, which re-renders, which moves the camera, which fires another move event. So state\nmoves one way: `onReady` hands you the map once, and from there you drive it imperatively.\n`terrainVariant`, `terrain`, `language` and `constraints` are followed after mount, since the\nmap never moves them on its own; every other option is a seed, read once, and a map built\nwith another one is remounted with a `key`.",

    importPath: "@mountaya/elements-react",
    name: "MapView",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        description: "Called once the map is constructed and its style has loaded.",
        name: "onReady",
        required: false,
        seam: "handler",
        type: "(map: MountayaMap) => void",
      },
      {
        description:
          "Called just before a map that reached `onReady` is destroyed, to release anything attached to it.\n\nPaired with `onReady`: a map torn down before its style loaded was handed to nobody, so it\nis destroyed without this being called. React StrictMode builds and tears down such a map\non every development mount, and an application hears of the one it keeps.",
        name: "onDestroy",
        required: false,
        seam: "handler",
        type: "(map: MountayaMap) => void",
      },
      {
        description:
          "Called with each failure the map reports that no call of yours could reject: an error MapLibre raised, the base map not arriving because the layers manifest could not be read or lists no such `terrain`, and a switch of `terrain` or `terrainVariant` after mount that the map refuses.\n\nAttached as the map is built, before it makes its first request, so a failure that lands\nbefore `onReady` is heard. It reports and replaces nothing. A refusal of access goes to\n`onAccessRequired` instead, and a map that cannot be built at all (no WebGL context) throws\nto the nearest error boundary, which is React's own contract for a failure while mounting.",
        name: "onError",
        required: false,
        seam: "handler",
        type: "(error: Error) => void",
      },
      {
        description:
          "Called when the API refuses one of the map's own requests with a 401, a 402 or a 403: a tile, a sprite, a glyph, a style, or the layers manifest.\n\nThe refusal carries the status and the address refused, so an application tells a sign-in\napart from access its agreement does not include (a 402: talk to us at\nhttps://mountaya.com/en/contact/). Attached as the map is built, like `onError`, since the\nmanifest is read before the style loads and an expired session token is refused there first.",
        name: "onAccessRequired",
        required: false,
        seam: "handler",
        type: '(refusal: MountayaMapEvents["accessrequired"]) => void',
      },
      {
        description:
          "The controls, panels and elements to place on the map, rendered inside its container once the map is ready.\n\nThey render under a `MountayaElementsProvider` that states this map, and this map's `client`\nwhere no enclosing provider states one, so `<MapView client={client}><MapControls zoom\n/></MapView>` needs no state and no `onReady`. Everything else is the enclosing provider's.\nA control placed outside the map takes the map from `onReady` through a provider of its own.\n\nA map whose first style never loads renders none of them, so a way out of that failure (a\nretry, another `terrain`) sits outside the map and hears of it through `onError`.",
        name: "children",
        required: false,
        type: "ReactNode",
      },
      {
        description:
          "A class for the container element.\n\nA container given a class is sized by that class: the fill described under `style` is\nleft out, since an inline declaration outranks every rule a class carries. A string that\nnames no class, empty or blank, is read as none, and the fill stays.",
        name: "className",
        required: false,
        seam: "style",
        type: "string",
      },
      {
        description:
          "CSS for the container element.\n\n`style` is the DOM's word here, not MapLibre's: the map style goes in `mapStyle`. The two\nshare a name in their own worlds and cannot share one here, and React's meaning wins because\nthis is a React component.",
        name: "style",
        required: false,
        seam: "style",
        type: "ElementStyle",
      },
      {
        description:
          "The client this map fetches its specifications through, and the client the children read where no enclosing provider states one.\n\nA seed, read once as the map is built: the map reads the credential it holds at each request,\nso a session token that rotates needs nothing here, and a map over another client is remounted\nwith a `key`.",
        name: "client",
        required: true,
        type: 'MountayaMapOptions["client"]',
      },
      {
        description:
          "Overrides the credential MapLibre attaches to its own tile, sprite and glyph requests.\n\nIts session token is followed: the one this prop holds at each request goes out, and a render\nthat omits the prop keeps the last one. The key, the share token and the cookie flag are a\nseed, read once as the map is built, and a map built with others is remounted with a `key`.",
        name: "credential",
        required: false,
        type: 'MountayaMapOptions["credential"]',
      },
      {
        description:
          "Where the camera opens, as `[longitude, latitude]`.\n\nA seed, read once as the map is built, as the whole opening pose is: from there the camera is\nthe reader's, and the map `onReady` hands you moves it.",
        name: "center",
        required: false,
        type: 'MountayaMapOptions["center"]',
      },
      {
        control: { kind: "number" },
        description: "The zoom the camera opens at. A seed, read once as `center` is.",
        name: "zoom",
        required: false,
        type: 'MountayaMapOptions["zoom"]',
      },
      {
        control: { kind: "number" },
        description:
          "The compass bearing the camera opens at, in degrees clockwise from north. A seed, read once as `center` is.",
        name: "bearing",
        required: false,
        type: 'MountayaMapOptions["bearing"]',
      },
      {
        control: { kind: "number" },
        description:
          "The tilt the camera opens at, in degrees off vertical. A seed, read once as `center` is.",
        name: "pitch",
        required: false,
        type: 'MountayaMapOptions["pitch"]',
      },
      {
        description:
          "Where a device fix comes from: a native shell's, or `inertGeolocation` for a product that locates nobody.\n\nA seed, read once as the map is built: a map served by another seam is remounted with a\n`key`. A seam written inline is no change.",
        name: "geolocation",
        required: false,
        type: 'MountayaMapOptions["geolocation"]',
      },
      {
        description:
          "MapLibre's own options, handed to it as written.\n\nA seed, read once as the map is built: a map built with others is remounted with a `key`. An\nobject written inline is no change, a callback among its members included.",
        name: "maplibre",
        required: false,
        type: 'MountayaMapOptions["maplibre"]',
      },
      {
        description:
          "A complete map style to start on, instead of a terrain from the layers manifest.\n\nA seed, read once as the map is built: a map that starts on another style is remounted with a\n`key`, and `setStyle` on the map switches it afterwards.",
        name: "mapStyle",
        required: false,
        type: 'MountayaMapOptions["style"]',
      },
      {
        description:
          'Overrides for what the map draws: the itinerary stroke, its casing, the layer ranks.\n\nIt cannot be a `--mtya-*` token: MapLibre evaluates paint expressions inside its own renderer\nagainst no DOM element, so a `var(--mtya-accent)` arrives as a string it cannot read. The\ntokens theme the chrome around the canvas; this themes what is drawn in it.\n\nFolded per key, so `{ itinerary: { casingColor: "#f8f9fa" } }` changes the halo and leaves\nevery width stop, dash and stacking rank where they were. A seed, read once as the map is\nbuilt: a map painted otherwise is remounted with a `key`.',
        name: "paint",
        required: false,
        type: 'MountayaMapOptions["paint"]',
      },
      {
        description:
          "The glyph set the markers this map draws take their glyphs from.\n\nA seed, read once as the map is built.",
        name: "markerIcons",
        required: false,
        type: "IconProvider",
      },
      {
        control: { kind: "enum", options: ["dark", "light"] },
        description:
          "The rendition of the catalogue terrain the base map draws, as the layers manifest lists it.\n\nThe terrain's own light or dark variant, which is the application's choice and is never read\noff the page's colour scheme or `data-mtya-color-scheme`: a dark interface over a light base\nmap is a legitimate product. Followed after mount: a new value switches the rendition in\nplace, and a render that omits it changes nothing.",
        name: "terrainVariant",
        required: false,
        type: 'MountayaMapOptions["terrainVariant"]',
      },
      {
        control: { kind: "string" },
        description:
          "The slug of the catalogue terrain the base map draws.\n\nFollowed after mount: a new slug switches the base map in place, and a render that omits it\nchanges nothing. A slug the manifest does not list reaches `onError`.",
        name: "terrain",
        required: false,
        type: 'MountayaMapOptions["terrain"]',
      },
      {
        control: { kind: "enum", options: ["de", "en", "es", "fr", "it", "ja", "zh"] },
        description:
          "The language labels are drawn in.\n\nFollowed after mount, and a render that omits it changes nothing.",
        name: "language",
        required: false,
        type: 'MountayaMapOptions["language"]',
      },
      {
        description:
          "The limits the camera may not leave: a zoom floor and ceiling, and a boundary.\n\nFollowed after mount, compared by its members so an object written inline changes nothing, and\na render that omits it changes nothing. A boundary or a zoom window the map cannot hold throws\n`MountayaElementsError` to the nearest error boundary, at mount as after it.",
        name: "constraints",
        required: false,
        type: 'MountayaMapOptions["constraints"]',
      },
      {
        description:
          "Replaces the transform the map derives from its client and its `credential`, for every request MapLibre makes.\n\nWhether one is supplied at mount decides, for the map's life, whether it replaces the\nderived transform: a transform first supplied on a later render is ignored, and a map that\ntakes one from then on is remounted with a `key`. The prop is then read at each request, so\na transform rebuilt when a credential rotates reaches the next tile, and a render that omits\nit keeps the last one supplied.",
        name: "transformRequest",
        required: false,
        type: 'MountayaMapOptions["transformRequest"]',
      },
    ],
    propsTypeName: "MapViewProps",
    summary:
      "MapView owns one map's lifecycle: it builds a `MountayaMap` into its container once, follows the few options a mounted map can change, and destroys the map as it unmounts.",
  },
  ModalFooter: {
    description:
      "**A dialog with no action renders no footer.** Not a footer holding a lone \"Close\": the\ncorner X is the dismissal, on every dialog, and spelling it a second time along the\nbottom edge makes the reader choose between two controls that do the same thing. The\ndialogs this applies to are the read-only ones (an activity's routing guide, a waypoint\nseen from a published itinerary), and they end on their content.\n\nThe `secondary` slot and the Cancel button are INDEPENDENT. A dialog with a\nside action and unsaved edits needs both (the side action does something, the\nCancel abandons what was typed), and a footer that let one hide the other would leave\nthe segment-group editor with no labelled way out of a rename.\n\nOmitting `onConfirm` makes the confirm a submit button so a form-backed dialog\ndrives it through its `<form onSubmit>`; passing `onConfirm` makes it a plain\nbutton for the mutation-owns-the-state dialogs. The confirm is `filled` (Mantine's\ndefault variant) with its colour per intent: the theme's primary colour by default, the\n`danger` tone for a destructive confirm. The Cancel is `transparent`: it carries no fill\nand no border, so the row has exactly one button shape competing for the eye and the\nprimary is unmistakable. It disables while `pending` so an in-flight action cannot be\ndismissed out from under itself. When `confirmDisabledReason` is set the confirm is\ndisabled and wrapped in a tooltip carrying the reason, pointed down (`position=\"bottom\"`):\nthe footer sits at the dialog's bottom edge, so the reason reads below the button rather\nthan crowding the dialog body above it. The same sentence is said in a polite live region\nthat stands, empty, for as long as the row has a confirm, so a confirm refused while the\ndialog is open is announced. All copy is supplied by the caller so the footer carries no\nlocalized strings of its own.",

    importPath: "@mountaya/studio-react",
    name: "ModalFooter",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description:
          "The abandon button's label; pairs with `onCancel` to render a Cancel.\n\nRequired in practice ALONGSIDE `confirmLabel` and meaningless without it: a Cancel is\nwhat the confirm is abandoned in favor of, so a row offering only a way out is a\ndismissal wearing a button. The pairing is the call site's to keep rather than a type's,\nwhich a `never` union could only make harder to read.",
        name: "cancelLabel",
        required: false,
        type: "string",
      },
      {
        control: { kind: "enum", options: ["caution", "danger", "primary"] },
        description:
          "The confirm's tone: `danger` for a destructive confirm, `primary` for an affirmative one.",
        name: "confirmColor",
        required: false,
        type: "StudioTone",
      },
      {
        control: { kind: "boolean" },
        description:
          "Disables the confirm, for a confirm gated on a precondition the dialog shows itself.",
        name: "confirmDisabled",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description:
          "Disables the confirm and says why: in a tooltip under it, and in a polite live region for assistive technology. The host's own words.",
        name: "confirmDisabledReason",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description:
          'The primary action\'s label, and the reason the row exists.\n\nOPTIONAL, for the one shape that has no primary: a dialog whose only offer is the\nside action in `secondary` (copy a revealed key, disable a share link). A "Done" beside\nit is the corner X spelled as a button: two dismissals, one of them dressed as the primary\naction. Without it the real action stands alone in the row and the X is the single way\nout.\n\nNever a dismissal\'s word. A footer reading "Close", "Done" or "OK" is a dialog with\nnothing to do, and that dialog takes NO footer at all: the X already closes it.',
        name: "confirmLabel",
        required: false,
        type: "string",
      },
      {
        description: "Extra top margin on the action row, opening space between it and the body.",
        name: "mt",
        required: false,
        type: "StudioSpace",
      },
      {
        description: "Abandon handler the Cancel button calls; pairs with `cancelLabel`.",
        name: "onCancel",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      {
        description: "Called by the confirm.",
        name: "onConfirm",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the confirmed action is out: the confirm spins and Cancel is disabled, so the action is neither fired twice nor dismissed from under itself.",
        name: "pending",
        required: false,
        type: "boolean",
      },
      {
        description:
          "A non-cancel secondary action pinned to the row's far left, opposite the confirm.",
        name: "secondary",
        required: false,
        type: "ReactNode",
      },
    ],
    propsTypeName: "ModalFooterProps",
    summary:
      "ModalFooter is the shared action row a dialog ends with WHEN IT HAS SOMETHING TO DO: the abandon/confirm cluster on the right and, when an explicit `secondary` action is supplied, that action pinned to the far left opposite it (`space-between`), so a destructive or utility action (Reset/Disable a link, Copy a key, Dissolve a group) reads as distinct from the primary. The footer standardizes both the geometry and the confirm-spins-while-pending affordance, so every dialog settles the same way.",
  },
  MountayaElementsProvider: {
    description:
      "**It INHERITS.** A field this provider does not state is the enclosing provider's, and\noutside any provider it is the bundled default. That is what makes nesting one usable for\nwhat nesting is actually for here: the map is built by the surface that draws it, so a\nstudio publishes it by mounting a second provider UNDER the host's, and the host's glyph\nsets, language and copy have to survive that. A provider that replaced instead would\ntake a licensee's Nucleo sets away and put Lucide back, silently: nothing throws, every\nglyph draws, and the marks are simply someone else's.\n\nThe rule is one sentence in both directions (**state a field and you own it, omit it and\nyou inherit it**), so a subtree that genuinely wants no map passes `map={null}` and a\nsubtree that wants English inside a French tree passes `language=\"en\"`.\n\n`strings` follows the same rule with one extra condition, because a bundle is selected by the\nlanguage as well as by the overrides: it is inherited only while this provider leaves the\nlanguage alone. Stating a new language re-resolves it from that language's base, which is the\nhonest answer: an override written for French is French.",

    importPath: "@mountaya/elements-react",
    name: "MountayaElementsProvider",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        description:
          "The map the controls drive, when there is one.\n\nNull until it has been constructed and loaded. Omitted, it is the enclosing provider's\nmap, which is what lets a surface that builds one publish it by nesting a provider that\nstates nothing else. A subtree that genuinely holds no map under a provider that does\nsays so with `map={null}`.",
        name: "map",
        required: false,
        type: "MountayaMap | null",
      },
      {
        description:
          "The client an element resolves an id through, when it is handed one instead of geometry.\n\n**Nothing in this package fetches.** An element hands this to `fetchItinerary`, which calls\na method on it: the arrangement `MountayaMap` already has with its own client, and the\nreason there is one credential, one retry policy and one error taxonomy across the family\nrather than two that drift.\n\nTyped as the narrow `ItinerarySource` rather than as the whole client: a prop that accepted\neverything would let an element reach anything, and the next element would. A `Mountaya`\ninstance satisfies it structurally, so a caller passes theirs straight in.\n\nOptional, and separate from `map` on purpose. `<ItineraryProfile>` given `segments` needs\nneither; given an `itineraryId` it needs this and still no canvas, which is what keeps the\nelement renderable in a dashboard card, a report or a print view.",
        name: "client",
        required: false,
        type: "ItinerarySource | null",
      },
      {
        description:
          "Glyphs for the map controls: zoom, compass, geolocate, the panel toggles.\n\nDefaults to the enclosing provider's table, and to the bundled Lucide set outside one. An\napplication holding a Nucleo licence passes its own table here, keyed by the same roles,\nand gets pixel parity with Mountaya's studio.\n\nCompared member by member, each function by its identity: a copy written in the JSX\n(`{ ...licensedIcons }`) holds the same functions and re-renders nothing below the provider.\nTwo functions that draw the same shape are not comparable short of calling them, so a\nprovider whose functions are built at each render re-renders every element under this one\nand re-parses every glyph they draw: build yours at module scope.",
        name: "controlIcons",
        required: false,
        type: "IconProvider",
      },
      {
        description:
          "Glyphs for `@mountaya/studio-react`: the studio surfaces, as distinct from the map controls above. Defaults to `controlIcons` when this provider states one, and otherwise to the enclosing provider's.\n\nIt is a second seam and not a second table on the first because **a set has one paint\nmode**, and these two surfaces do not share one. A control sits on a map: it is a solid\nshape on a photographic background, drawn from a filled family so it reads at any zoom\nover any terrain. A menu item sits on a panel beside text, and is drawn from an outline\nfamily so it weighs the same as the words next to it. Mountaya's own studio draws them\nthat way, and a design system that has one icon set for chrome and another for menus is the\nnormal case, not the exotic one.\n\nPainting either family the other way is the failure the `paint` field exists to prevent,\nand it does not throw: a filled glyph stroked draws a hairline outline of its silhouette,\nan outline glyph filled draws a blob. Both survive review as \"the icons look off\".\n\nDefaulting to `controlIcons` is therefore a convenience for a consumer with ONE family, not a\nsuggestion: passing a filled set here draws Mountaya's menus in blobs.\n\nA set answers every role of `UI_GLYPHS` on `@mountaya/elements/icons`, which names each glyph\nthe studio draws from this seam, its menu actions among them. A role the set leaves out draws\nnothing, with no error.",
        name: "uiIcons",
        required: false,
        type: "IconProvider",
      },
      {
        description:
          "Glyphs for the PLACES an element draws: the waypoint marks on an elevation curve.\n\nThe third seam, and the one that is not about chrome at all. `controlIcons` and `uiIcons` are\ntwo families of ROLES (zoom in, merge, rename), where this is a family of THINGS: a summit, a\nrefuge, a water point, a car park. An itinerary states one per waypoint out of a closed\nvocabulary, so the set answering them is chosen by what your data says rather than by what\nyour product looks like, and `WAYPOINT_GLYPHS` on `@mountaya/elements/icons` is that list.\n\nIt falls back to the enclosing provider's MARKER set rather than to `controlIcons`, which\nthe two above do not, and the asymmetry is the whole reason it exists: a control set answers\n`zoom-in` and knows nothing about a refuge, so defaulting to it would draw every place on\nevery curve as the same fallback pin, in the one place a reader is looking for the\ndifference.\n\nSame paint rule as the pair above: a set has one paint mode, and this one is drawn at\nfourteen pixels inside a coloured disc, so an outline family is the safe default and a\nfilled family is a legitimate choice for a licensee who has one.",
        name: "markerIcons",
        required: false,
        type: "IconProvider",
      },
      {
        control: { kind: "enum", options: ["de", "en", "es", "fr", "it", "ja", "zh"] },
        description:
          "The language the elements read in. Defaults to the enclosing provider's, and to English outside one.\n\nEnglish is built in, and {@link translations} holds every other language the page imported.\nThe rest of `MAP_LANGUAGES` is accepted and falls back to English, so a consumer can select\nthe same language here as they do for the map's labels and fill the gap through `strings`\nrather than reshaping their tree around which languages happen to be translated. A language\nMountaya translates and the page did not import reads in English and says so once in\ndevelopment.",
        name: "language",
        required: false,
        type: "MapLanguage",
      },
      {
        description:
          "The elements' words in each language beyond English that the page imported, keyed by language: `{ fr: frElementsStrings }` from `@mountaya/elements/i18n/fr`. Defaults to the enclosing provider's, and to English alone outside one.\n\nCompared by its members, so a set written inline in the JSX resolves once.",
        name: "translations",
        required: false,
        type: "Translations<ElementsStrings>",
      },
      {
        description:
          "Copy overrides, merged onto the selected language. Anything omitted keeps its default.\n\nOmitting this prop entirely inherits the enclosing provider's resolved bundle, so a nested\nprovider adding a map keeps a host's wording. Stating it resolves from the language's base\nrather than layering onto the outer's: a provider that states its copy is stating it.\n\nEither shape: a flat `PartialElementsStrings` overrides regardless of language, and a\nper-language record (`{ fr: { controls: … } }`) overrides one language and leaves the rest\nalone, which is how a consumer ships a language the elements do not carry. One shape or\nthe other, never both: one key decides the shape, so the sections of an object that named\na language beside them would be folded in no language.\n\nCompared by CONTENT, so the documented inline form (`strings={{ controls: … }}` written\nstraight into the JSX) costs one shallow walk per render rather than a full re-render of\nthe map chrome. A formatter has no content to compare and is compared by identity: one\nwritten inline is a new function at each render, and its readers render again with it, so\na formatter that reads its host's state words the state as it stands. Its readers are the\nelements whose words hold a formatter, `<WaypointCard>` among them, and a caller of\n`useMountayaStrings`. An element that words no formatter is handed one bundle across those\nrenders and renders for none of them. A formatter declared at module scope is one function\nand moves nothing.",
        name: "strings",
        required: false,
        type: "ElementsStringsOverrides",
      },
      {
        control: { kind: "string" },
        description:
          "The locale a number, a date and a relative moment are WRITTEN in. Defaults to `language`.\n\nA language decides which words a surface says; a locale decides the thousands separator, the\ncalendar, whether a date reads day-first and where the percent sign goes. A Swiss product\nreads French words with `de-CH` grouping. See `MountayaI18n` of `@mountaya/elements/i18n`,\nwhich is the same three fields as one object: the shape `@mountaya/studio-react`'s surfaces\ntake, and the one a port declares once rather than three times.",
        name: "locale",
        required: false,
        type: "string",
      },
      {
        description:
          'Overrides for the `--mtya-*` custom properties every element under this provider is drawn with. Merged onto the defaults, so an override naming one token leaves the rest alone.\n\nState them ONCE here rather than per element, which is what this prop is for: an element\nroot ASSIGNS its own token block rather than inheriting one (deliberately, so a published\npackage never writes to `:root`), so a declaration on an ancestor is shadowed and does\nnothing. The provider hands these down and each root merges them into its own `style`.\n\nA per-element `style` still wins over these, which is the order a consumer expects: the\nspecific setting beats the general one.\n\nCompared by CONTENT, as `strings` is, so the inline form `tokens={{ "--mtya-accent": … }}`\nre-renders nothing below the provider while it states the same values.\n\n`elementsTokens` is the list, with each default. There is deliberately no `colorScheme`\nprop beside this: the scheme is `data-mtya-color-scheme` on an ancestor the HOST owns, and\n`boundary.test.ts` bans this package from ever writing that attribute, because two sources\nfor one fact is a bug at the render where they disagree. Set it in your own stylesheet; the\nREADME shows the line.',
        name: "tokens",
        required: false,
        type: "ElementTokens",
      },
      {
        description:
          "Overrides for what the MAP draws: the itinerary stroke, its casing, the layer ranks.\n\nThe counterpart to {@link tokens}, and it has to be a separate seam rather than more custom\nproperties: MapLibre evaluates paint expressions inside its own renderer against no DOM\nelement, so a `var(--mtya-accent)` arrives as a string it cannot read and the layer draws in\nMapLibre's fallback. The tokens theme the chrome AROUND the canvas; this themes what is\ndrawn IN it.\n\nFolded per key through the same merge the strings use, so `{ itinerary: { casingColor } }`\nchanges the halo and leaves every width stop, dash and stacking rank where they were.\n\nCompared by CONTENT, as `tokens` is, so an override written inline re-renders nothing below\nthe provider while it states the same values.",
        name: "paint",
        required: false,
        type: "MapPaintOverrides",
      },
      { description: "", name: "children", required: true, type: "ReactNode" },
    ],
    propsTypeName: "MountayaElementsProviderProps",
    summary:
      "MountayaElementsProvider supplies the seams every element reads: the map they drive, the client they resolve an id through, the glyph sets, a language, a locale, the copy, the design tokens and the paint overrides.",
  },
  MountayaStudio: {
    description:
      "It reads the bus, the tool context, the store and the map lifecycle from\n`MountayaStudioHost` above it, so a sidenav rendered outside this component drives the same\nmap and the same store: the agent and a clicking reader share one mutation surface, and\nthere is no second one to keep in step. Once the map client is live and an itinerary is\nactive it frames the whole itinerary through the same tool the agent would use, keyed on the\nitinerary id so opening a different one reframes.\n\nWhat it draws on the map is every itinerary and zone the viewer can reach, as vector-tile\noverlays authorized by the tile transform, inside whatever camera limits `constraints`\nstates. Over the map sit the control stack, the floating panels, the search row, the\nco-pilot, the docked elevation profile and the alert strip, every one of them gated by the\nresolved `config` and driving the one bus.\n\nMount it under a `MountayaStudioHost`; it takes no required prop. The map client and the tile\ntransform are derived from the host's client, and everything else on the props is a surface of\nthe host's own that it wants placed in the layout, or a seam whose absence is a defined answer.",

    importPath: "@mountaya/studio-react",
    name: "MountayaStudio",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description:
          "A class of your own on the editor's root element.\n\nMerged with nothing (this root carries no class of the package's own), so it simply lands.\nWhat it is FOR is the case the host's `className` cannot reach: the editor is one child of the\nhost, and a layout that sizes the editor differently from the chrome beside it has to name the\neditor.\n\nA styling hatch, which a surface takes when a host places it itself, as\n`apiShape.test.tsx` records.",
        name: "className",
        required: false,
        seam: "style",
        type: "string",
      },
      {
        description:
          'Inline style on the editor\'s root element, merged OVER the layout this surface needs.\n\n`ElementStyle` rather than React\'s `CSSProperties`, so `style={{ "--mtya-accent": "#7048e8" }}`\ntype-checks: that spelling is the documented theming seam and `CSSProperties` refuses it.\n\nThe root\'s own `height: 100dvh` and `position: relative` are written first and a value here\nwins, which is deliberate: an editor embedded in a panel rather than filling a window has to\nbe able to say `style={{ height: "100%" }}`, which the root\'s own values would otherwise\nrefuse from outside.',
        name: "style",
        required: false,
        seam: "style",
        type: "ElementStyle",
      },
      {
        description:
          "The client the map draws through.\n\nOmitted, it is the HOST's client, which is the right answer wherever one credential serves\nboth the reads and the tiles. State it where the map's does not match (a signed-in studio\nthat rides a session cookie for tiles and a key for everything else), and the two halves\nstay independent.\n\nWhichever client ends up here is also what the tile transform is DERIVED from: its\nbrowser-safe credential, its tile-API origin, its language. That is why the two are separate\nprops rather than one seam and why neither is required: see `transformRequest` below.\n\nIts credential is held to the host's rule: under a host rendering the Embedded Studio (the\ndefault), a publishable key given no session token, and a session token minted for another\nsurface than `studio-embedded`, are refused with a configuration error before the map draws a\ntile; a share token and the browser's own session are taken, and under a host declaring\n`data.surface: \"embedding\"` (Mountaya's hosted embed) a key with any session, or none, is\naccepted. While its session's first token is on its way, the map is not built, so none of its\nrequests goes out without the token; the host waits the same way for its own client only.",
        name: "mapClient",
        required: false,
        type: 'MountayaMapOptions["client"]',
      },
      {
        description:
          "Overrides the transform that stamps a credential onto each tile request.\n\nOmitted, the map derives one from whichever client is drawing it: its browser-safe\ncredential, its tile-API origin as the ONLY host allowed to receive that credential, and\nits language. That derivation is narrower than anything a caller would write by hand:\nit follows a client repointed at a local stack or a proxy, where a hand-written origin\nwould keep naming the public host and silently authenticate nothing.\n\nSupply one for what the credential projection cannot know: a share token scoped to a\nsingle path prefix, a `surface=published` opt-in, an origin the client does not speak to.\n`createTransformRequest` from `@mountaya/elements` builds it, and takes `scoped` and\n`next` for exactly those cases.\n\nA secret-key client projects no credential at all, so the derived default authenticates\nas nobody rather than putting a server-side key on a map.",
        name: "transformRequest",
        required: false,
        type: "TileRequestTransform",
      },
      {
        control: { kind: "string" },
        description:
          "The terrain tiles the base map bakes its relief from, as a TileJSON or XYZ template.\n\nA URL rather than a provider, because every DEM source this speaks to is one of those two,\nand which one serves elevation (under whose key) is the host's answer.\n\nThe answers, most explicit first: this prop, then the host's `assets.demUrl`, then the\nlayers manifest's own `elevation`. Set none of them and the map renders flat rather than\nfiring a request that cannot succeed.",
        name: "demTilesUrl",
        required: false,
        type: "string",
      },
      {
        description:
          "Which controls the studio offers, and which terrains and overlays it may show.\n\nIt is a prop rather than something the studio resolves because both halves of the resolution\nare the host's: whose stored defaults apply, and which terrains and overlays their viewers are\noffered. Spread the default and narrow what you mean to narrow.",
        name: "config",
        required: false,
        type: "StudioConfig",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the map's WebGL context preserves its drawing buffer.\n\nKeep it off unless something reads the canvas out of band (a session recorder, a screenshot\nservice): preserving the buffer costs a copy per frame on every pan.",
        name: "preserveDrawingBuffer",
        required: false,
        type: "boolean",
      },
      {
        description:
          "The camera's limits: a zoom floor and ceiling, and a boundary it may not leave.\n\nA RESOLVER rather than a value, so the host keeps which organization is authoritative and\nhow a geofence is spelled on its wire. Omitted, the camera is unconstrained.",
        name: "constraints",
        required: false,
        type: "StudioConstraintResolver",
      },
      {
        description:
          "The slice of the host's URL the studio mirrors its map state into, and restores it from on mount.\n\nTwo answers, most explicit first: this prop, then the host's `behavior.url`, the seam the\nverbs write through. A host that states the seam on `MountayaStudioHost` states it once.\nWith neither, the studio still works and does not survive a reload: the terrain, the\noverlay, the enrichments and the time offset are held in the store alone.",
        name: "url",
        required: false,
        type: "StudioUrlSeam",
      },
      {
        description:
          "The live collaboration session for the open itinerary.\n\nSeparate from the zone's, because the two resources are edited through different tools and a\nreader can hold a lock on one while another holds the other. Omitted, the editor works\nexactly as it does for a single author.",
        name: "collabEngine",
        required: false,
        type: "CollabEngine | null",
      },
      {
        description:
          "The live collaboration session for the open zone.\n\nThe zone half of the pair above, and independent of it for the same reason. Omitted, zone\nediting works exactly as it does for a single author.",
        name: "zoneCollabEngine",
        required: false,
        type: "CollabEngine | null",
      },
      {
        control: { kind: "string" },
        description:
          "The Google Maps key the Street View surfaces probe coverage and open a panorama with.\n\nOmitted, the pegman and the context menu's Street View action are not offered, which is\nthe honest absence: a control that opens nothing is worse than one that is not there.",
        name: "googleMapsApiKey",
        required: false,
        type: "string",
      },
      {
        description: "The draggable handle a reader drops onto the map to open Street View.",
        name: "streetViewHandle",
        required: false,
        type: "ReactNode",
      },
      {
        description:
          "The elevation panel, drawn inside the studio's own docked sheet.\n\nA render prop rather than a node, because the sheet resolves how much room the panel has\nfrom the live geometry around it (an open floating panel collapses the profile to its\nstat bar), and that answer is the studio's to make and the panel's to obey.",
        name: "elevationPanel",
        required: false,
        type: "(collapsed: boolean) => ReactNode",
      },
      {
        description:
          "The host's own portalled surfaces, mounted inside the studio's element context so they read the same glyphs, copy and map.\n\nRendered unconditionally: whether a wizard's chunk is loaded is the host's decision, and\nevery one of these gates on a store slot the host can read through `useUiStore`.",
        name: "modals",
        required: false,
        type: "ReactNode",
      },
    ],
    propsTypeName: "MountayaStudioProps",
    summary:
      "MountayaStudio is the assembled editor: a map, everything drawn over it, and every surface the studio raises for itself.",
  },
  MountayaStudioHost: {
    description:
      "It owns the studio's shared state (the UI store, the tool bus with every published handler\nregistered on it, the tool context those handlers dispatch against, the resource-named cache\nthat makes a write invalidate what a read wrote, the confirm gate), and it mounts the providers\naround them: the query layer, the client, the elements, and the studio itself, with every seam\na host was given bound beneath. Mount it once, above everything that reads the studio,\nincluding chrome that sits OUTSIDE the map, which is the arrangement it exists for: a sidenav\nand a map are siblings, and the state they share belongs above both rather than passed between\nthem.\n\n**Each of the two outer providers follows one rule: state it and this host mounts it, omit it\nand this host inherits one.** A consumer who already has a `MountayaProvider` and a\n`QueryClientProvider` around their application passes neither and nothing is mounted twice; a\nconsumer with a credential and nothing else passes `client` and gets a working studio, query\nlayer included. The single exception is the client, which cannot be invented: with no `client`\nand no provider above, this throws at mount naming both fixes, because a studio with no\ncredential has nothing to read and would otherwise fail one request at a time.\n\nThe co-pilot runs only where `behavior.copilot` is stated: a host that states no co-pilot seam\nbuilds no agent session, opens no transport and offers no co-pilot tab.\n\n`ctx.map` and its sibling getters are late-bound over refs, so a verb reads the LIVE map at\ndispatch rather than the one that existed when the context was built, which is what lets the\nbus be created before any map has mounted, and why registering one never re-renders the tree.\n\nWhat a host states is read the same way wherever a handler alone reads it. The bus is built\nonce for the verbs it holds and each tool reads the host's current handler when it is\ndispatched; the seams, the confirm gate, the grants, the scope and the recognizers are read\noff the latest render. So a group written inline (`behavior={{ handlers: { … } }}`) builds\nnothing on a render that restates it. The tool context is a new object only when the client,\nthe cache, the persistence, the store, the url seam or the viewer is, since those are what a\nsurface renders from, or when the set of verbs the bus holds is, since the context's `bus`\ndispatches on that bus. An object restated with the members it held is the one it was.\n\nA studio runs on a session token minted for `studio-embedded`, the editor and a read-only\nviewer alike: your server mints it with `postSession`'s `surface` set to `\"studio-embedded\"`.\nIt is the only token a `studio-embedded` operation accepts, and what the studio draws and asks\nunder it, its map's tiles and its Data API requests among them, is recorded apart and never\ncounted toward the organization's limits, allowances or invoice. The host reads the surface off\nthe token its client carries and refuses another before the studio sends anything, and refuses\na publishable key given no session token the same way. While a session's first token is on its\nway it renders nothing beneath, so the map draws no tile under the key alone.\n\nThe hosts Mountaya runs itself are not held to it: one whose client carries a share token, as the\nshare page's does, one whose client carries the browser's own session and no key, as Mountaya's\nweb application's does, and one serving Map Publishing's hosted embed from Mountaya's own\norigin, which states `data.surface` as `\"embedding\"`. That product is read-only and recorded by\nits origin, so the host reads no session token for it, taking a key with a session minted for\nany surface or none, and grants `embedding` and `share` alone.\n\nThe embed wizard is offered only beneath a host whose client carries the browser's own session,\nas Mountaya's web application's does: an Embedded Studio, whose client carries a publishable key,\nholds no `embed` entitlement whatever its viewer answers, so it offers no Embed entry and\n`studio.embed.open` refuses. The wizard's live preview and its camera capture read the\npublisher's organization over that application's session, which an integrator's page does not\nhold.\n\nA studio that acts as a member through the Mountaya API needs that token minted for the\nmember: every write does, and every co-pilot run. Its client carries one beside the publishable\nkey, and the token names the user it was minted for. Under a token minted for no user the\nstudio is read-only: it holds neither `editor` nor `authenticated`, whatever\n`data.capabilities` grants, until a token naming a user replaces it.",

    importPath: "@mountaya/studio-react",
    name: "MountayaStudioHost",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description:
          "Where the pictures come from: the glyph tables, the relief raster, the activity and layer thumbnails and the routing guide's pictures.",
        name: "assets",
        required: false,
        type: "StudioAssetsConfig",
      },
      {
        description:
          "What it looks like: the scheme, the density, the tokens, the theme, the canvas paint.",
        name: "appearance",
        required: false,
        type: "StudioHostAppearanceConfig",
      },
      {
        description:
          "What it does, and what you do instead: handlers, seams, confirm, notify, the routers.",
        name: "behavior",
        required: false,
        type: "StudioHostBehaviorConfig",
      },
      {
        description:
          "What the studio mounts inside every provider it wires: a `MountayaStudio`, a `MountayaStudioViewer`, or surfaces you have arranged yourself.",
        name: "children",
        required: true,
        type: "ReactNode",
      },
      {
        description:
          "The client every read and write beneath this host runs on.\n\nTop-level rather than in `data`, because it is the one thing a studio cannot be mounted\nwithout and the first thing a consumer types. Omitted, the host reads the `MountayaProvider`\nabove it and throws if there is none: a missing credential is a configuration error, not a\nstudio that renders empty.",
        name: "client",
        required: false,
        type: "Mountaya",
      },
      {
        description:
          "What the studio is showing, and to whom: the catalog, the grants, the store, the viewer.",
        name: "data",
        required: false,
        type: "StudioHostDataConfig",
      },
      {
        description:
          "Which words, and which regional conventions: the language, the locale, both bundles.",
        name: "i18n",
        required: false,
        type: "StudioI18nConfig",
      },
      {
        control: { kind: "string" },
        description:
          "The itinerary the studio opens, `null` to clear it, omitted to leave activation to a surface below.\n\nTop-level beside `client` for the same reason: it is the other half of the shortest useful\ncall, and a studio with a credential and no itinerary is a studio waiting for one.",
        name: "itineraryId",
        required: false,
        type: "string | null",
      },
      {
        description:
          "What a third party ADDS to it: the plugins to install, where a failure goes, the engine.",
        name: "plugins",
        required: false,
        type: "StudioPluginsConfig",
      },
    ],
    propsTypeName: "MountayaStudioHostProps",
    summary:
      "MountayaStudioHost is the one component a consumer mounts, and it mounts everything else.",
  },
  MountayaStudioViewer: {
    description:
      "It mounts the DISPLAY hooks alone (the store-to-map sync, the language, the camera\nconstraints, the itinerary and collection layers, the segment colour, the overlays, the\nenrichment readouts and the timestamp) and renders the control stack and the floating\npanels gated by the config projected from `embedConfig`. It mounts NO editing hook: no\nsegment, waypoint or zone editing, no enrichment authoring, no suggestion picking, no URL\nstate, no agent. What a reader gets is the map, the itinerary, the controls the publisher\nenabled, and the read-only readouts.\n\nUnlike the editor it paints only the content it was CONFIGURED with (one itinerary through\nits own scoped specification, and a collection's contents when one is named), so a viewer\nnever draws the rest of an organization's work onto a public page.\n\nThe right-click readout is here and is deliberately not an exception: `studio.contextmenu.open`\nis an embed-capable display verb, and the popup's one authoring affordance is editor-gated\nand therefore hidden. A reader gets the elevation, POI, overlay and zone readouts with no\nway to mutate anything.\n\nEvery display hook reads the STORE, so the surface seeds the store from the config through\nthe bus (the configured terrain, the active overlay, the active itinerary, each through its\nown embed-capable verb and never a direct write). That is the same one-way flow the editor\nruns on, which is why a viewer and an editor can be the same map.",

    importPath: "@mountaya/studio-react",
    name: "MountayaStudioViewer",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description:
          "A class of your own on the viewer's root element.\n\nThe viewer is mounted OUTSIDE `MountayaStudioHost` (on a share page, inside an iframe), so\nthe host's own `className` is not available to it and this is the only way to reach the\nelement. Merged with nothing, because this root carries no class of the package's own.\n\nA styling hatch, which a surface takes when a host places it itself, as\n`apiShape.test.tsx` records.",
        name: "className",
        required: false,
        seam: "style",
        type: "string",
      },
      {
        description:
          'Inline style on the viewer\'s root element, merged OVER the layout this surface needs.\n\n`ElementStyle` rather than React\'s `CSSProperties`, so `style={{ "--mtya-accent": "#7048e8" }}`\ntype-checks: the documented theming spelling, which `CSSProperties` refuses.\n\nThe root\'s own full-height layout is written first and a value here wins, which is the case\nthis prop exists for: an embed in a fixed-aspect iframe is the COMMON viewer and `100dvh` is\nthe wrong height for it.',
        name: "style",
        required: false,
        seam: "style",
        type: "ElementStyle",
      },
      {
        description:
          "What the viewer is showing, fully resolved: the itinerary or collection, the terrain and overlay it opens on, which controls and profile sections it offers, and the reader's language. The host resolves it (from a publishable key, a URL, its own stored defaults, or all three), and this surface derives the control config and seeds the display state from it through the bus.",
        name: "embedConfig",
        required: true,
        type: "EmbedConfig",
      },
      {
        description:
          "The client the map draws through, which the tile transform is derived from unless `transformRequest` states one.\n\nBoth must authenticate as the SAME audience. The point of a viewer is that what a\npublisher previews is what a visitor gets, and a client reading as the owner behind a\ntransform reading as a visitor breaks exactly that, silently, because each half works.\n\nIts credential is held to the host's rule: under a host rendering the Embedded Studio (the\ndefault), a publishable key given no session token, and a session token minted for another\nsurface than `studio-embedded`, are refused with a configuration error before the map draws a\ntile; a share token and the browser's own session are taken, and under a host declaring\n`data.surface: \"embedding\"` (Mountaya's hosted embed) a key with any session, or none, is\naccepted. While its session's first token is on its way, the viewer shows its cover alone: no\nmap is built, no read is made and no ready event is posted until the token lands, so none of\nits requests goes out without the token, and the ready deadline counts from then.",
        name: "mapClient",
        required: true,
        type: 'MountayaMapOptions["client"]',
      },
      {
        description:
          "onReady fires each time the initial view settles, on a cold load and again on every content switch, carrying what an iframe host needs to size and identify what it is showing. A viewer mounted inside an iframe posts it to the parent page; one rendered on a page of its own omits it, because there is nobody to notify. One that throws stops nothing: the view has settled and the cover has lifted by then, and its fault is raised on a microtask, where the page's own error handling sees it.",
        name: "onReady",
        required: false,
        seam: "handler",
        type: '(payload: Omit<ReadyMessage, "type">) => void',
      },
      {
        description:
          "The security boundary a runtime override is re-clamped to.\n\nA viewer inside an iframe accepts configuration from the page around it, so it folds\nthose overrides over `embedConfig` and clamps the result to the SAME rules the host\napplied when it resolved the config. Omitted, no override is accepted, which is the\nright answer for a surface nobody outside can reconfigure.",
        name: "safetyRules",
        required: false,
        type: "EmbedSafetyRules",
      },
      {
        control: { kind: "boolean" },
        description:
          "Read the PUBLISHED snapshot rather than whatever the credential would otherwise resolve to.\n\nA visitor's credential already selects the publication, so a real viewer leaves this\noff. A PREVIEW does not: it authenticates as the owner in order to open at all, and\nwould otherwise read the editor's draft. It must move in lockstep with the tile\ntransform's option of the same name: one without the other paints draft geometry under\na published elevation profile, or the reverse.\n\nIt also selects the snapshot a collection's specification is read for, whose extent a\ncollection embed opens on. That read goes through the client rather than the transform, so\nthis is what keeps the extent on the itineraries the tiles draw. The docked profile is handed\nthe same snapshot as its `surface` option, so the reader's pace re-times the published route.",
        name: "publishedSurface",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Override which controls the surface offers.\n\nOmitted, they are derived from `embedConfig`: the publisher's own toggles, which is\nwhat a third-party embed should honour. A first-party surface passes its own resolved\nconfig instead, so a shared view offers what that host offers rather than what an\narbitrary publisher enabled.",
        name: "studioConfig",
        required: false,
        type: "StudioConfig",
      },
      {
        description:
          "Override the transform that stamps a credential onto each tile request.\n\nOmitted, the map derives one from the client drawing it: `MountayaStudioProps` documents\nthe derivation and when a surface has to state its own.",
        name: "transformRequest",
        required: false,
        type: "TileRequestTransform",
      },
      {
        description:
          "A mark drawn over the map, for a surface that carries attribution.\n\nA NODE rather than a flag, because whose mark it is and what it says are the host's, and\na package shipping a brand would be shipping the wrong one. It is not the tile credits:\nthose are a licence obligation the map draws itself, on every surface, always.",
        name: "watermark",
        required: false,
        type: "ReactNode",
      },
      {
        description:
          "The cold-load cover, told whether it should still be showing.\n\nA render prop because the studio owns the TIMING (the cover comes down when the\nconstraints hook reports the initial view framed, with a timeout backstop), and the host\nowns what is drawn until then. Without one the map's default centre is visible for the\nmoment before the framing lands.",
        name: "cover",
        required: false,
        type: "(visible: boolean) => ReactNode",
      },
      {
        description:
          "What to draw over the map while the API refuses its tiles (a 401, a 402 or a 403).\n\nA page served to visitors passes a neutral state that names nothing to do, as a share link\nand an embed do; a signed-in host may pass its own sign-in. Omitted, the map stays empty.",
        name: "tilesRequiredOverlay",
        required: false,
        type: "ReactNode",
      },
      {
        description:
          "The docked elevation profile, drawn inside the viewer's own sheet.\n\nA render prop rather than a node: which sections it shows comes from the config, and how\nmuch room it has is resolved from the live geometry around it, so both are the studio's\nto decide and the panel's to obey.",
        name: "elevationPanel",
        required: false,
        type: "(options: ViewerProfileOptions) => ReactNode",
      },
      {
        description: "The draggable handle a reader drops onto the map to open Street View.",
        name: "streetViewHandle",
        required: false,
        type: "ReactNode",
      },
      {
        control: { kind: "string" },
        description:
          "The Google Maps key the Street View surfaces probe coverage and open a panorama with.",
        name: "googleMapsApiKey",
        required: false,
        type: "string",
      },
      {
        description:
          "The host's own portalled surfaces, mounted inside the viewer's element context so they read the same glyphs, copy and map. Rendered unconditionally: whether a chunk is loaded is the host's decision, and each of these gates on a store slot it can read.",
        name: "modals",
        required: false,
        type: "ReactNode",
      },
      {
        control: { kind: "string" },
        description:
          "The terrain tiles the relief is baked from.\n\nThe answers, most explicit first: this prop, then the host's `assets.demUrl`, then the\nlayers manifest's own `elevation`. Set none of them and the map renders flat rather than\nfiring a request that cannot succeed.",
        name: "demTilesUrl",
        required: false,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the map's WebGL context preserves its drawing buffer: off unless something samples the canvas out of band, because it costs a copy per frame on every pan.",
        name: "preserveDrawingBuffer",
        required: false,
        type: "boolean",
      },
      {
        description:
          "The zoom floor and ceiling the viewer locks its camera to.\n\nREQUIRED, while both ends of it are optional, and the pair is deliberate: how far into a\npublished view a reader may zoom is the publisher's answer and never this package's, so a\nhost states it, and `{}` is how a host states that there is no clamp. An optional prop\nwould let a surface reach a reader without anyone having decided.",
        name: "zoomRange",
        required: true,
        type: "EmbedZoomRange",
      },
    ],
    propsTypeName: "MountayaStudioViewerProps",
    summary:
      "MountayaStudioViewer is the studio read-only: the same map, the same controls, the same docked profile, with every authoring surface withheld rather than disabled.",
  },
  OptionRow: {
    description:
      'A real `<input>` inside a `<label>`, not a `<button role="radio">`. The ARIA form is worse in\nevery way that matters: it needs roving tabindex and arrow keys implemented by hand to match\nwhat a radio group does natively, it needs `aria-checked` kept in step, and getting any of it\nslightly wrong produces a control that announces correctly and behaves wrongly. The native\nelement brings grouping, arrow-key navigation and the checked state with it.\n\nThe input is visually hidden rather than `display: none`: a hidden input is not\nfocusable, which would take the group off the keyboard entirely.\n\nA radio reports a repeat press as well as a change, and that is load-bearing rather than\ndefensive. Pressing an already-selected radio fires no `change` event, so a picker wired\nto `onChange` alone can never hear "the user pressed the thing that is already on", and\nfor a classic overlay, whose selection is exclusive, that press is the ONLY way to turn it\noff. Wired to change alone the overlay can be exchanged but never cleared, with nothing\nfailing.',

    importPath: "@mountaya/elements-react",
    name: "OptionRow",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        control: { kind: "enum", options: ["radio", "checkbox"] },
        description:
          '`radio` for a choice that replaces the last one, `checkbox` for one that stacks.\n\nThis is not decoration. It is the same distinction the layers manifest draws between a\nclassic overlay and an enrichment, surfaced to assistive technology: a screen-reader\nuser hearing "radio button" learns that selecting this will deselect the other, which\nis the rule the whole picker rests on.',
        name: "kind",
        required: true,
        type: '"radio" | "checkbox"',
      },
      {
        control: { kind: "string" },
        description: "Groups the radios that replace one another. Ignored for a checkbox.",
        name: "name",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "label",
        required: true,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "checked",
        required: true,
        type: "boolean",
      },
      { description: "", name: "onSelect", required: true, seam: "handler", type: "() => void" },
      {
        control: { kind: "boolean" },
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
      {
        description: "Rendered after the label: a badge, a lock.",
        name: "children",
        required: false,
        type: "ReactNode",
      },
    ],
    propsTypeName: "OptionRowProps",
    summary: "OptionRow is one selectable line in a panel.",
  },
  OrganizationScopeOptionRow: {
    description:
      "A menu-based switcher composes the same two pieces rather than this row, because a\nMenu.Item pins its glyph in a left section of its own; what they share is the glyph and\nthe words, not the box around them.\n\n`wrap=\"nowrap\"` keeps the glyph on the caption's line in a narrow dropdown (a sidenav's\nis the panel's width), where a wrapping Group would drop it above the text.",

    importPath: "@mountaya/studio-react",
    name: "OrganizationScopeOptionRow",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "name",
        required: true,
        type: "string",
      },
      {
        control: { kind: "enum", options: ["owner", "admin", "contributor", "viewer"] },
        description: "",
        name: "membershipRole",
        required: true,
        type: "OrganizationRole | null",
      },
    ],
    propsTypeName: "OrganizationScopeOptionRowProps",
    summary:
      "OrganizationScopeOptionRow is one row inside a scope picker's dropdown: the glyph for the scope's kind beside its two-line identity. It is what a Select offering a choice of scopes (the studio's owning-organization picker, or a host's collections scope switcher) hands to Mantine's `renderOption`, so no picker restates the row and two pickers cannot drift apart.",
  },
  PanelControl: {
    description:
      "The behaviours worth naming, because each is easy to lose:\n\nThe button is a **disclosure**: `aria-expanded` says whether its panel is shown and, while it\nis, `aria-controls` names the panel, a group labelled by `label`. It is painted held while the\npanel is open, the same calm grey as hover rather than a filled accent, since a saturated\nbutton beside an open panel competes with the panel's own content for attention.\n\n**Escape inside the panel closes it**, through `onToggle(false)`, and hands focus back to the\nbutton, so a keyboard reader leaves the panel where they entered it.\n\nIts **tooltip is suppressed** while the panel is open. Otherwise the bubble opens directly\nover the surface the press just revealed, which looks like a rendering fault.",

    importPath: "@mountaya/elements-react",
    name: "PanelControl",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "label",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "tooltipTitle",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "tooltipDescription",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "glyph",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description:
          "Names the panel in the DOM, on the element that wraps the toggle and the floating panel.\n\nThe stack's dismiss-on-press reads it there: a press inside the element of the open panel\nis never a dismissal, and `MapControlsProps.openPanel` names the open panel by this value.\nA panel the stack ships, or a word of the application's own for a panel it composes.",
        name: "kind",
        required: true,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "open",
        required: true,
        type: "boolean",
      },
      {
        description:
          "Called with the state the press is asking for, not with the state it left.\n\nThe control is controlled (`open` is the caller's), so a bare notification would leave\nevery caller re-deriving `!open` from the value it just rendered, and the two disagree\nfor exactly as long as a press and a re-render are apart.",
        name: "onToggle",
        required: true,
        seam: "handler",
        type: "(open: boolean) => void",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        description:
          "The control's own outermost element, for a caller that has to point at it.\n\nOne caller and one reason: a floating panel positioned somewhere ELSE on the page has to\nexempt its own toggle from its outside-click handler, or a press on the toggle closes the\npanel and the button's own handler immediately reopens it. Reading the button out of the\ndocument by its `kind` looks equivalent and is not: it finds the FIRST match on the page,\nso two maps on one page exempt each other's button, and it finds nothing at all across a\nshadow root.\n\nA panel rendered through `children` needs none of this: it is inside this element already.",
        name: "ref",
        required: false,
        type: "Ref<HTMLDivElement>",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
      {
        description:
          "The panel itself, rendered beside the button while open.\n\nOmit it when the application positions its own panel: in a drawer, a sidebar, or a\nlayer anchored somewhere other than the button. The control is then the toggle alone,\nand everything else about it holds: the held paint, the suppressed tooltip, and the\n`kind` the stack's dismiss-on-press exempts.\n\nOne of React's empty values (`null`, `false`, what a failed condition hands) reads as\nomitted, so a conditional panel draws no empty floating box.",
        name: "children",
        required: false,
        type: "ReactNode",
      },
    ],
    propsTypeName: "PanelControlProps",
    summary: "PanelControl is a button that owns a floating panel.",
  },
  PeerLockTooltip: {
    description: "",

    importPath: "@mountaya/studio-react",
    name: "PeerLockTooltip",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description:
          "The one inert control the tooltip attaches to: it takes a ref and spreads props.",
        name: "children",
        required: true,
        type: "ReactElement",
      },
      {
        control: { kind: "string" },
        description:
          "The user id of the peer holding the entity's lock, whose name the tooltip states.",
        name: "lockHolderId",
        required: true,
        type: "string",
      },
    ],
    propsTypeName: "PeerLockTooltipProps",
    summary:
      'PeerLockTooltip wraps an inert control in the "{holder} is editing this…" tooltip a peer-locked row surfaces, resolving the holder\'s display name through the host\'s peer-name seam (falling back to the name-less "A collaborator is editing this…" until it resolves). It mounts ONLY when a row is locked, so the name read it drives is deferred to the rare locked row rather than issued once per row, the same defer-to-contested pattern the peer-awareness badge uses. The tooltip doubles as the control\'s accessible description, so an assistive-tech user learns why the row is inert, and it reveals on focus as well as hover so a keyboard user reaches that explanation too.',
  },
  PersistedSegmentRow: {
    description:
      "The one thing it decides for itself is which verbs a row may carry at all. A segment still\nbeing drawn has an id the chain endpoints have never seen, so its chain bundle and its\ndownload are withheld rather than merely disabled: building them is building requests that\ncannot succeed.",

    importPath: "@mountaya/studio-react",
    name: "PersistedSegmentRow",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "groupActions", required: false, type: "SegmentRowGroupActions" },
      { description: "", name: "groupRail", required: false, type: "SegmentRowGroupRail" },
      {
        control: { kind: "enum", options: ["directions", "linestring"] },
        description:
          "The engine an insert beside the row opens in, for an activity that offers one engine alone. Null for an activity no engine serves, whose row offers no insert. Omitted, an insert opens in the segment's own engine.",
        name: "insertMode",
        required: false,
        type: 'SegmentRowChainOptions["insertMode"]',
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "isCommitted",
        required: true,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "itineraryId",
        required: true,
        type: "string",
      },
      {
        control: {
          kind: "enum",
          options: ["engine", "group", "waypointsMax", "locked", "head", "tail"],
        },
        description: "",
        name: "mergeNextDisabledReason",
        required: true,
        type: "SegmentMergeDisabledReason | null",
      },
      {
        control: {
          kind: "enum",
          options: ["engine", "group", "waypointsMax", "locked", "head", "tail"],
        },
        description: "",
        name: "mergePreviousDisabledReason",
        required: true,
        type: "SegmentMergeDisabledReason | null",
      },
      {
        description:
          "Downloads a segment, called with this row's id, which puts Download segment in a committed row's menu. It is the published row's own `onExport`, handed down unchanged: the list that holds the viewer passes it to a viewer holding `publish`, and a row given none offers no download.",
        name: "onExport",
        required: false,
        seam: "handler",
        type: 'SegmentRowProps["onExport"]',
      },
      {
        control: { kind: "string" },
        description: "",
        name: "previousSegmentId",
        required: true,
        type: "string | null",
      },
      { description: "", name: "segment", required: true, type: "ItinerarySegment" },
      {
        control: { kind: "enum", options: ["editing"] },
        description: "",
        name: "splitDisabledReason",
        required: true,
        type: "SegmentSplitDisabledReason | null",
      },
      {
        control: { kind: "number" },
        description: "",
        name: "activityDefaultSpeed",
        required: true,
        type: "number | null",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "activitySlug",
        required: true,
        type: "string | null",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "isFirst",
        required: true,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "isLast",
        required: true,
        type: "boolean",
      },
    ],
    propsTypeName: "PersistedSegmentRowProps",
    summary:
      "PersistedSegmentRow is the stored chain's row: the published editor row, with this tree's units, copy, collaboration badges and peer-lock name resolved around it.",
  },
  PoiPanel: {
    description:
      "The unit is the manifest's **key**, not the layer. A key is the wire's stable slug for a map\nconcept, and its own field description says clients group and toggle by it, because a single\nconcept is regularly drawn by more than one layer, a fill plus the symbol that labels it.\nOffering layers instead would let a user hide the fill and leave the labels floating over\nnothing, which reads as a rendering fault rather than a half-finished toggle.\n\nRows come from what is actually drawn, so a manifest that gains a concept gains a row with no\nrelease here. A map drawing no points of interest renders nothing rather than an empty panel\nframe.\n\nThe rows and their state are read off the map, and the panel follows the map's `displaychange`:\nit renders again whenever the map reports a change of what it draws or shows, whoever made it. So\nan overlay another surface mounts is listed as it is asked for, a key another surface hides reads\nas hidden at once, and so does a write a handler makes after it returns, all with the camera at\nrest. A camera move is no such change and costs the panel nothing, apart from the one render in\nwhich the zoom crosses `minZoom`.\n\nThe default list is the keys drawn **at the points-of-interest rank**. An unranked walk would\noffer hillshade and slope beside the huts, which are not points of interest and are not what this\npanel promises to toggle.",

    importPath: "@mountaya/elements-react",
    name: "PoiPanel",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "PoiPanelStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
      {
        description:
          'A label per manifest key. A key with no entry is shown as the key itself.\n\nThe keys come from the layers manifest at runtime, so they cannot live in the static\n`strings` contract the way the rest of this package\'s copy does. `"parking"` is legible\nwhere a blank row is not, which is why the fallback is the key rather than an empty\nstring: the same call `localizedName` in `@mountaya/elements/i18n` makes for a slug.',
        name: "labels",
        required: false,
        type: "Readonly<Record<string, string>>",
      },
      {
        description:
          "Restricts the panel to these keys, in this order.\n\nFor a consumer offering a curated subset (the assembled studio groups a few concepts out of a\nlonger manifest) rather than whatever the manifest happens to carry today.",
        name: "keys",
        required: false,
        type: "readonly string[]",
      },
      {
        description:
          'Curated groups, one row each. Supersedes {@link keys}.\n\nThe unit a user reasons about is rarely the manifest\'s: "water" is a spring, a drinking\ntap and a fountain, and offering the three separately asks them to know which of the\nthree the map drew. A group collapses that into one row whose toggle drives every key it\nnames.',
        name: "groups",
        required: false,
        type: "readonly PoiPanelGroup[]",
      },
      {
        description:
          "The groups currently on, when the application owns that state.\n\nUnset, the panel reads the map. Set, it only renders what it is told: the pair `onToggleGroup`\ncompletes, for a surface whose group state lives somewhere the map cannot answer for.",
        name: "activeGroups",
        required: false,
        type: "readonly string[]",
      },
      {
        description: "Overrides what a group's toggle does, on the terms {@link onToggle} states.",
        name: "onToggleGroup",
        required: false,
        seam: "handler",
        type: "(id: string, visible: boolean) => void",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Adds the master row above the categories, which turns the whole overlay on and off.\n\nOff by default: a panel offering one concept has nothing to master, and the row would read as a\nduplicate of the only toggle under it.",
        name: "showAllToggle",
        required: false,
        type: "boolean",
      },
      {
        description: "Overrides what the master row does, on the terms {@link onToggle} states.",
        name: "onToggleAll",
        required: false,
        seam: "handler",
        type: "(visible: boolean) => void",
      },
      {
        control: { kind: "number" },
        description:
          "The zoom below which the overlay draws nothing, so the panel can say so.\n\nPoints of interest are tiled at street zooms, so a user who switched them on at country\nscale sees an empty map and a panel full of lit toggles. The note names the reason; it\ndoes not disable anything, because the toggles are still what the map will honour once\nthe camera arrives.",
        name: "minZoom",
        required: false,
        type: "number",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Overrides what a toggle does.\n\nLeft unset, the panel drives the map. Set, it becomes presentational and the handler decides:\nthe same seam every control here offers.\n\nA write the handler makes to the map shows as the map reports it, whether the handler makes it\nbefore it returns or after.",
        name: "onToggle",
        required: false,
        seam: "handler",
        type: "(key: string, visible: boolean) => void",
      },
    ],
    propsTypeName: "PoiPanelProps",
    summary: "PoiPanel toggles what the points-of-interest overlay draws, one concept at a time.",
  },
  PreferencesPanel: {
    description:
      "Controlled, with no internal state, and that is the important decision here. Units are an\napplication-wide concern (the same choice governs an itinerary list, an export dialog and a\nprinted page, none of which this package can see), so a panel that owned the set would make the\nmap disagree with everything around it. Language is the exception: it is also a map concern, so\nselecting one drives `setLanguage` as well as reporting it.",

    importPath: "@mountaya/elements-react",
    name: "PreferencesPanel",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        description:
          "The preference set the panel edits: the length system, the form a travel rate takes, the slope notation, the temperature scale and the language.\n\nThe value every formatter and every element's `units` takes, so the value an application\nstores is the value it hands on. Seed it from `defaultUnits` of `@mountaya/elements/profile`:\nevery member is required, and a partial value round-tripped through storage is how a\nreader's units silently revert.",
        name: "preferences",
        required: true,
        type: "UnitFormatOptions",
      },
      {
        description:
          "Called with the whole preference set the press is asking for, not with the one field.\n\nThe same name `MapControls` takes, because it is the same value travelling one level further: a\npanel that spelled it `onChange` would leave the two surfaces reporting the same thing under\ntwo words.",
        name: "onPreferencesChange",
        required: true,
        seam: "handler",
        type: "(next: UnitFormatOptions) => void",
      },
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "PreferencesPanelStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
    ],
    propsTypeName: "PreferencesPanelProps",
    summary: "PreferencesPanel is the units and language picker.",
  },
  PresenceRoster: {
    description: "",

    importPath: "@mountaya/studio-react",
    name: "PresenceRoster",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description: "The editor resource whose session's collaborators the roster shows.",
        name: "resource",
        required: true,
        type: "CollabResource",
      },
      {
        description:
          "Overrides the words this surface says, for this surface only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest of\nthe section alone, and carried down to the rows and pickers this surface mounts. Scoped to\nthe sections it renders: a key it never says is a type error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "PresenceRosterStrings",
      },
    ],
    propsTypeName: "PresenceRosterProps",
    summary:
      "PresenceRoster renders a compact Avatar.Group of the collaborators currently in the given resource's session. The local user is never in the set (the slice holds only peers), so no self-exclusion is needed here. Each avatar shows the collaborator's initials in their deterministic palette color, with a tooltip carrying their display name, resolved through the host's peer-name seam. Beyond MAX_VISIBLE_AVATARS the group collapses into a \"+N\" overflow avatar. The roster is silent when no peer is present and while names load: a presence nicety carries no skeleton chrome.",
  },
  PrivacyControl: {
    description:
      "A dimmed explanation switches with both the selected state and the resource. When a `caption` is\nsupplied (the embed wizard, the zone form), the control renders compact: that explanation moves\ninto the label's help tooltip and the caption states the persistence scope below the segments,\nkeeping a setting saved apart from its surface terse among the fields around it. Flipping a\nsegment reports the new boolean through `onChange`; the consumer owns persistence (a local state\nset on create, a bus dispatch on edit/import/suggest, and behind an acknowledgement on the embed\nwizard's and the zone form's visibility flips).",

    importPath: "@mountaya/studio-react",
    name: "PrivacyControl",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "caption", required: false, type: "ReactNode" },
      {
        control: { kind: "boolean" },
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        description: "",
        name: "onChange",
        required: true,
        seam: "handler",
        type: "(isPrivate: boolean) => void",
      },
      {
        control: { kind: "enum", options: ["collection", "itinerary", "zone"] },
        description: "",
        name: "resource",
        required: true,
        type: '"collection" | "itinerary" | "zone"',
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "value",
        required: true,
        type: "boolean",
      },
    ],
    propsTypeName: "PrivacyControlProps",
    summary:
      "PrivacyControl is the one private/public control the itinerary create/edit form, the import shell, the suggest shell, the zone panel, and the embed wizard share, so each reads with the same wording and chrome. It carries a \"Visibility\" label through an Input.Wrapper, so it reads with the same dimmed/xs label treatment the rest of the form's inputs use. Below the label is the shared StudioSegmentedControl (sized to match the form's other inputs) of two segments, Private (the `visibility-private` padlock) and Public (the `visibility-public` globe), each laid out as an icon beside its label in a centered, no-wrap Group; the label is a plain span so it inherits the control's own label font rather than overriding it.",
  },
  PublicationNotice: {
    description:
      'It is the one warning for every surface that hands the itinerary to somebody else:\nthe share link, the downloaded file, the printed directions, the embed snippet and\nthe recorded video. Each acts on the publication, so once the draft moves ahead\nthey all silently stop matching the editor. Without this the divergence is invisible\nand reads as a bug ("I changed that, why is it not here?"); with it, the difference\nis the point and the fix is one click away. Wording it for any one of them in\nparticular would defeat the purpose, which is that a publisher meets the SAME\nsentence and the same button wherever they go.\n\nIt belongs ON the thing that is stale (the preview column, the link field, the\nsheet about to be generated), not at the top of a settings rail. That is where the\ncaveat means something; putting it above the controls instead would cost the rail its most\nvaluable space and, in the video wizard, would show it only on the final step, so the\nsteps before it would tune a camera against a stale preview with no signal at all. One\ncompact line keeps it a caveat rather than an alarm: nothing here is broken.\n\nThe never-published state reads here too, for the surfaces that stay usable without\na publication. A share link is one: it is a promise about a URL that stays valid\nacross a later publish, so it is minted and copyable either way and the notice is\nall the reader needs. The surfaces that produce an artifact NOW from nothing (a\nfile, a print, an embed frame, a recording) block on UnpublishedPanel instead.\n\nIt takes no scope gate of its own, and the omission is deliberate rather than an oversight.\nA PERSONAL itinerary can never be published, so it can never be `dirty` (the state that\nneeds the warning), and its one `unpublished` call site is the share modal, which never\nrenders it for one: the `studio.share.open` handler refuses a personal itinerary it can see,\nand a modal that opens anyway reads the draft and shows the share-link rule in place of the\nlink and everything below it. A guard here would be a second home for the rule with no case\nthat reaches it.\n\nThe button follows the rule every Publish in the studio follows. Publishing writes the\nitinerary, so it is offered only to a reader who may edit it (a write role in the organization\nthat owns it), and only where that organization\'s agreement includes Map Publishing (the\n`publish` entitlement). A member whose role only reads, and a viewer without the entitlement,\nread the line and are offered no act.',

    importPath: "@mountaya/studio-react",
    name: "PublicationNotice",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "itineraryId",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description:
          "The itinerary's owning organization, which decides whether the reader is offered the act.\n\nThe FACT, not the conclusion, as {@link UnpublishedPanel } takes it: the notice asks whether\nthe reader may edit the itinerary itself. Omitted (or the empty string a surface yields\nmid-read) is no answer yet, and the notice offers publishing: an unanswered question is not\na refusal.",
        name: "organizationId",
        required: false,
        type: "string | null",
      },
      {
        control: { kind: "enum", options: ["dirty", "unpublished"] },
        description: "",
        name: "state",
        required: true,
        type: 'Extract<PublicationState, "dirty" | "unpublished">',
      },
    ],
    propsTypeName: "PublicationNoticeProps",
    summary:
      "PublicationNotice tells a publisher that what sits beside it is the PUBLISHED itinerary, not the one they have been editing.",
  },
  RadioOptionCard: {
    description: "Memoised: choosing one option moves exactly one card's `selected`.",

    importPath: "@mountaya/studio-react",
    name: "RadioOptionCard",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "boolean" },
        description: "",
        name: "checked",
        required: true,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "description",
        required: false,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        control: {
          kind: "enum",
          options: [
            "action-add",
            "action-add-poi",
            "action-add-waypoint",
            "action-capture-view",
            "action-configure",
            "action-copy",
            "action-delete",
            "action-disable-link",
            "action-draw",
            "action-duplicate",
            "action-edit",
            "action-embed",
            "action-engine",
            "action-export",
            "action-group",
            "action-insert",
            "action-insert-after",
            "action-insert-before",
            "action-merge",
            "action-merge-next",
            "action-merge-previous",
            "action-move",
            "action-pick-point",
            "action-publish",
            "action-rename",
            "action-repeat-via",
            "action-reset",
            "action-retry",
            "action-rotate-link",
            "action-send",
            "action-set-speed",
            "action-split",
            "action-split-merge",
            "action-stop",
            "action-streetview",
            "action-ungroup",
            "agent",
            "agent-stop",
            "amenities",
            "api-key",
            "appearance",
            "ascent",
            "authoring",
            "back",
            "camera-mode-chase",
            "camera-mode-cinematic",
            "camera-mode-director",
            "camera-mode-drone",
            "camera-mode-helicopter",
            "camera-mode-overview",
            "camera-mode-smart",
            "check",
            "chevron-down",
            "chevron-right",
            "chevron-up",
            "close",
            "collection-edit",
            "collection-new",
            "compass",
            "contact",
            "controls",
            "danger",
            "datum-aspect",
            "datum-difficulty",
            "datum-distance",
            "datum-duration",
            "datum-elevation",
            "datum-generic",
            "datum-humidity",
            "datum-slope",
            "datum-snow-depth",
            "datum-snowfall",
            "datum-speed",
            "datum-surface",
            "datum-temperature",
            "datum-visibility",
            "datum-waytype",
            "datum-wind",
            "default-view",
            "descent",
            "directions",
            "download",
            "error",
            "explore",
            "external-link",
            "feedback-negative",
            "feedback-positive",
            "folder",
            "format-csv",
            "format-fit",
            "format-geojson",
            "format-gpx",
            "format-kml",
            "format-kmz",
            "format-tcx",
            "frame-custom",
            "frame-square",
            "frame-vertical",
            "frame-widescreen",
            "freehand",
            "geolocate",
            "guide",
            "help",
            "import",
            "import-external",
            "info",
            "itinerary",
            "layers",
            "locked",
            "media",
            "media-loop",
            "media-pause",
            "media-play",
            "media-stop",
            "menu",
            "more",
            "pin",
            "pin-filled",
            "place",
            "preferences",
            "profile",
            "profile-graph",
            "profile-minimal",
            "profile-strip",
            "properties",
            "reachability",
            "rejected",
            "route-loop",
            "route-point-to-point",
            "routed",
            "scope-organization",
            "scope-personal",
            "search",
            "search-empty",
            "share",
            "share-email",
            "share-facebook",
            "share-linkedin",
            "share-reddit",
            "share-telegram",
            "share-whatsapp",
            "share-x",
            "shot-hold",
            "shot-orbit",
            "skipped",
            "streetview",
            "success",
            "suggest",
            "theme-dark",
            "theme-light",
            "time-window",
            "timing",
            "unavailable",
            "upload",
            "video",
            "visibility-private",
            "visibility-public",
            "warning",
            "zone",
            "zone-mode-circle",
            "zone-mode-freehand",
            "zone-mode-polygon",
            "zone-mode-sector",
            "zone-mode-sensor",
            "zoom-in",
            "zoom-out",
          ],
        },
        description: "",
        name: "glyph",
        required: false,
        type: "UiGlyph",
      },
      { description: "", name: "media", required: false, type: "ReactNode" },
      { description: "", name: "specs", required: false, type: "ReactNode" },
      {
        control: { kind: "string" },
        description: "",
        name: "title",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "value",
        required: true,
        type: "string",
      },
    ],
    propsTypeName: "RadioOptionCardProps",
    summary: "RadioOptionCard is one option card of a mode, format or profile picker.",
  },
  ReadOnlySegmentList: {
    description:
      "It skips the editor list's store reads entirely rather than rendering their results inert. A\nvisitor holds none of those verbs, the bus would deny each one, and subscribing to a lock map\nto decide the appearance of controls that are not there is work with no output.",

    importPath: "@mountaya/studio-react",
    name: "ReadOnlySegmentList",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "emptyState", required: false, type: "ReactNode" },
      {
        description: "",
        name: "emphasisSegmentIds",
        required: false,
        type: "(segmentId: string) => readonly string[] | undefined",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "itineraryId",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "label",
        required: true,
        type: "string",
      },
      {
        description: "",
        name: "segments",
        required: true,
        type: 'ItineraryViewSource["segments"]',
      },
      {
        defaultValue: "defaultUnits",
        description: "",
        name: "units",
        required: false,
        type: "UnitFormatOptions",
      },
    ],
    propsTypeName: "ReadOnlySegmentListProps",
    summary:
      "ReadOnlySegmentList is the segment list a surface that grants no editor capability renders, such as the public share view. It is the SAME section chrome over read-only rows: no gutters, no add control, no live-edit overlay, no per-row menu.",
  },
  SearchControl: {
    description:
      "**Not** part of `<MapControls>`, on purpose. It is a panel-width bar, not a square chip, and a\nstack of icon buttons is the wrong place for it: an application positions it where its own chrome\nwants it, which in the assembled studio is the top-left of the map.\n\nEverything under the box comes from `useSearch`, which is exported beside this component: an\napplication with its own component library renders its own list from the same snapshot and gets\nthe same abort discipline, the same progressive paint and the same rows.\n\n**Without a map it is inert, unless `onSearch` says otherwise.** The map is what the default\ntransport, the area scope and the camera move all come from, so a search box over no map would be\na box that accepts typing and answers nothing. Given `onSearch` it works, without the area scope\nand the camera move, both of which need a map to mean anything.\n\nThe combobox pattern is implemented in full: `aria-expanded` and `aria-activedescendant` on the\ninput, `listbox`/`option` under it, and a `mousedown` that is prevented so a click on a result\nlands before the blur that would otherwise close the list out from under it.\n\nA pick and Escape close the list and leave the box focused, with its query. The list shows again\nwhen the reader types on, when the reader presses ArrowDown or ArrowUp in the box, or when focus\nleaves the control and returns. A query the application writes into a controlled box, the picked\nrow's title for one, opens nothing. While no list is shown no key reaches a row: the arrow that\nshows the list moves no cursor, and Enter picks nothing.",

    importPath: "@mountaya/elements-react",
    name: "SearchControl",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        control: {
          kind: "enumArray",
          options: ["collections", "itineraries", "places", "pois", "ways", "zones"],
        },
        description: "The branches to ask for.",
        name: "targets",
        required: false,
        type: "readonly SearchTargetName[]",
      },
      {
        control: { kind: "enum", options: ["DE", "EN", "ES", "FR", "IT", "JA", "ZH"] },
        description: "The language the Data API answers in.",
        name: "language",
        required: false,
        type: "DataLanguage",
      },
      {
        control: { kind: "number" },
        description: "Per-target result cap: a whole number from 1 to 50, the range the API takes.",
        name: "limit",
        required: false,
        type: "number",
      },
      {
        description:
          "The units every figure in the list is stated in: an itinerary's length and ascent, and the distance or elevation in the sentence under a place, a way or a point of interest.\n\nA change of its `length` or its `language` asks a fresh round, since those sentences are\nworded as their branch lands.",
        name: "units",
        required: false,
        type: "UnitFormatOptions",
      },
      {
        description:
          "How a row's title and second line are worded for the reader, as `SearchEngine` takes it.\n\n`resolveLabel` answers the word a wire value reads as: a category, a way's tags. `units`,\nwhen stated here, replaces the control's own `units` for the sentences this wording words,\nand an application states one or the other.",
        name: "wording",
        required: false,
        type: "SearchWording",
      },
      {
        control: { kind: "string" },
        description:
          "The query, when the application owns it. Unset, the control owns its own, whether or not `onQueryChange` is supplied.",
        name: "query",
        required: false,
        type: "string",
      },
      {
        description:
          "Called with the query each keystroke and each clear asks for. With `query` supplied it is how the application is asked to change the value. With none it observes: the control has already written its own.",
        name: "onQueryChange",
        required: false,
        seam: "handler",
        type: "(query: string) => void",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the search is restricted to the visible area, when the application owns it. Unset, the control owns its own, whether or not `onViewportScopeChange` is supplied.",
        name: "viewportScope",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Called with the scope a press on the checkbox asks for. With `viewportScope` supplied it is how the application is asked to change the value. With none it observes.",
        name: "onViewportScopeChange",
        required: false,
        seam: "handler",
        type: "(scoped: boolean) => void",
      },
      {
        description:
          "Overrides what picking a result does.\n\nUnset, the control moves the camera onto the result and drops a pin. Set, it does neither and\nthe handler decides: the assembled studio routes a pick through its own mutation surface this\nway while rendering this exact list.",
        name: "onSelect",
        required: false,
        seam: "handler",
        type: "(row: SearchRow) => void",
      },
      {
        description:
          "Overrides how a round reaches the API. Unset, it routes the map's own `search`.",
        name: "onSearch",
        required: false,
        seam: "handler",
        type: "SearchFetch",
      },
      {
        description:
          "Called once for each branch whose request failed, with the failure.\n\nAlways an `Error`, as every element's `onError` is: an `onSearch` that rejects with any other\nvalue is reported as a `MountayaElementsRejectionError` holding that value as its `cause`. It\nreports and replaces nothing: the list still shows the branch's error line and its retry.",
        name: "onError",
        required: false,
        seam: "handler",
        type: "(error: Error) => void",
      },
      {
        description: "The pin dropped on a picked result. Its id and position are the control's.",
        name: "resultMarker",
        required: false,
        type: 'Omit<MarkerOptions, "id" | "lngLat">',
      },
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "SearchControlStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
    ],
    propsTypeName: "SearchControlProps",
    summary:
      "SearchControl is a search box over Mountaya's unified search, with the results under it.",
  },
  SegmentGroupModal: {
    description: "",

    importPath: "@mountaya/studio-react",
    name: "SegmentGroupModal",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "SegmentGroupModal is the mount guard for the segment-group form. It renders nothing while the form is closed and keys the body on the target, so opening it on a different group (or switching from editing to creating) remounts with fresh fields rather than reconciling a stale draft into a new group.",
  },
  SegmentList: {
    description:
      "**The list renders the live-aware chain and reasons about the committed one.** A segment being\ndrawn from scratch appears at the tail and an insert appears in the gap it is bound for, so\nwhat is on screen is not what the server holds, and every chain verb is a server operation.\nThe gutters are positional over what is rendered, because a gutter is a divider between two\nrendered rows; everything else is keyed by id off the committed chain.\n\n**Each row is preceded by its gutter as a SIBLING rather than a child.** The list section\ngives every direct child paint containment, so an affordance drawn inside a row and offset\nabove it would be clipped. The gutter inherits the divider it replaces, so the list's height\nis unchanged. The gap BELOW the last row belongs to the add control, which carries the engine\npicker and the ceiling's gate a bare gutter has no room for.",

    importPath: "@mountaya/studio-react",
    name: "SegmentList",
    package: "@mountaya/studio-react",
    parts: [
      {
        name: "AddButton",
        props: [
          {
            control: { kind: "string" },
            description: "",
            name: "activitySlug",
            required: false,
            type: "string",
          },
          {
            control: { kind: "string" },
            defaultValue: "null",
            description: "",
            name: "agentLockHint",
            required: false,
            type: "string | null",
          },
          {
            control: { kind: "boolean" },
            defaultValue: "false",
            description: "",
            name: "engineChoice",
            required: false,
            type: "boolean",
          },
          {
            control: { kind: "string" },
            defaultValue: "null",
            description: "",
            name: "insertLimitHint",
            required: false,
            type: "string | null",
          },
          {
            control: { kind: "enum", options: ["directions", "linestring"] },
            description:
              "The engine a single-engine activity opens in, as the first entry of `availableEnginesForActivity` names it. `null` for an activity the catalog gives no engine: nothing can be drawn for it, and the control renders nothing.",
            name: "mode",
            required: true,
            type: "SegmentEditMode | null",
          },
          {
            description: "",
            name: "onExplainEngines",
            required: false,
            seam: "handler",
            type: "() => void",
          },
          {
            description: "",
            name: "onRefused",
            required: false,
            seam: "handler",
            type: "(result: ToolResult) => void",
          },
        ],
        propsTypeName: "AddSegmentButtonProps",
        summary: "AddSegmentButton is the foot of the segment editor.",
      },
      {
        name: "Row",
        props: [
          {
            control: { kind: "string" },
            description: "",
            name: "ascent",
            required: true,
            type: "string",
          },
          { description: "", name: "chain", required: false, type: "SegmentRowChainOptions" },
          {
            control: { kind: "string" },
            description: "",
            name: "color",
            required: false,
            type: "string | null",
          },
          {
            control: { kind: "boolean" },
            description: "",
            name: "deleteDisabled",
            required: false,
            type: "boolean",
          },
          {
            control: { kind: "string" },
            description: "",
            name: "distance",
            required: true,
            type: "string",
          },
          {
            control: { kind: "number" },
            description: "",
            name: "distanceMeters",
            required: false,
            type: "number | null",
          },
          {
            control: { kind: "number" },
            description: "",
            name: "durationSeconds",
            required: false,
            type: "number | null",
          },
          { description: "", name: "group", required: false, type: "SegmentRowGroupActions" },
          { description: "", name: "groupRail", required: false, type: "SegmentRowGroupRail" },
          { description: "", name: "indicators", required: false, type: "ReactNode" },
          {
            control: { kind: "enum", options: ["directions", "linestring"] },
            description: "",
            name: "mode",
            required: true,
            type: "SegmentEditMode",
          },
          {
            control: { kind: "string" },
            description: "",
            name: "name",
            required: true,
            type: "string",
          },
          {
            description:
              "Downloads a segment, called with the row's own segment id. Present, a row whose chain is carried offers Download segment in its menu; omitted, the row offers no download.\n\nDownloading a segment is a Map Publishing act, which the viewer's `publish` entitlement\nanswers and a row cannot read: the parent that holds the viewer passes it to a viewer holding\n`publish` and leaves it out for any other. One function serves every row, so a list hands\nthe same reference to each and its rows' memo holds.",
            name: "onExport",
            required: false,
            seam: "handler",
            type: "(segmentId: string) => void",
          },
          {
            control: { kind: "string" },
            description: "",
            name: "reasonMessage",
            required: false,
            type: "string | null",
          },
          {
            control: { kind: "number" },
            description: "",
            name: "speed",
            required: false,
            type: "number | null",
          },
          {
            control: { kind: "number" },
            description: "",
            name: "activityDefaultSpeed",
            required: false,
            type: "number | null",
          },
          { description: "", name: "trailing", required: false, type: "ReactNode" },
          {
            control: { kind: "string" },
            description: "The router profile a directions edit routes under.",
            name: "activitySlug",
            required: false,
            type: "string",
          },
          {
            control: { kind: "string" },
            description:
              "The sentence a row shows while a run drives the studio, or null where none can.",
            name: "agentLockHint",
            required: false,
            type: "string | null",
          },
          {
            control: { kind: "string" },
            description:
              "The itinerary the row belongs to. Undefined for a draft's row, whose id is on no tile.",
            name: "itineraryId",
            required: false,
            type: "string",
          },
          {
            description: "Opens the row's edit in place of the persisted chain's.",
            name: "onEditGeometry",
            required: false,
            seam: "handler",
            type: "() => void",
          },
          {
            description:
              "A refusal of one of the row's verbs, with the verb it came from. Read through a ref, so a handler written inline is safe.",
            name: "onFailure",
            required: false,
            seam: "handler",
            type: "(failure: SegmentRowFailure) => void",
          },
          {
            control: { kind: "string" },
            description:
              "The sentence a row shows while a peer holds the segment, or null where none can.",
            name: "peerLockHint",
            required: false,
            type: "string | null",
          },
          {
            control: { kind: "string" },
            description: "The segment the row stands for.",
            name: "segmentId",
            required: true,
            type: "string",
          },
          {
            control: { kind: "string" },
            description: "The segment's current name, which the rename opens on.",
            name: "segmentName",
            required: true,
            type: "string",
          },
          {
            control: { kind: "string" },
            description:
              "The sentence a row shows while its segment is not saved yet, or null where none can be.",
            name: "unsavedHint",
            required: false,
            type: "string | null",
          },
        ],
        propsTypeName: "SegmentRowProps",
        summary: "SegmentRow renders one segment as a two-line editor row.",
      },
      {
        name: "Seam",
        props: [
          {
            control: { kind: "string" },
            description: "",
            name: "afterSegmentId",
            required: true,
            type: "string | null",
          },
          {
            control: { kind: "boolean" },
            description: "",
            name: "controlsEnabled",
            required: true,
            type: "boolean",
          },
          { description: "", name: "group", required: true, type: "SegmentSeamGroup | null" },
          {
            control: { kind: "string" },
            description: "",
            name: "groupChipDisabledHint",
            required: true,
            type: "string | null",
          },
          {
            control: { kind: "boolean" },
            description: "",
            name: "insertEngineChoice",
            required: true,
            type: "boolean",
          },
          {
            control: { kind: "enum", options: ["directions", "linestring"] },
            description: "",
            name: "insertMode",
            required: true,
            type: "SegmentEditMode | null",
          },
          { description: "", name: "merge", required: true, type: "SegmentChainMerge | null" },
          {
            description: "",
            name: "onEditGroup",
            required: true,
            seam: "handler",
            type: "(groupId: string) => void",
          },
          {
            description: "",
            name: "onInsert",
            required: true,
            seam: "handler",
            type: "(afterSegmentId: string | null, mode: SegmentEditMode) => void",
          },
          {
            description: "",
            name: "onMerge",
            required: true,
            seam: "handler",
            type: "(segmentId: string) => void",
          },
          {
            description: "",
            name: "onMergePreview",
            required: true,
            seam: "handler",
            type: "(segmentIds: readonly string[] | null) => void",
          },
        ],
        propsTypeName: "SegmentSeamProps",
        summary:
          "SegmentSeam is the hover gutter between two segment rows: a zero-height sibling of the rows carrying the pill that inserts a segment into this gap and the pill that merges the two rows it sits between. It is a sibling rather than something drawn inside a row because every row carries paint containment, which would clip an affordance offset above it; the styling lives in the list section's module, where the seam inherits the divider it replaces so the list's height is unchanged.",
      },
    ],
    props: [
      {
        control: { kind: "number" },
        defaultValue: "null",
        description: "",
        name: "activityDefaultSpeed",
        required: false,
        type: "number | null",
      },
      {
        control: { kind: "string" },
        defaultValue: "null",
        description: "",
        name: "agentLockHint",
        required: false,
        type: "string | null",
      },
      {
        description:
          "Shown in place of the rows when the chain is empty AND nothing is being drawn.",
        name: "emptyState",
        required: false,
        type: "ReactNode",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "True when the activity can be authored either way, so the inserts offer the choice.",
        name: "engineChoice",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "label",
        required: true,
        type: "string",
      },
      {
        control: { kind: "number" },
        description:
          "A ceiling the host holds the list to, shown as a persistent `count/limit` beside the section label. The studio sets none: omitted, the label carries the bare count.",
        name: "limit",
        required: false,
        type: "number",
      },
      {
        control: { kind: "string" },
        description:
          "The sentence the info glyph beside a `limit` explains it with, shown only with a `limit`.",
        name: "limitTooltip",
        required: false,
        type: "string",
      },
      {
        control: { kind: "enum", options: ["directions", "linestring"] },
        description:
          "The engine a single-engine activity authors in, as the first entry of `availableEnginesForActivity` names it. `null` for an activity the catalog gives no engine: nothing can be drawn for it, so the list renders no add control and no insert pill.",
        name: "mode",
        required: true,
        type: "SegmentEditMode | null",
      },
      {
        description: "",
        name: "onExplainEngines",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      {
        description:
          "Downloads a segment, called with its id, which the published rows offer as Download segment once the chain carries them. Omitted, no row offers a download. Downloading is a Map Publishing act, which the viewer's `publish` entitlement answers: a host passes it to a viewer holding `publish`.",
        name: "onExport",
        required: false,
        seam: "handler",
        type: "(segmentId: string) => void",
      },
      {
        description: "Resolves a peer's id to the sentence a held row speaks with.",
        name: "peerLockHint",
        required: false,
        type: "(holderId: string) => string | null",
      },
      {
        description:
          "Host badges for one row, such as who is looking at the segment.\n\nCalled once for each segment while the slot's reference stands, and for every row again\nwhen the reference changes: the default row keeps what the slot drew for it until then. So a\nslot that reads a value outside its argument changes identity with that value, as a\n`useCallback` that lists it does.",
        name: "renderIndicators",
        required: false,
        type: "(segmentId: string) => ReactNode",
      },
      {
        description:
          "Renders one row, replacing the published one entirely.\n\nThe escape hatch for a host whose row is genuinely its own: Mountaya's carries\nlive-awareness badges, a localized sentence per refusable verb, and a peer's display name\nresolved from an organization directory. Everything the list derived arrives resolved, so\na custom row re-derives nothing and cannot disagree with the gutter beside it.\n\nOmit it and the list renders the published row, which is the whole point of the default:\n`units`, `renderIndicators` and `peerLockHint` cover the ordinary host without one.",
        name: "renderRow",
        required: false,
        type: "(row: SegmentListRow) => ReactNode",
      },
      {
        defaultValue: "defaultUnits",
        description: "",
        name: "units",
        required: false,
        type: "UnitFormatOptions",
      },
      {
        control: { kind: "string" },
        defaultValue: "null",
        description: "What a row the server has never seen says about itself.",
        name: "unsavedHint",
        required: false,
        type: "string | null",
      },
      {
        control: { kind: "string" },
        description: "The activity an inserted routed segment routes for.",
        name: "activitySlug",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "The itinerary whose chain the list renders and whose verbs it dispatches.",
        name: "itineraryId",
        required: true,
        type: "string",
      },
      {
        description:
          "A refusal from one of the list's chain verbs or group verbs, in the row's own vocabulary.\n\n`SegmentRowFailure` rather than a union of the list's own, because a host maps every\nrefusal to a sentence in ONE place: a gutter's insert and a row menu's insert are the same\nrefusal reaching the user from two controls, and giving them two vocabularies would make\nthe host answer the same question twice and eventually differently.\n\nFor the same reason it hears the refusals a row keeps as its `lastFailure` and no others: a\nwithheld confirmation ({@link isConfirmationWithheld}) never reaches it, since the verb never\nran.",
        name: "onFailure",
        required: false,
        seam: "handler",
        type: "(failure: SegmentRowFailure) => void",
      },
      {
        description: "The COMMITTED chain, in chain order.",
        name: "segments",
        required: true,
        type: 'ItineraryViewSource["segments"]',
      },
    ],
    propsTypeName: "SegmentListProps",
    summary:
      "SegmentListRoot renders the studio's segment editor: the chain as two-line editor rows, a hover gutter between each pair carrying the insert and merge accelerators, and the add control at the foot.",
  },
  SegmentSeam: {
    description:
      "The reveal is pure CSS (hovering either neighbouring row, or the pill itself,\nfades it in), so a pointer travelling down a long chain triggers no React state\nand no re-render. The one piece of local state is whether the insert pill's engine\nmenu is open, which pins the reveal through `data-menu-open` so the pill does not\nfade out from under its own dropdown. That is also why this component is memoized\nand subscribes to nothing: everything else it needs is resolved once by the list\nand handed down, so a peer's lock landing on one segment re-renders one seam's\nprops rather than re-subscribing every gutter.\n\nIt is a POINTER accelerator, not a second navigation path: the controls are\n`aria-hidden` and out of the tab order, because every verb they offer is already\nreachable from the row's own kebab and each row already contributes three tab\nstops. Doubling the tab depth of a long itinerary to duplicate verbs that are one\nkeystroke away in the menu is a worse keyboard experience, not a better one.",

    importPath: "@mountaya/studio-react",
    name: "SegmentSeam",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "afterSegmentId",
        required: true,
        type: "string | null",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "controlsEnabled",
        required: true,
        type: "boolean",
      },
      { description: "", name: "group", required: true, type: "SegmentSeamGroup | null" },
      {
        control: { kind: "string" },
        description: "",
        name: "groupChipDisabledHint",
        required: true,
        type: "string | null",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "insertEngineChoice",
        required: true,
        type: "boolean",
      },
      {
        control: { kind: "enum", options: ["directions", "linestring"] },
        description: "",
        name: "insertMode",
        required: true,
        type: "SegmentEditMode | null",
      },
      { description: "", name: "merge", required: true, type: "SegmentChainMerge | null" },
      {
        description: "",
        name: "onEditGroup",
        required: true,
        seam: "handler",
        type: "(groupId: string) => void",
      },
      {
        description: "",
        name: "onInsert",
        required: true,
        seam: "handler",
        type: "(afterSegmentId: string | null, mode: SegmentEditMode) => void",
      },
      {
        description: "",
        name: "onMerge",
        required: true,
        seam: "handler",
        type: "(segmentId: string) => void",
      },
      {
        description: "",
        name: "onMergePreview",
        required: true,
        seam: "handler",
        type: "(segmentIds: readonly string[] | null) => void",
      },
    ],
    propsTypeName: "SegmentSeamProps",
    summary:
      "SegmentSeam is the hover gutter between two segment rows: a zero-height sibling of the rows carrying the pill that inserts a segment into this gap and the pill that merges the two rows it sits between. It is a sibling rather than something drawn inside a row because every row carries paint containment, which would clip an affordance offset above it; the styling lives in the list section's module, where the seam inherits the divider it replaces so the list's height is unchanged.",
  },
  ShareViewModal: {
    description:
      "An ITINERARY link additionally carries the social destinations, whose caption is\ncomposed from the one active itinerary in the store, and names that itinerary in the\nheader through the shared subject line. A COLLECTION link carries neither: a collection\nhas no single distance or ascent a caption could boast, so its header simply names\nthe collection and the body is the link alone.",

    importPath: "@mountaya/studio-react",
    name: "ShareViewModal",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "name",
        required: true,
        type: "string",
      },
      { description: "", name: "onClose", required: true, seam: "handler", type: "() => void" },
      {
        control: { kind: "boolean" },
        description: "",
        name: "opened",
        required: true,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "shareToken",
        required: true,
        type: "string",
      },
      { description: "", name: "target", required: true, type: "ShareLinkTarget" },
      {
        description:
          "Overrides the words this surface says, for this surface only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest of\nthe section alone, and carried down to the rows and pickers this surface mounts. Scoped to\nthe sections it renders: a key it never says is a type error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "ShareViewModalStrings",
      },
    ],
    propsTypeName: "ShareViewModalProps",
    summary:
      "ShareViewModal is the visitor-facing share dialog on the read-only /studio/share surface. It reads and creates nothing: the visitor already holds the link, so it issues no mutation and dispatches no tool. It rebuilds the copyable link from the target and token that rode the URL, and presents it read-only; it offers nothing else to act on, and the corner close is its one way out. The open state and close handler are the caller's, because studio.share.open is authenticated-only and denied here, so a bus dispatch would no-op.",
  },
  SidenavContentBody: {
    description:
      "It carries three load-bearing properties, which is why it is a component rather than three\nprops restated per panel:\n\n- `p=\"md\"`, the inset. The panel track itself carries no padding, so each panel pads its own\n  content; that is what lets the sticky header and footer break out to the panel's true edge.\n- `mih=\"100%\"`, which fills the track's scrolling viewport even when the content is shorter.\n  Without it the flex column collapses to its content, the footer's `margin-top: auto` has no\n  slack to consume, and the action stops seating at the panel's bottom edge.\n- the studio's form gap (`md`): one vertical rhythm across every panel.\n\nThe studio's own form panels take a variant of it that adds the compact input tier on top of\nthis same geometry.",

    importPath: "@mountaya/studio-react",
    name: "SidenavContentBody",
    package: "@mountaya/studio-react",
    parts: [],
    props: [{ description: "", name: "children", required: true, type: "ReactNode" }],
    propsTypeName: "SidenavContentBodyProps",
    summary:
      "SidenavContentBody is a sidenav panel's body: the container a panel wraps its content in, and the middle member of the panel chrome, between {@link SidenavContentHeader } and the footer the panel pins to its bottom edge.",
  },
  SidenavContentHeader: {
    description:
      "A leading affordance on the left (a back arrow, or a static `icon` node for a surface whose\ndismiss lives in `actions`), then a title, an optional dimmed description, and an optional\nactions node on the right. It is sticky chrome pinned to the panel top, the mirror of\nthe panel's footer at the other edge: it sticks to the top of the panel's own\nscrolling viewport so the affordance and title stay reachable while the fields scroll beneath\nit, the page background occludes that content, and a hairline bottom divider matches the\nfooter's top one.\n\nNegative top and inline margins break it out of the panel's inset so the divider spans the\npanel's full width, which is why it must be the first child of a\n{@link SidenavContentBody }: the breakout cancels exactly that body's padding.\n\n`backLabel` and `backDisabledLabel` arrive resolved. This package ships no i18n runtime, so\nthe words are the caller's in whichever language they chose.",

    importPath: "@mountaya/studio-react",
    name: "SidenavContentHeader",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "boolean" },
        description: "",
        name: "backDisabled",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "backDisabledLabel",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "backLabel",
        required: false,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "icon",
        required: false,
        type: "undefined",
      },
      { description: "", name: "onBack", required: false, seam: "handler", type: "() => void" },
      { description: "", name: "actions", required: false, type: "ReactNode" },
      {
        control: { kind: "string" },
        description: "",
        name: "description",
        required: false,
        type: "string",
      },
      { description: "", name: "subtitle", required: false, type: "ReactNode" },
      { description: "", name: "title", required: true, type: "ReactNode" },
    ],
    propsTypeName: "SidenavContentHeaderProps",
    summary: "SidenavContentHeader is the chrome a sidenav panel mounts at its top.",
  },
  SidenavFormActions: {
    description:
      "The button is filled and takes no glyph: the verb carries the meaning. `onPrimary` is a pure\ncallback, so each flow decides what its verb drives. A flow needing a second, louder primary\nalongside this one composes a footer of its own instead.",

    importPath: "@mountaya/studio-react",
    name: "SidenavFormActions",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "banner", required: false, type: "ReactNode" },
      { description: "", name: "hint", required: false, type: "ReactNode" },
      { description: "", name: "onPrimary", required: true, seam: "handler", type: "() => void" },
      {
        control: { kind: "boolean" },
        description: "",
        name: "primaryDisabled",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "primaryDisabledReason",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "primaryLabel",
        required: true,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "primaryLoading",
        required: false,
        type: "boolean",
      },
      {
        description: "The flow's lesser verbs, on the button's row before it.",
        name: "secondary",
        required: false,
        type: "ReactNode",
      },
    ],
    propsTypeName: "SidenavFormActionsProps",
    summary:
      "SidenavFormActions is the bottom-pinned primary action a sidenav flow renders in its panel's footer: one button carrying the flow's explicit verb, full width unless the flow's lesser verbs share its row, with an optional dimmed hint beneath.",
  },
  StagingCreateFooter: {
    description:
      'It reads the commit-time fields off the active draft session through ONE shallow\nselector (selectActiveDraftSession, the neutral "which flow\'s draft is live"\nreader): the draft presence + the name + the resolved activity drive the gate,\nand the status drives the loading state. Create dispatches the per-flow commit and\nreads its per-flow label (both off toolNamesFor: import "Create itinerary", suggest\n"Save suggestion and create itinerary"), gated until a draft, a valid name (between\n`DISPLAY_NAME_MIN_LENGTH` and `DISPLAY_NAME_MAX_LENGTH` characters), AND a resolved\nactivity are all present; it shows a loading state while the commit request is in\nflight (import marks the session `confirming`, suggest `generating`).\n\nIt self-gates on the geometry-edit session: while a draft row\'s "Edit on map"\narms a segment/waypoint edit, EditActions owns the panel footer, so this footer\nrenders null and exactly one footer shows. The selector flips only on a session\nopen/close, not on a draft push, so the footer does not churn while the user\nreshapes the line. Every mutation flows through the bus.\n\nWhile another create\'s request is out, Create is held with the one-edit rule as its reason:\nthe commit would be refused with `edit-in-progress`. The draft\'s own commit shows the loading\nstate instead. The itinerary a commit creates takes the editor as it lands.',

    importPath: "@mountaya/studio-react",
    name: "StagingCreateFooter",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "enum", options: ["import", "suggest"] },
        description: "The flow whose staged draft the Create action commits.",
        name: "draftFlow",
        required: true,
        type: "DraftEditorFlow",
      },
      {
        description:
          "Overrides the words this surface says, for this surface only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest of\nthe section alone, and carried down to the rows and pickers this surface mounts. Scoped to\nthe sections it renders: a key it never says is a type error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "StagingCreateFooterStrings",
      },
    ],
    propsTypeName: "StagingCreateFooterProps",
    summary:
      "StagingCreateFooter is the one sticky footer of the staging panel: the Create action that commits the import/suggest draft. It lives at the very bottom of the panel (the host's sidenav renders it after the metadata form, the granularity control, and the draft rows), so the one sticky footer seats below every staging control rather than mid-panel.",
  },
  StreetViewControl: {
    description:
      "Street View opens AT a place, so the gesture that opens it names one. Pressing the handle\ncaptures the pointer, turns on Google's coverage raster so the user can see where imagery\nexists, and lifts a ghost that follows the cursor; releasing over the map unprojects the drop,\ngates it on the corridor when there is one, asks Google whether a panorama is there, and either\nhands the point to `onOpen` or bounces the handle with a notice. A press that does not drag,\nand Enter or Space on the focused handle, runs the same pipeline at the map's centre, so a\nreader who cannot drag opens Street View too. Escape while the handle is held ends the drag\nwith no drop.\n\n**The corridor gate runs before the coverage check, always.** The check is a billed request\nagainst the application's own key, so an off-itinerary drop must never reach it.\n\nBelow `minZoom` the handle is inert rather than absent (dimmed, undraggable, and carrying\na tooltip that names the reason), so the affordance stays discoverable and its return on\nzoom-in is obvious. A zoom that falls below the threshold with the handle still held ends\nthe drag then and there, so a drop can never land from a view where it is disabled. A map\nthe provider replaces under the held handle ends it too: the coverage raster and the corridor\ncome down over the map the drag began on, and the release that follows resolves no drop.",

    importPath: "@mountaya/elements-react",
    name: "StreetViewControl",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "StreetViewControlStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
      {
        control: { kind: "number" },
        defaultValue: "13",
        description: "The zoom below which the handle is inert.",
        name: "minZoom",
        required: false,
        type: "number",
      },
      {
        control: { kind: "string" },
        description:
          "The Maps JS API key the coverage check is made with.\n\nNever ours: Street View is Google's product billed to whoever asks for it, so the key\nbelongs to the application. Without one, and without an `onDrop` of your own, the\ncontrol renders nothing at all, because a handle whose every drop must fail is worse\nthan an absent one.",
        name: "googleMapsApiKey",
        required: false,
        type: "string",
      },
      {
        description:
          "Bounds the droppable area to an itinerary, and draws that corridor for the length of a drag.",
        name: "bounds",
        required: false,
        type: "StreetViewBounds",
      },
      {
        description:
          "Overrides what a grab does. Unset, the map shows Street View coverage and the corridor.\n\nPaired with `onDragEnd`, which is called on EVERY path that ends a drag: a release, a\npointer cancel, a capture the browser took back, a zoom falling below the threshold with the\nhandle still held, and the control leaving the page mid-drag. An override that shows\nsomething has to be told to take it down again by every one of them.\n\nThis is also the pair that makes the handle live with **no map in context**, where every\nother control here is made live by whichever single handler replaces its default. The\nasymmetry is the drop: unprojecting a screen point into a coordinate is the map's own\nmeasurement, so a mapless handle can be dragged and can tell an application about the\ndrag, but the drop itself resolves to nothing. Presentational Street View is therefore\nthe drag overrides, and `onDrop`/`onOpen` never fire without a canvas to aim at.",
        name: "onDragStart",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      {
        description:
          "Overrides what the end of a drag does, on the terms {@link onDragStart} states.",
        name: "onDragEnd",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      {
        description:
          "Called when a drop is refused, for an application raising its own message. Only the latest drop is answered: a check a later drop replaced reports nothing, and neither does one that answers after the control was disabled or unmounted.",
        name: "onUnavailable",
        required: false,
        seam: "handler",
        type: "(reason: StreetViewMiss) => void",
      },
      {
        description:
          "Called when a drop's coverage check failed rather than found no imagery: the optional peer `@googlemaps/js-api-loader` is not installed, Google's own library failed to load, or Google failed the request (a key it does not accept, a quota spent, the network). The drop is refused as `unavailable` all the same, since nothing can be opened.\n\nA reader cannot tell this from a place with no imagery, so it is the developer's signal.\nWithout it, a missing peer is written once to the console of a development build, and a\nfailed load or a failed request is reported nowhere.",
        name: "onError",
        required: false,
        seam: "handler",
        type: "(error: Error) => void",
      },
      {
        description:
          "Replaces the glyph in the handle and in the ghost.\n\nFor an application with artwork of its own, such as a pegman image its users already\nrecognise.",
        name: "children",
        required: false,
        type: "ReactNode",
      },
      {
        description: "",
        name: "onDrop",
        required: false,
        seam: "handler",
        type: "(lngLat: [number, number]) => boolean",
      },
      { description: "", name: "onOpen", required: false, seam: "handler", type: "undefined" },
    ],
    propsTypeName: "StreetViewControlProps",
    summary: "StreetViewControl is the handle a user drags onto the map to look around.",
  },
  StudioBillingGateProvider: {
    description:
      "`seam` is handed on as it is given, and held by nothing here: a surface reads the gate again\nwhenever the function is a new one. A seam declared once, at module scope or under\n`useCallback`, renders no surface of its own accord.",

    importPath: "@mountaya/studio-react/headless",
    name: "StudioBillingGateProvider",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description: "The surfaces that read the gate.",
        name: "children",
        required: true,
        type: "ReactNode",
      },
      {
        description: "The host's answer per organization, handed on as it is given.",
        name: "seam",
        required: true,
        type: "StudioBillingGateSeam",
      },
    ],
    propsTypeName: "StudioBillingGateProviderProps",
    summary:
      "StudioBillingGateProvider supplies the host's billing binding to every surface beneath it.",
  },
  StudioControls: {
    description:
      "The stack reads top to bottom as zoom → layers/preferences → download/directions → Street\nView → geolocate → compass, putting the primary navigation affordance first. Native map\ngestures stay on the canvas underneath; only the buttons are here, and no MapLibre control\nwidget is mounted.\n\n**Every press goes through the bus, never to the map.** Each control takes an `on*` that\nsuppresses its own default entirely, so a click and an agent's tool call animate the camera\nalong one path rather than two that have to be kept agreeing. That is why the whole stack\ncomposes as `children` with every built-in flag off: this needs its own handler, its own\ngating flag and its own panel surfaces per control, and the flags express none of those.\n\nThe floating panels are positioned by whoever mounts them, so the two panel toggles carry no\npanel of their own. `MapControls` still reads the open-panel state, because that is what\ntells it a press elsewhere in the stack has a panel to dismiss; it never writes it, and the\ndismissal routes through the bus like every other mutation.\n\n**The run lock is read once and threaded to every control**, so the whole stack goes inert\ntogether while an agent run drives. Reading it in one place is what keeps a control added\nhere from shipping live: it takes the same `disabled` its siblings do.",

    importPath: "@mountaya/studio-react",
    name: "StudioControls",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      { description: "", name: "config", required: true, type: "StudioConfig" },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "embedded",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        defaultValue: "null",
        description:
          "The itinerary the download and directions controls act on when none is active.\n\nA failed or unresolved detail read would otherwise leave both a dead button while the\ndialogs behind them can still answer for the configured id.",
        name: "fallbackItineraryId",
        required: false,
        type: "string | null",
      },
      {
        control: { kind: "string" },
        description:
          "The Google Maps key the Street View handle bills its coverage check to.\n\nAbsent, the control renders nothing at all rather than a handle whose every drag ends in\nsilence. That gate is the control's own: this passes the key and lets it decide.",
        name: "googleMapsApiKey",
        required: false,
        type: "string",
      },
      { description: "", name: "streetViewHandle", required: false, type: "ReactNode" },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
    ],
    propsTypeName: "StudioControlsProps",
    summary:
      "StudioControls is Mountaya's map control stack: the buttons that sit over a map and change what it shows, in the order a reader meets them.",
  },
  StudioCopilotProvider: {
    description: "",

    importPath: "@mountaya/studio-react/headless",
    name: "StudioCopilotProvider",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description: "The surfaces that read the co-pilot's availability.",
        name: "children",
        required: true,
        type: "ReactNode",
      },
      {
        description:
          "The host's answer per organization. Its identity must be stable across renders.",
        name: "seam",
        required: true,
        type: "StudioCopilotSeam",
      },
    ],
    propsTypeName: "StudioCopilotProviderProps",
    summary:
      "StudioCopilotProvider supplies the host's co-pilot binding to every surface beneath it.",
  },
  StudioHeader: {
    description:
      'A component a host PLACES rather than a prop on `<MountayaStudio>`, because this package does\nnot own the panel it sits in: where the lockup belongs (above a list, beside a rail, in a\ndrawer) is a layout decision, and the studio has no element there to hang it off. So the host\nplaces it and configures it through `appearance.header`, which is the same split\n`i18n.languageMark` has.\n\nWhat it renders, from `appearance.header` and the viewer\'s `white-label` entitlement:\n\n| `appearance.header` | `white-label` | renders                                         |\n| ------------------- | ------------ | ----------------------------------------------- |\n| `undefined`         | either       | the Mountaya mark, "Mountaya", "Map Studio"     |\n| a node or a brand   | yes          | the override                                    |\n| a node or a brand   | no           | the Mountaya default (the override is refused)  |\n| `null`              | yes          | nothing                                         |\n| `null`              | no           | the Mountaya default                            |\n\nThe gate is the SAME entitlement that takes the Mountaya mark off the video preview and the\nframes the recorder composites, and it is read here rather than resolved by the host for the\nreason `PreviewWatermark`\'s is: the promise is "a white-label studio carries no Mountaya\nmark", and a second mount that forgot to ask would break it in silence.\n\nRead LIVE, through `useStudioViewer` rather than through `useStudioEntitlement`. That hook\nthrows where no studio is mounted above it, which is a correct answer for a verb an agreement\ngates and the wrong one here: a header placed outside a studio must still draw its own name.\nThere, and in a studio given no `viewer`, it reads the inert viewer, which holds no\nentitlement, so it keeps the default, as a viewer whose agreement does not cover\nwhite-labelling should see.\n\nIt takes no props, not even `className`. There is no positioning problem here for a hatch to\nsolve (a host that needs this somewhere particular wraps it in their own box), and\n`apiShape.test.tsx` keeps that list closed for the reason it records.',

    importPath: "@mountaya/studio-react",
    name: "StudioHeader",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "StudioHeader is the brand lockup a studio's own chrome carries, and the seam a host replaces it through.",
  },
  StudioHostRegistryProvider: {
    description:
      "{@link MountayaStudioHost } mounts one itself, so a consumer taking the assembled studio never\nwrites this. It is here for the consumer who does NOT (one wiring their own map lifecycle,\ntheir own video surface or their own collaboration session) and who still wants the\npublished surfaces that read this seam to work. It is the symmetric companion of\n`StudioProvider`: that one publishes the bus, the store and the grants; this one publishes\nwhat a child registers back INTO.\n\nMounting one inside a host REPLACES the host's, which is a context behaving like a context,\nand is what makes it usable in a test that drives one surface without assembling a studio\naround it.",

    importPath: "@mountaya/studio-react",
    name: "StudioHostRegistryProvider",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description: "The surfaces that register into the registry.",
        name: "children",
        required: true,
        type: "ReactNode",
      },
      {
        description:
          "The registry the surfaces beneath read and register into, in place of a host's.",
        name: "registry",
        required: true,
        type: "StudioHostRegistry",
      },
    ],
    propsTypeName: "StudioHostRegistryProviderProps",
    summary: "StudioHostRegistryProvider publishes a registry to everything beneath it.",
  },
  StudioNavigationBlockerProvider: {
    description:
      "`blocker` is handed on as it is given, and held by nothing here: a guard reads the binding\nagain whenever the function is a new one. A binding declared once, at module scope or under\n`useCallback`, renders no guard of its own accord.",

    importPath: "@mountaya/studio-react/headless",
    name: "StudioNavigationBlockerProvider",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description: "The host's router binding, handed on as it is given.",
        name: "blocker",
        required: true,
        type: "StudioNavigationBlocker",
      },
      {
        description: "The guards that read the binding.",
        name: "children",
        required: true,
        type: "ReactNode",
      },
    ],
    propsTypeName: "StudioNavigationBlockerProviderProps",
    summary:
      "StudioNavigationBlockerProvider supplies the host's router binding to every guard beneath it.",
  },
  StudioNotifyProvider: {
    description:
      "`notify` is handed on as it is given, and held by nothing here: a surface that reads it\nrenders again whenever the object is a new one, so a host declares it once.",

    importPath: "@mountaya/studio-react/headless",
    name: "StudioNotifyProvider",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description: "The surfaces that raise a notification.",
        name: "children",
        required: true,
        type: "ReactNode",
      },
      {
        description: "The host's notification surface, handed on as it is given.",
        name: "notify",
        required: true,
        type: "StudioNotify",
      },
    ],
    propsTypeName: "StudioNotifyProviderProps",
    summary:
      "StudioNotifyProvider supplies the host's notification surface to everything beneath it.",
  },
  StudioOrganizationsProvider: {
    description: "",

    importPath: "@mountaya/studio-react/headless",
    name: "StudioOrganizationsProvider",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description: "The surfaces that read the roster.",
        name: "children",
        required: true,
        type: "ReactNode",
      },
      {
        description: "The host's roster: the organizations the viewer reads and writes.",
        name: "organizations",
        required: true,
        type: "StudioOrganizations",
      },
    ],
    propsTypeName: "StudioOrganizationsProviderProps",
    summary: "StudioOrganizationsProvider supplies the host's roster to every surface beneath it.",
  },
  StudioPluginsProvider: {
    description:
      "`MountayaStudioHost` mounts this UNCONDITIONALLY, which is the deliberate difference from the\nseam providers beside it. Those mount only when given a value so a nested host inherits an\nenclosing ANSWER (a notifier, a router), and that is right for an answer and wrong for a\nREGISTER TARGET: inheriting one would make `plugins={{ install: [] }}` indistinguishable from\nomitting it, and would let an install under a nested host escape into the enclosing one. It is\nthe same argument `StudioHostContext` already makes for the bus.",

    importPath: "@mountaya/studio-react/headless",
    name: "StudioPluginsProvider",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description: "The surfaces that read the registry.",
        name: "children",
        required: true,
        type: "ReactNode",
      },
      {
        description:
          "The registry the host installs plugins into, and how it reports a refused one.",
        name: "plugins",
        required: true,
        type: "StudioPluginsBinding",
      },
    ],
    propsTypeName: "StudioPluginsProviderProps",
    summary: "StudioPluginsProvider supplies the registry to everything beneath it.",
  },
  StudioPortalScope: {
    description:
      "Mount it inside the studio's root, around the studio's content. `<MountayaStudioHost>` mounts\none. A portalled surface is drawn outside the tree that styles it, so it has to land in a node\nunder the root that carries the tokens and the colour scheme.\n{@link mountayaStudioTheme } names that node by class, which\nMantine resolves against the whole document: the first match, whichever studio it belongs to.\nWith two studios on one page, an editor beside an embed preview say, the second studio's\nsurfaces are then drawn under the first one's scheme and tokens. The scope makes a node of its\nown inside the one that carries the class, and hands every surface inside it that node, so\neach studio's surfaces stay in its own root. That holds for two studios in one React root, in\ntwo roots, and in two roots hydrated from a server's markup.\n\nA root with no scope keeps the class the theme states, which is enough for a page that draws\none studio in the document itself.\n\nThe node is handed over as an element and never named by a selector. A selector is resolved\nagainst the document, which reaches into no shadow root and no other document, and Mantine\ndraws a surface whose selector matches nothing in a node it attaches nowhere. An element is\nfound by nobody, so a studio drawn inside a shadow root keeps its surfaces inside it.\n\nThe scope states the element in a theme it builds itself: the enclosing theme, with the\ntarget of `Portal` restated beside whatever defaults that theme states for it. It is handed\nto no `MantineThemeProvider`, because Mantine folds a nested theme into the enclosing one\nmember by member, and an element folded over the class the published theme states does not\nsurvive it. A theme nested inside the scope folds over this one as over any other: a default\nit states for `Portal` is added, and a selector it states as the target is the one its own\nsurfaces follow. An element stated there does not survive the fold, so a tree inside the\nstudio that sends its surfaces elsewhere names its node by a selector.",

    importPath: "@mountaya/studio-react",
    name: "StudioPortalScope",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description:
          "The studio's own content: every surface inside it portals into this scope's node.",
        name: "children",
        required: false,
        type: "ReactNode",
      },
    ],
    propsTypeName: "StudioPortalScopeProps",
    summary:
      "StudioPortalScope gives one studio a portal node of its own, and points every menu, modal and tooltip drawn inside it at that node.",
  },
  StudioProvider: {
    description:
      "It composes WITH `MountayaElementsProvider` rather than replacing it: a studio needs a map,\nglyphs and copy too, and duplicating those here would give a consumer two places to set an\nicon table. Mount both; order does not matter.",

    importPath: "@mountaya/studio-react/headless",
    name: "StudioProvider",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description: "What it looks like: the scheme, the density, the tokens, the canvas paint.",
        name: "appearance",
        required: false,
        type: "StudioAppearanceConfig",
      },
      {
        description:
          "Where the pictures come from: the glyph tables, the relief raster, the artwork seams.",
        name: "assets",
        required: false,
        type: "StudioAssetsConfig",
      },
      {
        description:
          "What the studio does, and what you do instead: the seams, the router, the peer names.",
        name: "behavior",
        required: false,
        type: "StudioBehaviorConfig",
      },
      {
        description: "The surfaces that read the studio.",
        name: "children",
        required: true,
        type: "ReactNode",
      },
      {
        description:
          "What the studio is showing and to whom, plus the bus, the context and the store.",
        name: "data",
        required: false,
        type: "StudioProviderDataConfig<TCtx>",
      },
      {
        description:
          "Which words, and which regional conventions: the language, the locale, both bundles.",
        name: "i18n",
        required: false,
        type: "StudioI18nConfig",
      },
    ],
    propsTypeName: "StudioProviderProps",
    summary: "StudioProvider supplies the studio seams to everything under it.",
  },
  StudioSectionBoundary: {
    description:
      "A caught error is held until the retry is pressed, whatever the boundary wraps by then. A\nmount whose content changes (one panel for another, one itinerary for the next) gives the\nboundary a `key` that names what it shows, so the error leaves with the surface that threw.",

    importPath: "@mountaya/studio-react",
    name: "StudioSectionBoundary",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description: "The surface a render error is contained to.",
        name: "children",
        required: true,
        type: "ReactNode",
      },
    ],
    propsTypeName: "StudioSectionBoundaryProps",
    summary:
      "StudioSectionBoundary isolates one studio surface (a floating panel, the agent chat, the docked elevation profile) behind an error boundary, so a render error in that surface degrades to a compact retry card in its place rather than unmounting the whole studio: the map, the controls, and the sibling panels stay live. The fallback's retry clears the boundary so a transient failure recovers without a route reload.",
  },
  StudioToggleRow: {
    description:
      "The label IS the Switch's label, so the entire row is the toggle's hit target and a click\nanywhere on it flips the value, while keeping the native checkbox semantics and keyboard focus a\nbare onClick would lose. Its TYPE comes from the Switch as well, since the glyph branch inherits\nrather than sizing itself: the row reads at one font size whether or not it leads with a chip,\nand the density tier is what moves it. The hover tint is a rounded row inset within its\ncontainer's own padding rather than a full-bleed band: the row cannot know whether it sits in an\n`xs` dropdown or an `sm` panel, and a component that reaches out through its parent's inset only\nworks in the one place it was measured for.\n\nMemoized because these render as a list: flipping one POI category must repaint\nthat row, not every row.",

    importPath: "@mountaya/studio-react",
    name: "StudioToggleRow",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "boolean" },
        description: "",
        name: "checked",
        required: true,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      { description: "", name: "glyph", required: false, type: "ReactNode" },
      {
        control: { kind: "string" },
        description: "",
        name: "label",
        required: true,
        type: "string",
      },
      {
        description: "",
        name: "onToggle",
        required: true,
        seam: "handler",
        type: "(enabled: boolean) => void",
      },
    ],
    propsTypeName: "StudioToggleRowProps",
    summary:
      "StudioToggleRow is the one full-width on/off line the studio's floating surfaces are built from: the layers panel's enrichments and the POI picker's categories. Written twice, the row would drift onto two densities (a POI list padding every row by a full `xs` and bleeding it past its dropdown's inset beside layer switches with no padding at all), so the picker reachable from the top-left control would read a whole step looser than its two siblings on the right. One component means a density is changed here or not at all.",
  },
  SuggestForm: {
    description:
      "Starting from a candidate (session.draft set) seeds the draft and hands off to the\nunified create/edit panel's staging step (the name, owning organization,\nvisibility, collections, the editable route, and the Create action all live there,\nnot here), so this surface stays generation-only. The shell reads the whole\nsession through one shallow selector and derives the gates during render; every\nmutation flows through the bus, the one mutation surface the agent shares.",

    importPath: "@mountaya/studio-react",
    name: "SuggestForm",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "SuggestForm is the GENERATION body of the suggest surface, sharing the studio form rhythm (a SidenavContentFormBody at the canonical gap): the shared content header backs out by discarding the whole suggest session; the activity chooser (kept first, since generation is gated on it) seeds the first catalog activity once on open; the distance / ascent / speed inputs hold the wire units while displaying the reader's units; the route-type control sits beside the points it governs (revealing the end-point field when point-to-point); the start point field is always shown and the end only for a point-to-point route; the results list renders the candidates; the inline error and, when the route pool is spent for the current constraints, an informational nudge to widen the distance/ascent both sit above the footer; and the SuggestFormActions footer holds the Suggest button (gated until the request-anchoring inputs are set, spelling out the keep/fresh math once candidates are kept, disabled while exhausted) plus, while a candidate is previewed, the filled Start-from CTA above it.",
  },
  UnpublishedPanel: {
    description:
      "That is every surface that acts on the publication but the share link: the exported file, the\nprinted directions, the embed frame and the recorded video are all artifacts made NOW\nout of a snapshot, and there is no snapshot to make them from. The honest answer is\nthis panel rather than a rendered draft: a draft preview would show a working map for\nan embed that serves an empty one, which is how a publisher ends up shipping a broken\nsnippet. It states the cause and offers the single act that fixes it, so the dead end\nis one click deep rather than a mystery. The share link stays usable, and\ncarries {@link PublicationNotice } instead.\n\nIt leads with the same badge the editor's own surfaces show, so a publisher who has\njust read \"Draft\" in the sidenav meets the same word here rather than having to work\nout that the two are describing one state.\n\nFor a PERSONAL itinerary the panel keeps its shape and drops its act: publishing is not\nmerely unavailable now, it is unavailable at all, so offering the button would make this a\ndead end one click deeper rather than none. The two lines change to name that rule, and the\nhost's `publicationPrompt` takes the button's place: the host attaches its own action there,\nthrough the `billingGate` seam, and the studio writes none of it.\n\nPublishing writes the itinerary, so the button is offered only to a reader who may edit it: a\nwrite role in the organization that owns it, which holds Map Studio. A member whose role reads\nonly reads why the surface is empty and is offered no act, and so does a reader who does not\nhold the `publish` entitlement, whose organization's agreement does not include Map\nPublishing.",

    importPath: "@mountaya/studio-react",
    name: "UnpublishedPanel",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "itineraryId",
        required: true,
        type: "string",
      },
      {
        control: { kind: "string" },
        description:
          "The itinerary's owning organization, which decides whether publishing is offered at all.\n\nThe FACT, not the conclusion: the panel asks `usePublishableScope` itself rather than\ntaking a `canPublish` boolean, exactly as ItineraryActionsMenu takes `organizationId`\nrather than `canEmbed`. A host passing the conclusion would be a second place the rule\nlives, and a host that got it wrong would be silently trusted.\n\nNull is an answer: a personal itinerary names no organization, and the panel states the\nrule in the act's place. Omitted (or the empty string a surface yields mid-read) is no\nanswer yet, and the panel offers publishing: an unanswered question is not a refusal, and\nit is also the right default for a host that states no owner at all.",
        name: "organizationId",
        required: false,
        type: "string | null",
      },
    ],
    propsTypeName: "UnpublishedPanelProps",
    summary:
      "UnpublishedPanel is what a surface shows in place of its own body when the itinerary has never been published and the thing it produces cannot exist without one.",
  },
  VideoExportWizard: {
    description:
      "A video belongs to an organization, so over a PERSONAL itinerary the wizard shows no step: only\nthe sentence that says so, and its close ({@link PersonalItineraryNotice}). While the itinerary's\nrecord is loading, the wizard is drawn as for any other. Mounting useVideoRouteSync here feeds\nthe wizard its route the moment the exported itinerary's detail loads, independent of the\nstudio's active itinerary, which is what makes the preview fly and the preset radios land;\nuseVideoProfileSeed seeds the profile overlay's units + language from the viewer's preferences on\nopen; the shell's useVideoItinerarySubject reads the exported itinerary's name for the header (a\nfree hit on the route-sync cache). All run once per open since the wizard subtree is keyed by\nitinerary id.",

    importPath: "@mountaya/studio-react/video",
    name: "VideoExportWizard",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        description:
          "What to do when a director run reports that an organization's AI configuration is stale.\n\nOptional, because its absence is an answer rather than a gap: a host with no cache has\nnothing to mark stale, and the configuration is re-read on the next natural fetch.",
        name: "invalidateAiConfig",
        required: false,
        type: "(organizationId: string) => void",
      },
      {
        control: { kind: "string" },
        description:
          "The itinerary the wizard exports: the one its plan, its route and its recording are of.",
        name: "itineraryId",
        required: true,
        type: "string",
      },
      {
        description:
          "The host's own remedy for a director call that failed, which the model reads after the playbook's recovery for that tool and code.\n\nOptional, because its absence is an answer rather than a gap: the model then reads the\nplaybook's recovery alone. Read when a call fails, so one written inline costs nothing.",
        name: "recoveryAddendum",
        required: false,
        type: "RecoveryAddendum",
      },
      {
        description:
          "Overrides the words this surface says, for this surface only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest of\nthe section alone, and carried down to the rows and pickers this surface mounts. Scoped to\nthe sections it renders: a key it never says is a type error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "VideoExportWizardStrings",
      },
      {
        description: "The client the preview map reads its tiles and its data through.",
        name: "mapClient",
        required: true,
        type: 'MountayaMapOptions["client"]',
      },
      {
        description:
          "The host's rewrite of each tile request the preview map makes, where it has one.",
        name: "transformRequest",
        required: false,
        type: "TileRequestTransform",
      },
    ],
    propsTypeName: "VideoExportWizardProps",
    summary:
      "VideoExportWizard is the export-as-video wizard entry, gating the AI director on the `video-organization-modes` entitlement. For a viewer granted it, the DirectorChatProvider around the shell runs a director session, so the Camera step hosts the director chat + the mode-cards scrim and the preview stage hosts the directing indicator, and the shell reacts to the director's run; any other viewer gets the presets + preview + record shell with no director session ever instantiated (mirroring the hidden AI Director mode card on the Camera step). The entitlement is read reactively, so a change while the wizard is open reveals or hides the director live, and the shell stays mounted across it: the preview map and the wizard's own state are no part of the director. The tool layer's gate on the director verbs is the real boundary; this mount gate is the matching UI surface.",
  },
  ViewerMapView: {
    description:
      "Its client is `MapLibreViewClient`, which refuses every edit verb and builds no edit engine, so\nthis component loads neither `terra-draw` nor `@maplibre/maplibre-gl-directions`, and a bundle\nthat mounts it alone keeps none of their code. Its entry names them all the same, through\n{@link EditorMapView }, so they are installed for a build to resolve. An editor mounts\n{@link EditorMapView }, which takes the same props.",

    importPath: "@mountaya/elements-react/editor",
    name: "ViewerMapView",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description:
          "Names the map container as a labelled `region` landmark for assistive technology.\n\nThis package ships no i18n runtime, so the label is the caller's, in the reader's language.\nOmitted (a secondary mini-map inside a surface already labelled), the container carries no\nlandmark role.",
        name: "ariaLabel",
        required: false,
        type: "string",
      },
      {
        description:
          "The class of the container the map attaches its canvas to. Follows every render.\n\nA container given a class is sized by that class: the fill described under `style` is left\nout, since an inline declaration outranks every rule a class carries. A string that names\nno class, empty or blank, is read as none, and the fill stays.",
        name: "className",
        required: false,
        seam: "style",
        type: "string",
      },
      {
        description:
          "The SDK client the map fetches its own specifications through.\n\nThe credential, the retry policy and the Tile API base are the application's to compose, so\nit hands over the instance it already holds, as it hands over `transformRequest`.\n\nA seed, read once as the map is built: a client whose session token is a function carries\neach rotation to every request the map makes, and a different client needs a new `key`.",
        name: "client",
        required: true,
        type: 'MountayaMapOptions["client"]',
      },
      {
        description:
          "The base layer the map opens on, for an application that already holds the layers manifest.\n\nA seed, read once as the map is built. Afterwards the base layer changes through\n`MapClient.setBaseTerrain`, never through this prop.",
        name: "initialBaseTerrain",
        required: false,
        type: "BaseTerrainConfig",
      },
      {
        description:
          "The camera the map opens on: a center as `[longitude, latitude]` and a zoom.\n\nA seed, read once as the map is built. Afterwards the camera moves through the client. A\n`#view` hash in the address wins over it while `withUrlHash` is on, since that hash is a\nview somebody shared.",
        name: "initialViewport",
        required: false,
        type: "MapViewport",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the reader pans, zooms and rotates the map directly.\n\n`false` hands the camera to the surface that owns the map (a mini-map beside a street-level\nview, for instance): it then moves only through the `MapClient` it reports. A seed, read\nonce as the map is built.",
        name: "interactive",
        required: false,
        type: "boolean",
      },
      {
        description:
          "The glyph set this map's markers draw from.\n\nA seed, read once as the map is built. An application with two glyph families in play (a\nlicensed set for its own surfaces and a plain one for an embedded map) names the one it wants\nhere.",
        name: "markerIcons",
        required: false,
        type: "IconProvider",
      },
      {
        control: { kind: "number" },
        description:
          "How many tiles MapLibre keeps in memory, as a whole number of tiles.\n\nA surface that loads tiles ahead and then replays the same views, as a video recording does,\npasses a large value so those tiles are still held when the replay reaches them. A seed, read\nonce as the map is built: MapLibre takes it at construction alone.",
        name: "maxTileCacheSize",
        required: false,
        type: "number",
      },
      {
        description:
          "Called when the API refuses one of the map's own requests with a 401, a 402 or a 403: a tile, a sprite, a glyph or a style.\n\nThe refusal carries the status and the address refused, so an application words a sign-in\napart from a missing agreement. Attached as the map is built, like `onError`, and before any\nclient is handed out: a base style the API refuses is refused before the style loads, which\nmay keep `onClientReady` from ever being called, and `MapClient.onTilesAccessRequired` can\nonly be subscribed on a client that was handed out. A refusal is never also reported to\n`onError`.\n\nRead at each report, so a new handler takes effect without rebuilding the map.",
        name: "onAccessRequired",
        required: false,
        seam: "handler",
        type: '(refusal: MountayaMapEvents["accessrequired"]) => void',
      },
      {
        description:
          "Called as the surface unmounts, just before the client it handed out through `onClientReady` is disposed, so the application releases its reference to it.\n\nPaired with `onClientReady`: a surface torn down before its style loaded handed out no\nclient, so it is torn down without this being called. React StrictMode builds and tears\ndown such a surface on every development mount.\n\nRead as the surface unmounts, so a new handler takes effect without rebuilding the map.",
        name: "onClientDestroy",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      {
        description:
          "Called once with the `MapClient` this map is driven through, when its style can take sources and layers.\n\nNever called for a surface that unmounts before then, and never for a map whose base style\nwas refused or failed: `onAccessRequired` and `onError` say why such a map stays blank. The\nclient is disposed as the surface unmounts, after `onClientDestroy`, so a reference kept past\nthat point drives nothing.\n\nRead when the map reports ready, so a handler written inline rebuilds nothing and the one of\nthe latest render is the one called.",
        name: "onClientReady",
        required: true,
        seam: "handler",
        type: "(client: MapClient) => void",
      },
      {
        description:
          "Called with each failure the map reports that no call of yours could reject: an error MapLibre raised, such as a style that does not parse or a tile the server failed on.\n\nAttached as the map is built, before it makes its first request, so a failure that lands\nbefore `onClientReady` is heard, and so is one that keeps it from ever being called. It\nreports and replaces nothing. A refusal of access goes to `onAccessRequired` instead, and a\nmap that cannot be built at all (no WebGL context) throws to the nearest error boundary,\nwhich is React's own contract for a failure while mounting.\n\nRead at each report, so a new handler takes effect without rebuilding the map.",
        name: "onError",
        required: false,
        seam: "handler",
        type: "(error: Error) => void",
      },
      {
        description:
          "Called with the `MountayaMap` the client wraps, beside `onClientReady` and from the same readiness, so the two land together.\n\nThe elements read the map (`MountayaElementsProvider` takes it and every control drives it)\nwhere an application's own surfaces read the `MapClient`, so a surface that mounts both takes\nboth from here.\n\nRead when the map reports ready, as `onClientReady` is.",
        name: "onMapReady",
        required: false,
        seam: "handler",
        type: "(map: MountayaMap) => void",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the rendered frame stays readable after the browser has shown it.\n\n`true` for a surface whose canvas is read back: a video recording, frame by frame, and a\nsession recorder that samples the page. It costs a copy of the frame on every render, so a\nsurface nothing reads leaves it off. A seed, read once as the map is built: MapLibre takes it\nat construction alone.",
        name: "preserveDrawingBuffer",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Rewrites each request the map makes for a tile, a sprite, a glyph or a style, so it carries the application's own credential.\n\nWhether the map takes one is decided at the mount: supplied then, it replaces the transform\nthe map derives from `client`, and omitted then, the derived one stands for the map's life.\nThe function itself is read at each request, so a transform rebuilt when a credential rotates\nreaches the next tile, and a render that omits the prop keeps the last one supplied.",
        name: "transformRequest",
        required: false,
        type: "TileRequestTransform",
      },
      {
        description:
          'CSS for the container element.\n\n`style` is the DOM\'s word here, not MapLibre\'s: the map style goes on the terrain seeds\nabove. Supplying it replaces the fill and is never merged with it, so a caller sizing the map\ntakes over the sizing entirely, and one who only wants a border writes\n`{ height: "100%", width: "100%", border: "1px solid" }`.',
        name: "style",
        required: false,
        seam: "style",
        type: "ElementStyle",
      },
      {
        description:
          "Builds the recording subsystem this map records and previews through.\n\nAbsent, the map carries no video encoder and the client answers that recording is\nunsupported, which is what a map showing a route should cost. A surface that exports one\nsupplies the seam, reading what it needs off the map it is handed: `VideoRunEngine` from\n`@mountaya/elements/video` takes the raw map and the shared event engine, both of which the\nmap publishes.\n\nA seed, read once as the map is built: the recorder attaches a render listener to the map it\nwas built over, so a factory that changed identity later could not replace it.",
        name: "video",
        required: false,
        type: "(map: MountayaMap) => VideoSeam",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the map draws its own credit line: MapLibre's attribution control, holding the Mountaya credit beside what each tile source declares.\n\n`false` only for a host that draws the same credits itself, which then answers for the tile\nlicences: a map that shows tiles and credits nobody breaks them. A seed, read once as the\nmap is built.",
        name: "withAttribution",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the map keeps its camera in the page's address, as `#view=<zoom>/<lat>/<lng>/<bearing>/<pitch>`, so a view is shared by its link and restored on load.\n\nIt writes `location.hash` at each camera move and replaces the history entry, so a page\nturns it on where it owns the address: one map per page, the one whose view a link shares.\nA seed, read once as the map is built.",
        name: "withUrlHash",
        required: false,
        type: "boolean",
      },
    ],
    propsTypeName: "ViewerMapViewProps",
    summary:
      "ViewerMapView owns one map's lifecycle and hands out the `MapClient` a viewer drives it through: it draws an itinerary and the tile layers, moves the camera, answers a map query and records through a `VideoSeam`.",
  },
  WarningAlert: {
    description:
      'An orange alert carrying the warning glyph and the caller\'s message, so every "heads up" (a\nkey shown only once, an irreversible switch) reads with one treatment. The hue is orange\nrather than yellow deliberately: it is the same warning hue a header notice uses, so an\nin-place caution and a banner read as the same class of thing.',

    importPath: "@mountaya/studio-react",
    name: "WarningAlert",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      { description: "", name: "children", required: true, type: "ReactNode" },
      {
        control: { kind: "string" },
        description: "",
        name: "title",
        required: false,
        type: "string",
      },
    ],
    propsTypeName: "WarningAlertProps",
    summary:
      "WarningAlert is the in-place caution surface: the third member of the family {@link ErrorAlert } and {@link FormErrorAlert } open, and the one that reports something that has NOT gone wrong yet.",
  },
  WaypointCard: {
    description:
      "**It is the surface a reader meets when they click a pin**, and the one an author is shown while\nthey write the place, so the two cannot disagree about what a place says. An embed visitor, a\nshare-link recipient and the studio's own editor all draw this component, which is the whole\nreason it is published rather than assembled three times.\n\nThe header is the PLACE rather than the card: the place's own mark in the place's own colour, its\nname, and beneath it either the author's description or, for the common case of a place captured\nfrom a POI and never described, a sentence assembled from what it records. See `waypointHeadline`\non `@mountaya/elements/profile`.\n\n**Every section renders only when it holds something**, and a place that declares nothing at all\nsays so in one line rather than showing five empty headings.\n\nIt renders no map and fetches nothing. Give it a waypoint and it draws one; `itineraryWaypoint`\non `@mountaya/elements/data` maps a stored record onto that shape, and {@link useWaypointCard}\nderives the same content for a caller drawing their own.\n\nA root like the profile and the tables: it merges the provider's `tokens` into its own style,\nunder its `style` prop, so the `--mtya-waypoint-card-*` properties are set once on the provider.",

    importPath: "@mountaya/elements-react/elements",
    name: "WaypointCard",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        description:
          "Writes a cut-off's instant, an ISO 8601 string, in the card's timing rows.\n\nWithout it no cut-off is shown: an instant needs a locale and a time zone, which are the\napplication's. `<ItineraryProfile>` and `<ItineraryWaypointTable>` take the same function.",
        name: "formatCutoffAt",
        required: false,
        type: "(iso: string) => string",
      },
      {
        defaultValue: "3",
        description:
          "The level of the heading the card's name is written in, so the card fits the outline of the page it is placed in: `2` under a page's `<h1>`, `4` inside an `<h3>` section. Any other value is read as the default.",
        name: "headingLevel",
        required: false,
        type: "2 | 3 | 4 | 5 | 6",
      },
      {
        control: { kind: "string" },
        description:
          'The name shown in the header.\n\nTaken as a prop rather than read off the waypoint, because an unnamed place is named by WHERE\nIT SITS among its siblings ("Water #2"), and only the list holding those siblings can resolve\nthat.',
        name: "name",
        required: false,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "true",
        description:
          "Whether the card shows its photo grid. `false` hides it, for a card in a column too narrow to show one honestly.",
        name: "withPhotos",
        required: false,
        type: "boolean",
      },
      {
        description: "Overrides the words this element renders, for this element only.",
        name: "strings",
        required: false,
        type: "WaypointCardStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
      {
        description: "Units, slope notation and the locale to format in.",
        name: "units",
        required: false,
        type: "UnitFormatOptions",
      },
      {
        description: "The place: a `Waypoint`, the element shape, not the wire's own record.",
        name: "waypoint",
        required: true,
        type: "Waypoint",
      },
    ],
    propsTypeName: "WaypointCardProps",
    summary:
      "WaypointCard states one place: what it is called, what it is, and everything it records.",
  },
  WaypointIconCircle: {
    description: "",

    importPath: "@mountaya/studio-react",
    name: "WaypointIconCircle",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description: "",
        name: "color",
        required: true,
        type: "string | null",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "colorless",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        control: {
          kind: "enum",
          options: [
            "circle-warning",
            "megaphone",
            "bed",
            "apartment-building",
            "camper-van",
            "circle-check",
            "parking-sign",
            "medical-cross",
            "droplet",
            "eye",
            "flag",
            "flag-start",
            "flag-end",
            "hut",
            "circle-info",
            "map-pin",
            "camping",
            "trophy",
            "cutlery",
            "bus",
            "train",
            "cable-car",
            "charging-station",
            "gate",
            "toilet",
            "shower",
            "trash",
            "shop",
            "picnic",
            "helicopter",
            "signal",
            "climbing",
            "ladder",
          ],
        },
        description: "",
        name: "icon",
        required: true,
        type: "WaypointIcon | null",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "itineraryId",
        required: false,
        type: "string",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "label",
        required: true,
        type: "string",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "session",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description: "",
        name: "waypointId",
        required: true,
        type: "string",
      },
    ],
    propsTypeName: "WaypointIconCircleProps",
    summary:
      "WaypointIconCircle is the colored-disc button at the left of a waypoint editor row: it renders the waypoint's icon centered inside a circle filled with the waypoint's color, mirroring the on-map sprite the tile renderer paints. Clicking it opens a popover with two side-by-side controls (the lazy-loaded ColorPicker on the left and the icon-grid picker on the right), so a single affordance covers both appearance dimensions in one compact row and the popover never loads its picker widgets until needed. While the user drags the color slider the disc previews the new color locally; the chosen color persists once the interaction settles (a swatch click or end of drag). The icon picker persists on click. Both writes go through the bus via the supplied handlers: the circle never touches the store directly.",
  },
  WaypointList: {
    description:
      '**The fallback names are counted over what is RENDERED, not over what is saved.** A waypoint\ncarries no name until someone gives it one, and the fallback numbers it among its same-icon\nsiblings, so the draft row a user is about to name has to be in that count, or it reads\n"Water #2" until it commits and then jumps to "Water #3".',

    importPath: "@mountaya/studio-react",
    name: "WaypointList",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        defaultValue: "null",
        description: "",
        name: "agentLockHint",
        required: false,
        type: "string | null",
      },
      {
        description:
          "The add control, rendered below the list. Omitted on a surface that cannot add.",
        name: "addControl",
        required: false,
        type: "ReactNode",
      },
      { description: "", name: "emptyState", required: false, type: "ReactNode" },
      {
        description:
          "Names one icon in the reader's language, for the fallback a nameless waypoint shows.",
        name: "iconLabel",
        required: true,
        type: '(icon: ItineraryViewSource["waypoints"][number]["icon"]) => string',
      },
      {
        control: { kind: "string" },
        description: "",
        name: "label",
        required: true,
        type: "string",
      },
      {
        description:
          "What a single click on a row's body opens, replacing the move session it otherwise starts.\n\nPer row for the reason `renderMenuItems` is: whatever it opens has to know which place. A\nsurface that leaves it out keeps the move, which is what a list with no dialog behind it\nshould do.",
        name: "onOpen",
        required: false,
        seam: "handler",
        type: "(row: WaypointListRow) => void",
      },
      {
        description:
          "A refused verb of a row, handed to the host to report where it belongs on their surface.",
        name: "onFailure",
        required: false,
        seam: "handler",
        type: "(failure: WaypointRowFailure) => void",
      },
      {
        description: "Resolves a peer's id to the sentence a held row speaks with.",
        name: "peerLockHint",
        required: false,
        type: "(holderId: string) => string | null",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "readOnly",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Draws the disk of one row.\n\nCalled for a row when that row renders, and a row renders when its own facts change or when\nthe reference of a prop the list hands every row does, this slot's among them. It is not\ncalled at each render of the list. So a slot that reads a value outside its arguments\nchanges identity with that value, as a `useCallback` that lists it does: a slot whose\nreference stands keeps what it drew.",
        name: "renderIconCircle",
        required: true,
        type: "(row: WaypointListRow) => ReactNode",
      },
      {
        description:
          "Host badges for one row, such as who is looking at the place.\n\nCalled when `renderIconCircle` is, under the same rule: a slot that reads a value outside\nits argument changes identity with that value.",
        name: "renderIndicators",
        required: false,
        type: "(waypointId: string) => ReactNode",
      },
      {
        description:
          "Host menu items for one row, above its own verbs.\n\nPer row rather than one node, for the reason `renderIconCircle` is: an item acting on a place\nhas to know which place. A draft row is passed too: a host offering an action a waypoint\ncannot answer until it is saved decides that itself, from `row.isDraft`.\n\nCalled when `renderIconCircle` is, under the same rule: a slot that reads a value outside\nits argument changes identity with that value.",
        name: "renderMenuItems",
        required: false,
        type: "(row: WaypointListRow) => ReactNode",
      },
      {
        control: { kind: "string" },
        description: "The itinerary whose waypoints the list renders.",
        name: "itineraryId",
        required: true,
        type: "string",
      },
      {
        description: "The SAVED waypoints, in position order.",
        name: "waypoints",
        required: true,
        type: 'ItineraryViewSource["waypoints"]',
      },
    ],
    propsTypeName: "WaypointListProps",
    summary:
      "WaypointList is the studio's waypoint editor: the saved waypoints in position order, the in-flight create draft appended while one is open, and the host's add control below.",
  },
  ZoneActionsMenu: {
    description:
      "A hand-drawn zone has one action, Delete, which stays open whatever the zone's scope takes. A\ncomputed zone offers one more:\n\n- Reshape by hand arms the draw engine over the computed shape. It is here, and\n  behind a confirm, precisely BECAUSE it is destructive in a way a drag does not\n  look: the drawn shape replaces the query one-way, and the zone can never be\n  refreshed again. A computed zone therefore opens unarmed, and this is the only\n  way to start reshaping one. In a scope that takes no zone write (the personal space, or an\n  organization whose agreement does not include Map Studio) it is disabled with the reason\n  the zone form gives.\n\nA published zone offers Unpublish in a band of its own directly above Delete, as the\nitinerary menu does: it withdraws the copy the collection embeds and share links serve, which\nPublish puts straight back, so it carries no red. The prompt that states the consequence is the\nbus's, which the verb declares.\n\nEvery verb of the menu writes the zone, so each is offered only to a reader who holds the write\ngrant, as the itinerary menu offers its own: a write role in the organization that owns the\nzone, or its authorship for a personal one (`canEditOwnedResource`), read from `organizationId`\nand `createdById` against the host's roster. A reader whose role only reads is offered no\nReshape by hand either, enabled or disabled: the API answers their attempt 403, and a reason\nabout the scope would explain a refusal that is not the one they meet. The scope's\n`writeRefusal` is asked of Reshape by hand alone, since an organization whose agreement does not\ninclude Map Studio still withdraws and deletes its zones. A host that states no roster states\nno roles, and every verb is offered, as the API decides. A menu that would offer nothing at all\nrenders nothing.\n\nRe-running the stored query is NOT offered here. The panel's own reachability\nfields are the live query: editing one and saving regenerates the polygon, which\nleaves a menu entry that recomputes the same parameters with nothing to say for\nitself. The verb still exists on the bus (studio.zone.recompute) for the co-pilot,\nwhich has no form to edit.\n\nThe horizontal three-dot trigger, the red destructive item, and the xs item type\nmatch the itinerary menu so every contextual menu in the studio reads\nidentically. Every action routes through the bus, the one mutation surface.",

    importPath: "@mountaya/studio-react",
    name: "ZoneActionsMenu",
    package: "@mountaya/studio-react",
    parts: [],
    props: [
      {
        control: { kind: "string" },
        description:
          "The id of the user who created the zone, its own `created_by.id`. With `organizationId` it decides whether the reader may reshape, withdraw or delete the zone: a personal zone is its author's alone. Omitted, no author is known, and under a roster the host states a personal zone offers none of the three.",
        name: "createdById",
        required: false,
        type: "string",
      },
      {
        control: { kind: "enum", options: ["danger", "info", "resolved", "warning"] },
        description: "The zone's severity, which a reshape by hand opens its edit in.",
        name: "level",
        required: true,
        type: "ZoneEditLevel",
      },
      {
        control: { kind: "enum", options: ["circle", "freehand", "polygon", "sector", "sensor"] },
        description: "The zone's shape, which a reshape by hand opens its edit in.",
        name: "mode",
        required: true,
        type: "ZoneEditMode",
      },
      {
        control: { kind: "string" },
        description:
          "The organization the zone belongs to, or null for one kept in the personal space: the scope the host's `writeRefusal` is asked about, so a zone whose scope takes no edit offers Reshape by hand disabled, with the reason, and with `createdById` the owner the reader's write grant is read against. Omitted, no scope refuses Reshape by hand and the verb answers for itself.",
        name: "organizationId",
        required: false,
        type: "string | null",
      },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description:
          "Whether the zone has a publication, which puts the Unpublish item in the menu for a reader who holds the write grant. The host answers it because the host holds the record (the panel has the bound zone's `status`). Omitted, by a surface that cannot answer, the item is not rendered rather than offered against an unknown state. It sits in its own band above Delete: the two are the ways a zone stops being available, and the divider between them says only one of the two is permanent.",
        name: "published",
        required: false,
        type: "boolean",
      },
      {
        control: { kind: "boolean" },
        description:
          "Whether the zone is computed from a stored query, the one kind Reshape by hand is offered for, to a reader who holds the write grant.",
        name: "sourced",
        required: true,
        type: "boolean",
      },
      {
        control: { kind: "string" },
        description: "The saved zone the menu acts on.",
        name: "zoneId",
        required: true,
        type: "string",
      },
      {
        description:
          "Overrides the words this surface says, for this surface only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest of\nthe section alone, and carried down to the rows and pickers this surface mounts. Scoped to\nthe sections it renders: a key it never says is a type error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "ZoneActionsMenuStrings",
      },
    ],
    propsTypeName: "ZoneActionsMenuProps",
    summary:
      "ZoneActionsMenu is the three-dots action menu in the zone panel's sticky header, the zone-family counterpart to {@link ItineraryActionsMenu }.",
  },
  ZoneForm: {
    description:
      "The machine is `ZoneFormEngine` in `@mountaya/studio/engines`. It holds the fields, seeds them\nfrom the zone the store binds or from a create's defaults, keeps a create's name following its\nparameters until the reader types one, asks for the reachability preview once the parameters\nhold still, decides when Save is offered and ready, and saves through the one tool the form's\nstate calls for. Every mutation it makes goes through the bus, the one mutation surface: the\nform never writes the store and never touches the draw engine. This component does only what\nReact must: hold the engine for the store's life, hand it the host's seams after every render,\nre-render when its snapshot changes, and lay the fields out.\n\n`method` is the one branch key, and the engine derives it: a create reads the seeded method,\nan EDIT derives it from whether the saved zone carries a `source`. So a computed zone shows the\nparameters it is generated from exactly where a drawn zone shows its locked shape, and neither\nmode needs its own copy of the fields around them.\n\nA DRAWN create is two steps, because there is nothing to describe until a shape exists.\n\n- Step 1, Define: the owning organization, the collections, the method and the shape, then\n  \"Create and draw on map\", which mints a client zone id through the `ids` seam as it stands\n  at the click (the tool context's when it names one, the host's otherwise) and arms the\n  engine. Arming IS the step change, so the step is derived from the session and cannot fall\n  out of sync with the engine. A zone cannot persist empty, so this arms and does not persist.\n- Step 2, Details: the name, the description, the severity and the availability window, then\n  Save. The name is required and the description is not.\n\nA REACHABILITY create is one surface with one action: the origin, activity, measure, budget and\npace, painting a live preview on the map as they change, beside the details. Nothing is armed,\nsince there is nothing to draw, and its extent is a query whose every field is a value.\n\nA create begun from the map INSIDE an open itinerary inherits that itinerary's organization,\ncollections and activity, and shows a line naming them in place of the three pickers (see\n`ZoneInheritedContext`). The sidenav's own zone entry inherits nothing, because no itinerary\nneed be open there at all.\n\nAn EDIT is not stepped: everything about a saved zone sits on one surface, because there is no\norder to impose on decisions already made. Its visibility sits there too, and is the one field\nsaved apart from Save: a flip between private and public is acknowledged and written at once,\nas an itinerary's is, since it changes what every collection embed draws. A create lands\nprivate, as an itinerary does. A DRAWN zone opens with its reshape armed and Save\ncommits the reshaped geometry. A COMPUTED zone opens unarmed and its parameters are editable in\nplace: Save re-sends the source, which the API recomputes from. Reshaping a computed zone by\nhand is a deliberate action in the panel header's menu, behind a warning, because it replaces\nthe query with the drawn shape one-way.\n\nWhile a drawn shape is armed, Undo and Redo sit beside Save and step the draft's history through\n`studio.zone.editUndo` and `studio.zone.editRedo`, each disabled while its side of the history\nis empty. They are the touch and pointer half of the map's own Cmd/Ctrl+Z, and the one way to\ntake back a co-pilot's proposal while focus is in a field the proposal filled.\n\nPermission gate: every input and Save are disabled unless the reader may edit the active zone\n(a write role in its organization, or its creator for a personal zone). The API's write is\nauthoritative; this is the affordance-level mirror, so a read-only viewer cannot begin an edit\nthey could not save. It is computed here, from this render's reader, where the engine learns\nof a changed reader only after the render: the snapshot holds what follows from the store and\nthe fields, and what follows from the reader alone is this render's to say.\n\nEscape over a saved zone is this surface's own: it binds the key on the window and dispatches\n`studio.sidenav.back`, the path a back affordance takes, which asks about an armed reshape\nbefore it tears it down. The binding stands while a saved zone is bound, and a press is taken\nunless a segment edit or a waypoint session is armed behind the zone, which is the nearer thing\nand takes it, or the co-pilot is driving, or a surface nearer the press took it\n(`keyTakenNearer`): a dialog inside the studio (an element with the `dialog` or the\n`alertdialog` role, or a native `<dialog>`), an open list it closed (a select, a combobox, a\nmenu), or a default prevented. A dialog closes on the key itself, so the press that dismisses\nthe confirm the verb raised asks nothing again, and a dialog a host mounts the studio in takes\nno key from it. A create binds nothing, since its Escape is the draw's own cancel. A host that\nmounts the form binds no Escape of its own to the same verb: one press would dispatch it\ntwice.\n\nAnother store is another form: the engine is held per store, so the render that carries a\nreplaced store reads a fresh engine seeded from it and never the previous store's fields.\nNothing is disposed on unmount: unsubscribing parks the engine, which drops the pending\npreview, and which survives StrictMode tearing an effect down and setting it up again with\nthe same object. A server render reads the same snapshot, which a parked engine seeds on the\nread.",

    importPath: "@mountaya/studio-react",
    name: "ZoneForm",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "ZoneForm is the single zone authoring surface in the sidenav: one form that creates a zone by hand or by reachability, and edits either kind afterwards.",
  },
  ZoneSubHeader: {
    description:
      "- A PERSONAL zone shows the gray Personal pill: the personal space publishes nothing.\n- A zone with nothing left to publish shows the lime, checked Up to date pill, which answers\n  nothing. Withdrawal is the zone action menu's, one row above, as the itinerary's is.\n- A reader who may not edit the zone, or who does not hold the `publish` entitlement (an\n  organization whose agreement does not include Map Publishing), is told the state and offered\n  no act.\n- Otherwise the Publish button, standing down with the reason on hover while a collaborator\n  holds the zone, while the co-pilot drives, or while an edit is open that publishing would\n  freeze half-finished.\n\nOne rule differs from the itinerary's band, and it is the verb's own: a reshape of this zone\nthat holds what was saved is no open edit. A hand-drawn zone opens with its reshape armed, so\nthat session is the panel at rest; what stands the act down is what the reader changed and has\nnot saved (a shape, or a field of the zone form, which the form states on the store) or an edit\nof anything else. The verb gives the reader's own lock back for the length of the request, so\nthe server's lock rule is met.\n\nIt renders nothing while no saved zone is bound: a create has no publication yet.",

    importPath: "@mountaya/studio-react",
    name: "ZoneSubHeader",
    package: "@mountaya/studio-react",
    parts: [],
    props: [],
    propsTypeName: "",
    summary:
      "ZoneSubHeader is the band under the zone panel's header that answers \"what does the public see of this zone?\" and offers the one act that changes that answer. It is the zone's twin of {@link ItinerarySubHeader }, and reads exactly as it does: one row, the state on the left (a coloured mark, the state's word, the dimmed age of what is live, the sentence on hover) and the act on the right, standing down with its reason rather than unmounting, so the form below it never moves.",
  },
  ZoomControl: {
    description:
      "MapLibre ships a NavigationControl that does this, and it is deliberately not used,\nhere or anywhere else in the package. Its markup, its focus ring and its a11y decisions\nare its own, and a map wearing both MapLibre's chrome and ours reads as two products\nbolted together.",

    importPath: "@mountaya/elements-react",
    name: "ZoomControl",
    package: "@mountaya/elements-react",
    parts: [],
    props: [
      { description: "", name: "className", required: false, seam: "style", type: "string" },
      {
        control: { kind: "boolean" },
        defaultValue: "false",
        description: "",
        name: "disabled",
        required: false,
        type: "boolean",
      },
      {
        description:
          "Overrides what a press does.\n\nLeft unset, the control drives the map directly, which is what a consumer wants. Set,\nthe control becomes presentational and the handler decides, which is how Mountaya's\nown studio routes every action through its tool bus while sharing this exact component.\n\nSupplying one also makes that half of the pair live with no map in context, because a\nhandler that suppresses the default has nothing left to wait for. The two halves are\ngated separately: a stack that overrides only zoom-in must not carry a live zoom-out\nwhose default it never wired.",
        name: "onZoomIn",
        required: false,
        seam: "handler",
        type: "() => void",
      },
      { description: "", name: "onZoomOut", required: false, seam: "handler", type: "() => void" },
      {
        description:
          "Overrides the words this element renders, for this element only.\n\nFolded onto whatever the provider resolved, so an override naming one key leaves the rest\nof the section alone. Scoped to the sections this element reads: a key it never renders is a\ntype error rather than a silent no-op.",
        name: "strings",
        required: false,
        type: "ZoomControlStrings",
      },
      { description: "", name: "style", required: false, seam: "style", type: "ElementStyle" },
    ],
    propsTypeName: "ZoomControlProps",
    summary: "ZoomControl is the +/- pair, as one flush chip.",
  },
};

export const ELEMENT_PAGES: ElementPages = {
  "sdks/typescript/editor": ["EditorMapView", "ViewerMapView"],
  "sdks/typescript/elevation-profile": ["ItineraryProfile"],
  "sdks/typescript/install": ["MountayaElementsProvider"],
  "sdks/typescript/map": [
    "MapView",
    "MapControls",
    "ZoomControl",
    "CompassControl",
    "CompassRose",
    "GeolocateControl",
    "PanelControl",
    "DirectionsControl",
    "DownloadControl",
    "SearchControl",
    "StreetViewControl",
    "LayersPanel",
    "PoiPanel",
    "PreferencesPanel",
    "ControlButton",
    "ControlGroup",
    "ControlTooltip",
    "Glyph",
    "OptionRow",
  ],
  "sdks/typescript/studio": [
    "MountayaStudio",
    "MountayaStudioViewer",
    "MountayaStudioHost",
    "StudioHostRegistryProvider",
    "ShareViewModal",
  ],
  "sdks/typescript/studio/chrome": [
    "StudioControls",
    "SegmentList",
    "ReadOnlySegmentList",
    "WaypointList",
    "EditorListSection",
    "SegmentSeam",
    "RadioOptionCard",
    "StudioToggleRow",
    "PrivacyControl",
    "LanguageSelect",
    "AllowlistField",
    "OrganizationScopeOptionRow",
    "AgentThreadsProvider",
    "FullscreenModal",
    "FullscreenHeader",
    "PublicationNotice",
    "UnpublishedPanel",
    "AppModal",
    "ModalFooter",
    "ConfirmModal",
    "StudioHeader",
    "SidenavContentHeader",
    "SidenavContentBody",
    "SidenavFormActions",
    "ErrorAlert",
    "ListSkeleton",
    "EmptyState",
    "WarningAlert",
    "FormErrorAlert",
    "ActivityIcon",
    "DialogSubject",
    "ItineraryActionsMenu",
    "ItineraryForm",
    "ItinerarySubHeader",
    "ItinerarySegmentList",
    "ItineraryWaypointList",
    "ItineraryProfilePanel",
    "PersistedSegmentRow",
    "DraftSegmentRow",
    "ItineraryHeaderTitle",
    "ItineraryHeaderIdentity",
    "ItineraryHeaderMeta",
    "EditorSaveFooter",
    "StagingCreateFooter",
    "WaypointIconCircle",
    "SegmentGroupModal",
    "DraftProvenanceBanner",
    "SuggestForm",
    "ZoneForm",
    "ZoneActionsMenu",
    "ZoneSubHeader",
    "StudioSectionBoundary",
    "StudioPortalScope",
    "CopilotNoAccessCard",
    "CopilotPanel",
    "EmbedWizard",
    "EmbedFieldControl",
    "ImportItineraryContent",
    "ImportPreviewLegend",
    "ImportRoutingGuideProvider",
    "ImportRoutingGuideLink",
  ],
  "sdks/typescript/studio/collaboration": [
    "PresenceRoster",
    "PeerLockTooltip",
    "LockedActionsButton",
  ],
  "sdks/typescript/studio/headless": [
    "StudioProvider",
    "StudioCopilotProvider",
    "StudioNavigationBlockerProvider",
    "StudioNotifyProvider",
    "StudioOrganizationsProvider",
    "StudioBillingGateProvider",
    "StudioPluginsProvider",
  ],
  "sdks/typescript/studio/video": ["VideoExportWizard"],
  "sdks/typescript/tables": ["ItinerarySegmentTable", "ItineraryWaypointTable", "WaypointCard"],
};
